/**
 * Executable API contract for `Backend/accounts-backend` — all 46 routes of the
 * prod HTTP API v2 `jz7twvotyf` (`prod-accounts-backend-Dashboard-Api`), plus the
 * shared `DualAuth` authorizer 13 other repos depend on.
 *
 * Base source: `Backend/accounts-backend` at **`origin/main`** =
 * `e6034a8842e9db3ebf8bb4123dd3e338ed41bf10`, a historical production observation.
 * Scoped dispatch-audit overlay: `origin/dev` **`ed03a5bde85d38cbe96937549dd3fe55f9a67a53`
 * → `ae15bca2d9601f6cf06284034444a65ade56f7e9`** (six commits / sixteen paths). Only the
 * named account-session, account-status, management-session, permissions, requirement-utils,
 * and Stripe-webhook behavior from that overlay is represented here. This is not a full
 * `origin/dev@ae15bca` contract.
 *
 * Buckets, all four present in this one repo:
 *   A  42 dashboard-authenticated routes behind the shared `DualAuth` authorizer
 *   B   1 public donor-facing route (`GET /public/privacy-policy/{slug}`, auth NONE)
 *   C   3 inbound webhooks (Stripe x2, Clerk), auth NONE, signature-verified in-handler
 *   D     effect variants for the webhooks' table writes and outbound calls
 *
 * What this file is: the runtime oracle the parity recorder diffs a candidate against.
 * It pins the LEGACY wire shape bug-for-bug — 25 distinct CORS header maps with
 * `content-type` / `Content-Type` case drift, five outcomes that share one byte, a
 * docstring that contradicts its own return, `str(e)` provider strings on the wire.
 * Nothing is normalized; the fixes we would make after cutover are in `openapi.yaml`
 * under `x-deviations`, applied to nothing.
 *
 * Typecheck: pnpm exec tsc --noEmit --strict --target es2022 --module esnext \
 *              --moduleResolution bundler --skipLibCheck <this file>
 * Test:      pnpm --filter @charitystack/org-engagement-contracts test
 */
import { z } from 'zod';
import {
  ANY_HEADERS,
  GATEWAY_ALL,
  LambdaProxyResponse,
  variant,
} from './legacy-gateway.js';

export { LambdaProxyResponse };

/**
 * API Gateway's own responses. Reduced from GATEWAY_ALL to what THIS api can emit, and
 * nothing is dropped: all five are reachable. 401 comes from the gateway when the
 * `$request.header.Authorization` identity source is missing on one of the 42 authorized
 * routes (PROBED LIVE 2026-08-30: `GET /merchant` with no header -> 401
 * {message:'Unauthorized'}); 403 when DualAuth returns `{isAuthorized:false}`; 500 when
 * the authorizer throws JWKSFetchError or a handler raises; 503 on integration timeout or
 * Lambda throttle; 404 when the route table matches nothing — there is NO $default route
 * on this api (`aws apigatewayv2 get-routes --api-id jz7twvotyf`, 46 routes, none
 * `$default`).
 */
export const GATEWAY_VARIANTS = {
  GATEWAY_UNAUTHORIZED: GATEWAY_ALL.GATEWAY_UNAUTHORIZED,
  GATEWAY_FORBIDDEN: GATEWAY_ALL.GATEWAY_FORBIDDEN,
  GATEWAY_NOT_FOUND: GATEWAY_ALL.GATEWAY_NOT_FOUND,
  GATEWAY_INTERNAL: GATEWAY_ALL.GATEWAY_INTERNAL,
  GATEWAY_UNAVAILABLE: GATEWAY_ALL.GATEWAY_UNAVAILABLE,
} as const;

/**
 * The 25 DISTINCT header maps this repo emits. That there are 25 of them across 26
 * handlers is itself the finding: `content-type` vs `Content-Type` case drift,
 * `Access-Control-Allow-Methods` values that do not match the route they are served on
 * (PATCH /nonprofit-settings' 200 advertises `POST`), and `Content-Type: application/json`
 * present on some helpers and absent from others. Each is pinned exactly where the handler
 * sets it; nothing is unified.
 */
export const H_POST_OPTIONS_UC_IDEM = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization,Idempotency-Key"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_POST_OPTIONS_UC_JSON_IDEM = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization,Idempotency-Key"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Content-Type': z.literal("application/json"),
  })
  .strict();
export const H_POST_OPTIONS_UC_JSON_RETRY_IDEM = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization,Idempotency-Key"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Content-Type': z.literal("application/json"),
    'Retry-After': z.literal("3"),
  })
  .strict();
export const H_OPTIONS_PATCH_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("OPTIONS,PATCH"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_OPTIONS_PATCH_UC_JSON = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("OPTIONS,PATCH"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Content-Type': z.literal("application/json"),
  })
  .strict();
export const H_POST_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_POST_OPTIONS_UC_JSON = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Content-Type': z.literal("application/json"),
  })
  .strict();
export const H_GET_POST_PATCH_DELETE_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("GET,POST,PATCH,DELETE,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_GET_PUT_DELETE_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("GET,PUT,DELETE,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("GET,PUT,POST,PATCH,DELETE,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_POST_LC_NOSTORE = z
  .object({
    'Access-Control-Allow-Headers': z.literal("content-type"),
    'Access-Control-Allow-Methods': z.literal("POST"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Cache-Control': z.literal("no-store"),
  })
  .strict();
export const H_GET_LC_NOSTORE = z
  .object({
    'Access-Control-Allow-Headers': z.literal("content-type"),
    'Access-Control-Allow-Methods': z.literal("GET"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Cache-Control': z.literal("no-store"),
  })
  .strict();
export const H_GET_POST_LC_NOSTORE = z
  .object({
    'Access-Control-Allow-Headers': z.literal("content-type"),
    'Access-Control-Allow-Methods': z.literal("GET,POST"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Cache-Control': z.literal("no-store"),
  })
  .strict();
export const H_OPTIONS_GET_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type"),
    'Access-Control-Allow-Methods': z.literal("OPTIONS,GET"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_PATCH_LC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("content-type"),
    'Access-Control-Allow-Methods': z.literal("PATCH"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_POST_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type"),
    'Access-Control-Allow-Methods': z.literal("POST"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_OPTIONS_POST_GET_LC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("content-type"),
    'Access-Control-Allow-Methods': z.literal("OPTIONS,POST,GET"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_PATCH_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type"),
    'Access-Control-Allow-Methods': z.literal("PATCH"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_PUT_LC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("content-type"),
    'Access-Control-Allow-Methods': z.literal("PUT"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_GET_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("GET,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_GET_OPTIONS_UC_JSON = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("GET,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Content-Type': z.literal("application/json"),
  })
  .strict();
export const H_PATCH_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("PATCH,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_POST_OPTIONS_UC_2 = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();
export const H_POST_OPTIONS_UC_JSON_2 = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type"),
    'Access-Control-Allow-Methods': z.literal("POST,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
    'Content-Type': z.literal("application/json"),
  })
  .strict();
export const H_GET_POST_PATCH_OPTIONS_UC = z
  .object({
    'Access-Control-Allow-Headers': z.literal("Content-Type,Authorization"),
    'Access-Control-Allow-Methods': z.literal("GET,POST,PATCH,OPTIONS"),
    'Access-Control-Allow-Origin': z.literal("*"),
  })
  .strict();

/**
 * A 204/preflight whose `body` is the empty string. That is NOT JSON, so it cannot go
 * through the normal decode-then-match path; the parser special-cases it and matches
 * against `body: z.literal('')`.
 */
export const emptyBodyVariant = <S extends number, H extends z.ZodTypeAny>(
  statusCode: S,
  headers: H,
) => z.object({ statusCode: z.literal(statusCode), headers, body: z.literal('') }).strict();

// --------------------------------------------------------------------------
// Closed enums — each closed by reading the SOURCE branch structure at the pinned
// SHA, not by re-listing. The grep or branch read that closes it is in the doc.
// --------------------------------------------------------------------------

/**
 * Emit as z.enum([...]). The fresh-create path is narrower than the replay path.
 * _activation_path_from_has_website (create_org.py:357-362) is a single ternary on
 * bool(has_website) — exactly 2 outcomes, no else gap. It sets
 * v2_record.onboardingDetails.activationPath at :694, which is what :1183 reads back and
 * :1203 returns, so the legacy 'A'/'B'/'manual' values are unreachable on
 * CREATE_ORG_SUCCESS_200.
 */
export const ACTIVATION_PATH_CREATE = [
  "hasWebsite",
  "doesNotHaveWebsite",
] as const;

/**
 * Emit as z.enum([...]). The reachable output set of _normalize_activation_path
 * (create_org.py:365-384) — THREE values, not five. Branch read: :373 maps
 * 'hasWebsite'|'A' -> 'hasWebsite'; :375 maps 'doesNotHaveWebsite'|'B' ->
 * 'doesNotHaveWebsite'; :380 `if value in VALID_ACTIVATION_PATH_VALUES: return value`
 * where that set is the 5-member {hasWebsite, doesNotHaveWebsite, A, B, manual}
 * (constants.py:124-130) — but the first two branches have already consumed 4 of the 5, so
 * this branch can ONLY ever return 'manual'; :382 and :384 both return one of the first
 * two. No missing else. Used only by CREATE_ORG_IDEMPOTENT_REPLAY_200.
 */
export const ACTIVATION_PATH_NORMALIZED = [
  "hasWebsite",
  "doesNotHaveWebsite",
  "manual",
] as const;

/**
 * org_custom_fields.py:153 `ALLOWED_TYPES = ('string',)`. Closed by reading both
 * consumers: normalize_definitions :499 COERCES anything else to DEFAULT_TYPE, and
 * create_definition :724 REJECTS anything else with a 400 — so no other value can be
 * served. This is what lets CustomFieldDefinition.type be z.literal('string') rather than
 * z.string().
 */
export const ALLOWED_TYPES = [
  "string",
] as const;

/**
 * functions/permissions.py:8-75, read in SOURCE ORDER. Closed by exec-ing the module and
 * taking len() = 36 (not by re-listing by eye — an earlier pass of this work miscounted it
 * as 34). Order is load-bearing: GET /org-role-permissions serves this list verbatim,
 * hence the ALL_PERMISSIONS_TUPLE pin above.
 */
export const ALL_PERMISSIONS = [
  "org:payments:manage_payments",
  "org:payments:process_payments",
  "org:payments:view_payments",
  "org:elements:manage_elements",
  "org:elements:process_elements",
  "org:elements:view_elements",
  "org:forms:manage_forms",
  "org:forms:process_forms",
  "org:forms:view_forms",
  "org:payouts:manage_payouts",
  "org:payouts:view_payouts",
  "org:billing:manage_billing",
  "org:settings:manage_checkout_settings",
  "org:settings:manage_email_settings",
  "org:settings:manage_integration_settings",
  "org:settings:manage_organization_settings",
  "org:settings:manage_tags",
  "org:settings:manage_portal_settings",
  "org:settings:manage_processor_settings",
  "org:contacts:manage_contacts",
  "org:contacts:process_contacts",
  "org:contacts:view_contacts",
  "org:contacts:import_contacts",
  "org:contacts:reverse_imports",
  "org:subscriptions:manage_subscriptions",
  "org:subscriptions:view_subscriptions",
  "org:marketing:manage_marketing",
  "org:broadcasts:create_broadcasts",
  "org:broadcasts:view_broadcasts",
  "org:broadcasts:edit_broadcasts",
  "org:segments:create_segments",
  "org:segments:view_segments",
  "org:segments:edit_segments",
  "org:email_templates:create_email_templates",
  "org:email_templates:view_email_templates",
  "org:email_templates:edit_email_templates",
] as const;

/**
 * REQUEST-side enum (not a response body) — included because it closes
 * BILLING_PLAN_INVALID_PLAN_400's trigger. PLAN_CONFIG at billing_plan.py:61-78 is a dict
 * literal with exactly one top-level key, 'STARTER', read end to end. 'CUSTOM' is
 * explicitly rejected earlier at :388 with its own message and is NOT a config key. Any
 * other value after `.strip().upper()` — including '' from a missing key — yields 'plan
 * must be STARTER'.
 */
export const BILLING_PLAN_ACCEPTED_PLANS = [
  "STARTER",
] as const;

/**
 * CLOSED SET. `grep -n "'error'"` on functions/billing_plan/billing_plan.py at the pinned
 * SHA returns exactly 8 hits — :239, :243, :246, :385, :389, :391, :395, :413 — and all
 * eight are hardcoded literals with no interpolation. No `str(e)` reaches any response
 * body in this file (the sole broad handler, :232, logs only `type(exc).__name__`), which
 * is what makes the set closable. This is the ONLY handler in the group with a closed
 * error set; the other five all leak an unvalidated provider string. Usable as `z.object({
 * error: z.enum(BILLING_PLAN_ERROR_MESSAGES) }).strict()` for a union over all non-200
 * billing-plan responses, though each variant above pins its own single literal for
 * precise status/body pairing.
 */
export const BILLING_PLAN_ERROR_MESSAGES = [
  "Billing permissions are temporarily unavailable",
  "Clerk authentication required",
  "Manage billing permission required",
  "Invalid JSON body",
  "CUSTOM is display-only and cannot be saved here",
  "plan must be STARTER",
  "Organization record not found",
  "Method not allowed",
] as const;

/**
 * VALID_BUILTIN_ROLES = list(BUILTIN_ROLE_TO_CLERK.keys()) at clerk_users.py:61, derived
 * from the dict literal at :54-59. `grep -n 'BUILTIN_ROLE_TO_CLERK'` -> 5 hits (:54
 * definition, :60 inversion, :61 keys, :313 lookup, :405 lookup); no mutation anywhere, so
 * the set is closed. Insertion order is load-bearing because the list is repr'd into
 * POST_CLERK_USERS_INVALID_ROLE's message.
 */
export const CHARITYSTACK_BUILTIN_ROLES = [
  "ADMIN",
  "FINANCE",
  "EDITOR",
  "MARKETING",
] as const;

/**
 * The 4 values of BUILTIN_ROLE_TO_CLERK (clerk_users.py:55-58) plus the literal
 * 'org:member' assigned on the CUSTOM: path at :311 and :403. `grep -n \"'org:\"` -> 8
 * hits (:55,:56,:57,:58,:73,:122,:311,:403); :73 and :122 are READ-side comparisons, not
 * writes. These are the only role strings this Lambda can send to Clerk, which is what
 * closes RoleNotAvailableBody.role.
 */
export const CLERK_NATIVE_ROLES_WRITTEN = [
  "org:admin",
  "org:finance",
  "org:editor",
  "org:marketing",
  "org:member",
] as const;

/**
 * Closed by BRANCH STRUCTURE, not by the docstring. `grep -n 'event_type =='` -> exactly 6
 * hits (clerk_webhooks.py:169,171,173,175,177,179), plus one tuple test `elif event_type
 * in ('organization.created', 'organization.updated')` at :181 contributing 2 more = 8,
 * plus a terminal `else:` at :183 so no state is unnamed. The module docstring at :7-13
 * lists only SIX and OMITS the organization.* pair (added later under PE-2048, see :48) —
 * trusting it would miss the entire ORGANIZATIONS_TABLE write path. NOTE: this enum does
 * NOT appear in any response body; it is the dispatch key that selects which body variant
 * is produced, and it also interpolates into CLERK_WEBHOOK_UNHANDLED_EVENT_TYPE /
 * CLERK_WEBHOOK_DISPATCH_ERROR_500 where the value is unvalidated caller data rather than
 * one of these 8.
 */
export const CLERK_WEBHOOK_EVENT_TYPES = [
  "user.created",
  "user.updated",
  "user.deleted",
  "organizationMembership.created",
  "organizationMembership.updated",
  "organizationMembership.deleted",
  "organization.created",
  "organization.updated",
] as const;

/**
 * org_role_permissions.py:69. `git grep -n CUSTOMIZABLE_ROLES <SHA> --
 * functions/org_role_permissions/` = 5 hits (:69 def, :163 GET loop, :183 GET defaults,
 * :213 PUT valid set, :285 reset body). Fixes the closed 3-key `defaults` object in
 * RP_GET_OK_200 and the closed `rolePermissions` in RP_RESET_OK_200. No else-gap:
 * org:admin is excluded by design (docstring :33) and org:member has no entry in
 * ROLE_PERMISSIONS either.
 */
export const CUSTOMIZABLE_ROLES = [
  "org:finance",
  "org:editor",
  "org:marketing",
] as const;

/**
 * `git grep -n "'code':" <SHA> -- functions/org_custom_fields/org_custom_fields.py` = 4
 * hits: :625 (WRITE_CONFLICT, in _conflict_response, shared by all three mutating verbs),
 * :815 (LABEL_CONFLICT, POST), :833 (QUOTA_EXCEEDED, POST), :956 (LABEL_CONFLICT, PATCH).
 * Every other error body in the file has NO `code` key at all — that ABSENCE is
 * load-bearing and is why the schemas above are .strict() rather than carrying an optional
 * code.
 */
export const CUSTOM_FIELD_ERROR_CODES = [
  "CUSTOM_FIELD_LABEL_CONFLICT",
  "CUSTOM_FIELD_QUOTA_EXCEEDED",
  "CUSTOM_FIELD_WRITE_CONFLICT",
] as const;

/**
 * The determinate prefixes in NS_WRITE_FAILED_500's regex. Closed by the three
 * `future_to_label[...] = f'...'` assignments at nonprofit_settings_dashboard.py:758,
 * :763, :770 — no other future is submitted to the executor.
 */
export const NS_WRITE_FAILED_LABEL_PREFIXES = [
  "merchant:",
  "v2_brand:",
  "v2_registration:",
] as const;

/**
 * EMPTY BY VERIFICATION, not by omission: `git grep -n "'code':" <SHA> --
 * functions/org_contact_tags/` returns 0 hits. Every error body in that handler is exactly
 * {"error": string}, which is what makes .strict() safe on all of them.
 */
export const ORG_CONTACT_TAGS_ERROR_CODES = [
] as const;

/**
 * The only `code` key across all six routes is clerk_role_errors.py:88, reachable solely
 * via CR_DELETE_ROLE_NOT_AVAILABLE_409. Every other error body on this handler is exactly
 * {"error": string}.
 */
export const ORG_ROLE_PERMISSIONS_ERROR_CODES = [
  "role_not_available",
] as const;

/**
 * Backs PolicyMergeField. Closed by the literal five-key `fields` dict in
 * policy_template.build_policy_draft and the matching five-key _PLACEHOLDERS dict;
 * missingFields is a list comprehension over exactly those keys.
 */
export const POLICY_MERGE_FIELDS = [
  "organizationName",
  "address",
  "email",
  "phone",
  "websiteUrl",
] as const;

/**
 * NOT a body enum — the complete input-validation predicate for the repo's only
 * unauthenticated route, recorded here because it determines which of the two
 * byte-identical 404s a donor receives. Closed by reading the single guard at
 * privacy_policy.py:612. It does NOT call org_slug_utils.validate_slug, so MIN_SLUG_LENGTH
 * (3) and the 51-entry RESERVED_SLUGS frozenset are NOT enforced on the read path — the
 * read and write validators are genuinely different predicates.
 */
export const PP_PUBLIC_GET_SLUG_PREDICATE = [
  "slug is non-empty",
  "len(slug) <= 63",
  "slug matches /^[a-z0-9]+(?:-[a-z0-9]+)*$/",
] as const;

/**
 * Emit as z.enum([...]). Closes validate_slug (org_slug_utils.py:56-71): the function has
 * exactly 5 non-None return statements (:62, :64, :66, :68, :70) plus the final `return
 * None` at :71, so the set is complete by branch read. Two members are f-strings whose
 * interpolands are module constants — MIN_SLUG_LENGTH = 3 and MAX_SLUG_LENGTH = 63
 * (org_slug_utils.py:33-34) — so they render as fixed literals, not patterns. The last
 * member contains an EM DASH (U+2014), not a hyphen-minus.
 */
export const SLUG_VALIDATION_ERRORS = [
  "Organization Slug is required",
  "Organization Slug must be at least 3 characters",
  "Organization Slug must be at most 63 characters",
  "Organization Slug may only contain lowercase letters, numbers, and single hyphens",
  "This slug is reserved — choose a different one",
] as const;

/**
 * The FIXED-literal error bodies on POST /webhooks/stripe-subscription. Closed by `grep -n
 * '_response(' ` = 8 call sites (:760, :766, :769, :775, :777, :782, :788, :790); of
 * these, :782 and :790 are the two 200s and :788 is the ONLY f-string error, leaving
 * exactly these 5 literals. Note four of the five are 400s — this route has four distinct
 * 400 bodies.
 */
export const SSW_ERROR_MESSAGES_LITERAL = [
  "Webhook secret not configured",
  "Invalid signature",
  "Invalid payload",
  "Invalid JSON body",
  "Event body is not a JSON object",
] as const;

/**
 * The `status` key of the two 200 bodies. Closed by `grep -n '_response(200' ` = exactly 2
 * sites: :782 {'status': 'ignored', ...} and :790 {'status': 'ok', ...}. No other 200
 * producer exists in the file.
 */
export const SSW_RESPONSE_STATUS_FIELD = [
  "ignored",
  "ok",
] as const;

/**
 * REQUEST-side enum — closes SUBSCRIPTION_CANCEL_NOT_ADMIN_403's trigger.
 * subscription_cancel.py:92 `if _get_clerk_role(event) not in ('org:admin', 'admin')` is a
 * literal 2-tuple. The role is read at :70 as `lam.get('role') or lam.get('orgRole') or
 * ''`, so an absent claim ('') is rejected. Contrast billing_plan.py:187, which reads ONLY
 * 'orgRole' and gates on the permission 'org:billing:manage_billing' instead — the two
 * billing routes use incompatible authorization models.
 */
export const SUBSCRIPTION_CANCEL_ADMIN_ROLE_VALUES = [
  "org:admin",
  "admin",
] as const;

/**
 * subscription_cancel.py:121 `'status': 'scheduled'` is a single hardcoded literal and
 * there is no other 200 return in the file, so the key is a constant. Pinned inline as
 * z.literal('scheduled').
 */
export const SUBSCRIPTION_CANCEL_STATUS_VALUES = [
  "scheduled",
] as const;

/**
 * The only three `return None, '<msg>'` sites in verify_svix_signature
 * (clerk_webhooks.py:107, :117, :131). Exactly one contains the substring 'not configured'
 * that :154 tests, so exactly one maps to 503 and the other two to 400. NONE of these
 * strings ever reaches the client — the body is always the literal 'Webhook verification
 * failed'. Listed so the generator understands why two variants share one body.
 */
export const SVIX_VERIFY_ERROR_STRINGS = [
  "Signing secret not configured in SSM",
  "Missing Svix headers",
  "Invalid signature: {str(e)}",
] as const;

/**
 * Every string ever passed as _error_response(status, message) on POST /webhooks/stripe.
 * Closed by `grep -n 'return _error_response' ` = exactly 5 call sites (:844, :853, :856,
 * :891, :817). All 5 are literals; none is an f-string, so this route NEVER leaks
 * exception text into a body — unlike its subscription sibling.
 */
export const SW_ERROR_MESSAGES = [
  "Webhook not configured",
  "Invalid signature",
  "Verification failed",
  "Account fetch failed",
  "Processing failed",
] as const;

/**
 * Closed by reading the dispatch branch structure of stripe_webhooks.handler(), not by
 * re-listing. `git show <SHA>:functions/stripe_webhooks/stripe_webhooks.py | grep -nE
 * 'event_type ==|event_type in'` returns exactly 2 dispatch lines: :872 `if event_type in
 * _V2_ACCOUNT_EVENT_TYPES` and :898 `if event_type == "account.updated"`.
 * _V2_ACCOUNT_EVENT_TYPES is a module-level frozen set literal at :670-674 with exactly 3
 * members. 2 dispatch sites + a 3-member set = 4 handled types. THE MISSING `else` IS
 * LOAD-BEARING: no else follows :898, so control falls through to the bare `return
 * _ok_response()` at :908 and the ACCEPTED type set is unbounded. This enum therefore
 * appears in NO response body — it is never echoed. It constrains only which inbound
 * events cause effects.
 */
export const SW_HANDLED_STRIPE_EVENT_TYPES = [
  "v2.core.account.updated",
  "v2.core.account[requirements].updated",
  "v2.core.account[future_requirements].updated",
  "account.updated",
] as const;

/**
 * Emit as z.union([z.literal('url is required'), z.literal('URL must start with http:// or
 * https://'), z.literal('Invalid URL format'), z.literal('URL resolves to a private or
 * reserved address'), z.literal('Could not resolve hostname'), z.string().regex(/^Invalid
 * URL: /)]) — a union, NOT z.enum, because the sixth member is the f-string f"Invalid URL:
 * {e}" at url_validation.py:78 interpolating a raw ValueError from ipaddress.ip_address().
 * Closed by `git grep -n 'return None, {' functions/url_validation.py` -> exactly 6 error
 * returns (:43, :49, :61, :71, :76, :78) plus one success return at :80. Shared verbatim
 * by WEBSITE_ENRICH_URL_VALIDATION_400 and VERIFY_SCRIPT_URL_VALIDATION_400. Note both
 * callers DROP the `detail` key that two of these returns carry (:71 'ssrf_blocked', :76
 * 'dns_error').
 */
export const URL_VALIDATION_ERROR = [
  "url is required",
  "URL must start with http:// or https://",
  "Invalid URL format",
  "URL resolves to a private or reserved address",
  "Could not resolve hostname",
  "Invalid URL: <str(ValueError)>",
] as const;

/**
 * Reference enum for the 502 family — each variant above pins its own member as a
 * z.literal, so this is documentation of the closed set rather than an identifier the
 * bodies reference. Closed by `git grep -n '_error_response(502' verify_script.py` ->
 * exactly 6 call sites (:271, :274, :277, :280, :283, :292), each with a distinct
 * third-arg literal. All six share the same `error` literal 'Could not reach website' and
 * are separable ONLY by `detail`. The 400/403/405 variants pass no detail, so the key is
 * absent there — _error_response omits it on falsiness (verify_script.py:158).
 */
export const VERIFY_SCRIPT_ERROR_DETAILS = [
  "http_error",
  "timeout",
  "ssl_error",
  "connection_error",
  "unknown_error",
  "waf_blocked",
] as const;

// --------------------------------------------------------------------------
// Shared wire shapes
// --------------------------------------------------------------------------

/**
 * Every stored attribute of `prodOrganizationsTable`, from the APPROVED projection census
 * (approvals.prodOrganizationsTable, granted 2026-08-29). Key names, counts and DynamoDB
 * types only — no value was retained. Full-table read of all 198 rows on 2026-08-30: 61
 * top-level attributes, 211 paths to depth 4. A key present on all 198 rows is required;
 * anything else is `.optional()`. This is what closes GET /merchant's key set — the
 * HANDLER closes nothing, because it returns `dict(v2_record)` verbatim.
 */
export const V2OrganizationRow = z
  .object({
    activationComplete: z.boolean().optional(), // 175/198
    address: z.record(z.unknown()), // 198/198
    applePayStatus: z.record(z.unknown()).optional(), // 16/198
    billingDetails: z.record(z.unknown()), // 198/198
    brandAssets: z.record(z.unknown()).optional(), // 38/198
    causeCategory: z.string().optional(), // 23/198
    checklistMinimized: z.boolean().optional(), // 7/198
    clerkOrganizationId: z.string(), // 198/198
    contactPortalElementID: z.string().optional(), // 3/198
    contact_tags: z.array(z.unknown()).optional(), // 1/198
    createdAt: z.string(), // 198/198
    dateNextPayoutUnixHour: z.union([z.number(), z.string()]).optional(), // 137/198
    defaultPaymentProcessor: z.string(), // 198/198
    ein: z.string().optional(), // 195/198
    entityType: z.string(), // 198/198
    feeAmounts: z.record(z.unknown()), // 198/198
    finixDetails: z.record(z.unknown()), // 198/198
    finixOldMerchantId: z.string().optional(), // 104/198
    globalCustomInputs: z.array(z.unknown()).optional(), // 36/198
    globalDefaultCoverFees: z.boolean().optional(), // 5/198
    globalFAQs: z.array(z.unknown()).optional(), // 39/198
    hasLock: z.boolean().optional(), // 10/198
    hasWebsite: z.boolean().optional(), // 23/198
    integrationsDetails: z.record(z.unknown()), // 198/198
    integrationsEnabled: z.boolean().optional(), // 18/198
    isTaxExempt: z.boolean().optional(), // 23/198
    isTest: z.boolean().optional(), // 36/198
    lastPublishedFormId: z.string().optional(), // 15/198
    legalName: z.string().optional(), // 23/198
    marketingPhone: z.string().optional(), // 4/198
    marketing_tier_status: z.string().optional(), // 6/198
    newVirtuousSegmentsByRequest: z.record(z.unknown()).optional(), // 1/198
    onboardingDetails: z.record(z.unknown()), // 198/198
    onboardingStatus: z.string().optional(), // 175/198
    organizationBaseURLs: z.array(z.unknown()).optional(), // 196/198
    organizationId: z.string(), // 198/198
    organizationName: z.string(), // 198/198
    organizationSlug: z.string(), // 198/198
    payoutDetails: z.record(z.unknown()), // 198/198
    payoutStatus: z.string().optional(), // 137/198
    pocketflow_business_deleted_at: z.string().optional(), // 1/198
    pocketflow_business_id: z.string().optional(), // 6/198
    pocketflow_failed_contacts_count: z.union([z.number(), z.string()]).optional(), // 6/198
    pocketflow_failed_contacts_counts: z.record(z.unknown()).optional(), // 6/198
    pocketflow_last_synced_at: z.string().optional(), // 6/198
    portalPrimaryColor: z.string().optional(), // 5/198
    pricing_provisioned_at: z.string().optional(), // 8/198
    privacyOverlayEnabled: z.boolean().optional(), // 3/198
    privacyPolicyDocument: z.record(z.unknown()).optional(), // 4/198
    privacyPolicyLink: z.string().optional(), // 11/198
    registrationContact: z.record(z.unknown()).optional(), // 5/198
    scriptInstalled: z.boolean().optional(), // 2/198
    sms_number_requested_at: z.string().optional(), // 3/198
    sms_number_requests: z.array(z.unknown()).optional(), // 3/198
    sms_pending_request_id: z.string().optional(), // 1/198
    statementDescriptor: z.string().optional(), // 195/198
    stripeConnectComplete: z.boolean().optional(), // 16/198
    stripeDetails: z.record(z.unknown()), // 198/198
    timezone: z.string().optional(), // 46/198
    updatedAt: z.string(), // 198/198
    websiteUrl: z.string().optional(), // 38/198
  })
  .strict();

/**
 * The flat V1 alias keys `org_utils.flatten_v2_to_v1_compat` ADDS on top of the V2 row
 * (`org_utils.py:958-985`). Derived mechanically from `V1_TO_V2_FIELD_MAP` at the pinned
 * SHA by reversing it (first V1 key wins per V2 path), dropping `id` (explicitly skipped
 * at :963-964), and adding the one hard-coded alias `merchantFAQs` (:972-974): 108 keys.
 * Each is added ONLY when the underlying V2 path resolves non-None AND the key is not
 * already present, so every one is `.optional()`.
 */
export const V1CompatAliases = z
  .object({
    EIN: z.unknown().optional(),
    achMaxFee: z.unknown().optional(),
    activationPath: z.unknown().optional(),
    address1: z.unknown().optional(),
    address2: z.unknown().optional(),
    applePayStatus: z.unknown().optional(),
    approvedForPaypalPaymentProcessing: z.unknown().optional(),
    backupApprovedForPaypalPaymentProcessing: z.unknown().optional(),
    backupPaypalMerchantEmail: z.unknown().optional(),
    backupPaypalMerchantID: z.unknown().optional(),
    backupPaypalTrackingID: z.unknown().optional(),
    billingPlan: z.unknown().optional(),
    billingPlanConfiguredAt: z.unknown().optional(),
    billingPlanConfiguredBy: z.unknown().optional(),
    bloomerangAppeals: z.unknown().optional(),
    bloomerangCampaigns: z.unknown().optional(),
    bloomerangConnectionEnabled: z.unknown().optional(),
    bloomerangFunds: z.unknown().optional(),
    bloomerangPayoutAppName: z.unknown().optional(),
    bloomerangProcessingFeeId: z.unknown().optional(),
    bloomerangTransactionAppName: z.unknown().optional(),
    bodyHTML: z.unknown().optional(),
    brandColor: z.unknown().optional(),
    cardMin: z.unknown().optional(),
    checkoutBumpCSSharePercent: z.unknown().optional(),
    city: z.unknown().optional(),
    clerkOrganizationID: z.unknown().optional(),
    contactPortalElementID: z.unknown().optional(),
    countryCode: z.unknown().optional(),
    dateNextPayoutUnixHour: z.unknown().optional(),
    defaultCoverFees: z.unknown().optional(),
    defaultPaymentProcessor: z.unknown().optional(),
    donorPerfectConnectionEnabled: z.unknown().optional(),
    donorPerfectPayoutAppName: z.unknown().optional(),
    donorPerfectTransactionAppName: z.unknown().optional(),
    driverFlowsCompleted: z.unknown().optional(),
    fakeMerchant: z.unknown().optional(),
    faqs: z.unknown().optional(),
    fixedFee: z.unknown().optional(),
    globalCustomInputs: z.unknown().optional(),
    hasFinixPayoutLock: z.unknown().optional(),
    hasLock: z.unknown().optional(),
    headHTML: z.unknown().optional(),
    integrationsEnabled: z.unknown().optional(),
    isTaxExempt: z.unknown().optional(),
    locationId: z.unknown().optional(),
    logoUrl: z.unknown().optional(),
    merchantFAQs: z.unknown().optional(),
    merchantID: z.unknown().optional(),
    merchantIdentity: z.unknown().optional(),
    merchantName: z.unknown().optional(),
    neoncrmConnectionEnabled: z.unknown().optional(),
    neoncrmPayoutAppName: z.unknown().optional(),
    neoncrmTransactionAppName: z.unknown().optional(),
    netSuiteConnectionEnabled: z.unknown().optional(),
    netSuitePayoutAppName: z.unknown().optional(),
    netSuiteTransactionAppName: z.unknown().optional(),
    oldMerchantIdentity: z.unknown().optional(),
    onboardingCompletedSteps: z.unknown().optional(),
    onboardingCurrentStep: z.unknown().optional(),
    orgDescription: z.unknown().optional(),
    organizationBaseURLs: z.unknown().optional(),
    payoutArrivalDay: z.unknown().optional(),
    payoutCounter: z.unknown().optional(),
    payoutCreatedDateQueryRef: z.unknown().optional(),
    payoutCreationRetryAttempts: z.unknown().optional(),
    payoutFrequency: z.unknown().optional(),
    payoutStatus: z.unknown().optional(),
    paypalMerchantEmail: z.unknown().optional(),
    paypalMerchantID: z.unknown().optional(),
    paypalOauthValid: z.unknown().optional(),
    paypalPaymentProcessingFixedFee: z.unknown().optional(),
    paypalPaymentProcessingPercentFee: z.unknown().optional(),
    paypalPaymentsReceivable: z.unknown().optional(),
    paypalPlatformPercentFee: z.unknown().optional(),
    paypalPrimaryEmailConfirmed: z.unknown().optional(),
    paypalThirdPartyProcessorFee: z.unknown().optional(),
    paypalTrackingID: z.unknown().optional(),
    percentFee: z.unknown().optional(),
    platformFixedFee: z.unknown().optional(),
    platformPercentFee: z.unknown().optional(),
    portalPrimaryColor: z.unknown().optional(),
    processingFixedFee: z.unknown().optional(),
    processingPercentFee: z.unknown().optional(),
    salesforceConnectionEnabled: z.unknown().optional(),
    salesforcePayoutAppName: z.unknown().optional(),
    salesforceTransactionAppName: z.unknown().optional(),
    showCheckoutBump: z.unknown().optional(),
    showMonthlyBump: z.unknown().optional(),
    state: z.unknown().optional(),
    statementDescriptor: z.unknown().optional(),
    stripeAccountID: z.unknown().optional(),
    stripeActivatedAt: z.unknown().optional(),
    stripeChargesEnabled: z.unknown().optional(),
    timezone: z.unknown().optional(),
    updated_at: z.unknown().optional(),
    virtuousConnectionEnabled: z.unknown().optional(),
    virtuousProcessingFeeId: z.unknown().optional(),
    virtuousProjects: z.unknown().optional(),
    virtuousSecretKey: z.unknown().optional(),
    virtuousSegmentId: z.unknown().optional(),
    virtuousSegments: z.unknown().optional(),
    virtuousTransactionAppName: z.unknown().optional(),
    websiteUrl: z.unknown().optional(),
    zapierConnectionEnabled: z.unknown().optional(),
    zapierPayoutWebhookURL: z.unknown().optional(),
    zapierWebhookURL: z.unknown().optional(),
    zip: z.unknown().optional(),
  })
  .strict();

/**
 * REMOVED — there is deliberately NO merged `MerchantRecord` schema for GET /merchant.
 *
 * A `V2OrganizationRow.merge(V1CompatAliases)` symbol used to live here, documented as
 * emitting "exactly the union of the two schemas above". That claim was FALSE, and the
 * way it was false is the reason the symbol is gone rather than repaired:
 * `flatten_v2_to_v1_compat` does NOT stop at the row plus the aliases. It also adds keys
 * that exist in NEITHER half, so the union UNDER-states the emitted key set. Verified at
 * the pinned SHA:
 *   - `billingPlanSource` — `org_utils.py:601`, the top of `_add_billing_fee_compat_fields`
 *     BEFORE any branch, with a second unconditional backstop at
 *     `get_merchant.py:221-222` immediately before `return _response(200, result)`.
 *     Present on EVERY 200.
 *   - `dashboardPercentFee` / `dashboardFixedFee` — `org_utils.py:657-658` (negotiated),
 *     `:831-832` (STARTER) and `:834-835` (non-STARTER). All three sit inside
 *     `if use_split_fee_defaults:` (`:783`) and the source's own comment at `:836-840`
 *     records that there is NO else branch (175/176 prod records reach it), so these are
 *     conditional, not universal.
 *   - `onboardingSelectedPlan` — `org_utils.py:1004`, guarded by `if 'selectedPlan' in od`.
 *
 * Re-adding the four keys was considered and rejected. A merged schema here would be a
 * SECOND, hand-maintained model of a response whose key set this contract deliberately
 * refuses to close in code: the live pin is `GET_MERCHANT_OK_200 = z.record(z.unknown())`
 * (see its docblock), because the row is an OPEN PASSTHROUGH closed by an approved writer
 * census, not by the handler. A fixed merged schema therefore cannot be wired up at
 * cutover without contradicting that decision — and while unwired it drifts silently, as
 * it already did. The two halves stay exported and are each pinned by count in the suite
 * (`V2OrganizationRow` = 61, `V1CompatAliases` = 108), which is where drift is actually
 * caught; the merge added no coverage on top of that.
 *
 * SECURITY (unchanged, and still asserted in the suite against `V1CompatAliases`): the
 * response admits `virtuousSecretKey` (3/198 orgs populated), `zapierWebhookURL` (9/198),
 * `paypalMerchantEmail` and `paypalTrackingID` (33/198 each) and `stripeAccountID`
 * (148/198) — every one of which `org_utils._SENSITIVE_FIELDS` (`org_utils.py:1031`)
 * names as sensitive. That redaction applies ONLY to shadow-diff logs, never to this
 * response. Pinned bug-for-bug; see openapi.yaml x-deviations.
 */

/**
 * ORDER-PRESERVING pin of the 36-element ALL_PERMISSIONS list. GET /org-role-permissions
 * returns the Python list object VERBATIM as `allPermissions`
 * (org_role_permissions.py:184) — it is never sorted, filtered, or set-ified on that path
 * — so the response array is byte-for-byte the source order of
 * functions/permissions.py:8-75. A z.array(z.enum(...)) would NOT catch a reordering of
 * that catalog, which is exactly the drift this pin exists to detect. Index 11 is the
 * MANAGE_BILLING_PERMISSION constant ('org:billing:manage_billing', permissions.py:6).
 * Count verified by exec-ing the module: 36.
 */
export const ALL_PERMISSIONS_TUPLE = z.tuple([z.literal('org:payments:manage_payments'), z.literal('org:payments:process_payments'), z.literal('org:payments:view_payments'), z.literal('org:elements:manage_elements'), z.literal('org:elements:process_elements'), z.literal('org:elements:view_elements'), z.literal('org:forms:manage_forms'), z.literal('org:forms:process_forms'), z.literal('org:forms:view_forms'), z.literal('org:payouts:manage_payouts'), z.literal('org:payouts:view_payouts'), z.literal('org:billing:manage_billing'), z.literal('org:settings:manage_checkout_settings'), z.literal('org:settings:manage_email_settings'), z.literal('org:settings:manage_integration_settings'), z.literal('org:settings:manage_organization_settings'), z.literal('org:settings:manage_tags'), z.literal('org:settings:manage_portal_settings'), z.literal('org:settings:manage_processor_settings'), z.literal('org:contacts:manage_contacts'), z.literal('org:contacts:process_contacts'), z.literal('org:contacts:view_contacts'), z.literal('org:contacts:import_contacts'), z.literal('org:contacts:reverse_imports'), z.literal('org:subscriptions:manage_subscriptions'), z.literal('org:subscriptions:view_subscriptions'), z.literal('org:marketing:manage_marketing'), z.literal('org:broadcasts:create_broadcasts'), z.literal('org:broadcasts:view_broadcasts'), z.literal('org:broadcasts:edit_broadcasts'), z.literal('org:segments:create_segments'), z.literal('org:segments:view_segments'), z.literal('org:segments:edit_segments'), z.literal('org:email_templates:create_email_templates'), z.literal('org:email_templates:view_email_templates'), z.literal('org:email_templates:edit_email_templates')]);

/**
 * derive_cs_role (clerk_users.py:68-75) has exactly two returns: f'CUSTOM:{custom_role}'
 * at :74 and BUILTIN_CLERK_TO_ROLE.get(clerk_role, 'EDITOR') at :75. The .get default
 * means an UNRECOGNISED Clerk role silently renders as 'EDITOR'. The CUSTOM: suffix is
 * unbounded — it is Clerk membership metadata this Lambda writes from a caller-supplied
 * slug validated only against the org's customRoles map, and it can be empty (a bare
 * 'CUSTOM:' splits to '' at :302/:394).
 */
export const CharityStackRole = z.union([z.enum(['ADMIN', 'FINANCE', 'EDITOR', 'MARKETING']), z.string().regex(/^CUSTOM:[\s\S]*$/)]);

/**
 * Closed by the dict literal at clerk_users.py:164-173 — exactly 8 keys. `status` is
 * `inv.status or 'pending'`, so it is a Clerk-supplied string with a 'pending' fallback;
 * the list call filters status='pending' (:151) but the value is echoed from the SDK
 * rather than pinned, so it is not narrowed to a literal. createdAt/expiresAt carry the
 * same number-or-empty-string degradation as ClerkMemberRow.
 */
export const ClerkInvitationRow = z.object({ invitationId: z.string(), email: z.string(), clerkRole: z.string(), charitystackRole: CharityStackRole, customRole: z.string().nullable(), status: z.string(), createdAt: z.union([z.number(), z.literal('')]), expiresAt: z.union([z.number(), z.literal('')]) }).strict();

/**
 * Closed by the dict literal at clerk_users.py:232-243 — exactly 10 keys there, plus
 * `phone` added at :200.
 *
 * GUARD AUDIT (the string fields are NOT uniformly guarded — do not assume they are).
 * Checked field by field at the pinned SHA:
 *   - GUARDED, cannot be null: `firstName` / `lastName` / `email` / `imageUrl` (:234-237)
 *     are `user_data.X or '' if user_data else ''` — Python binds the conditional last,
 *     so BOTH the missing-object and the falsy-value cases yield ''. `phone` (:200) is
 *     `primary.phone_number if primary else ''`, and `_enrich_users_with_phones` writes
 *     `phone_by_user.get(entry['userId'], '')`. `charitystackRole` comes from
 *     derive_cs_role, whose fallback arm is `BUILTIN_CLERK_TO_ROLE.get(clerk_role,
 *     'EDITOR')` (:75) — always a string.
 *   - NOT guarded: `clerkRole` is BARE `m.role` (:238). `userId` is
 *     `user_data.user_id if user_data else ''` (:233), which guards the OBJECT, not the
 *     VALUE — a present `user_data` with a null `user_id` passes through as null.
 * The pins below stay `z.string()` for both anyway. `m.role` is the Clerk membership
 * role that derive_cs_role is keyed on one line earlier, and `user_data.user_id` is the
 * Clerk user primary key; neither is nullable in any observed Clerk response, and all
 * 198-org fixture traffic shows them populated. This is therefore a pin that is
 * deliberately NARROWER than the code strictly proves: if Clerk ever returned null the
 * schema would reject a real response. That is the intended failure direction — it
 * surfaces loudly rather than silently admitting a null into a role/identity field —
 * but it is a CHOICE, not a guarantee the source provides. Do not widen it on the
 * strength of the old "all `x or ''` guarded" claim, which was false.
 *
 * `customRole` is pub_meta.get('custom_role', None), so string-or-null.
 * `permissionOverrides` is pub_meta.get('permission_overrides', {}) — arbitrary Clerk
 * metadata this Lambda itself writes UNVALIDATED from the caller's body (:335, :424,
 * :440), so z.unknown(), not z.record(...). `createdAt` is `m.created_at if m.created_at
 * else ''` — a Clerk epoch-millis NUMBER that degrades to the empty STRING when falsy,
 * hence the union; it is NOT an ISO string. `phone` is .optional() because
 * _enrich_users_with_phones only runs on ?withPhones=true (:251) and is swallowed on
 * failure (:254) — but note it is added to EVERY entry when it runs (:199-200), so in
 * practice the key is present on all rows or none, never mixed.
 */
export const ClerkMemberRow = z.object({ userId: z.string(), firstName: z.string(), lastName: z.string(), email: z.string(), imageUrl: z.string(), clerkRole: z.string(), charitystackRole: CharityStackRole, customRole: z.string().nullable(), permissionOverrides: z.unknown(), createdAt: z.union([z.number(), z.literal('')]), phone: z.string().optional() }).strict();

/**
 * Built by normalize_dictionary (org_contact_tags.py:313-338), which emits exactly
 * {'value','usage_count'} — closing the key set. value is non-empty (blanks dropped at
 * :335-336). usage_count: DynamoDB returns it as a Decimal, and :333-334 coerces
 * Decimal->int BEFORE serialization, so it is a real JSON NUMBER, not a string — checked
 * because this handler also uses a bare json.dumps (grep for DecimalEncoder/cls=/default=
 * across all three handlers returns 0 hits, so an uncoerced Decimal would have RAISED
 * TypeError rather than stringifying). Negative or non-int counts are floored to 0 at
 * :335-336.
 *
 * BUG-FOR-BUG — usage_count can be a JSON BOOLEAN. The floor guard is
 * `if not isinstance(count, int) or count < 0: count = 0`, and in Python
 * `isinstance(True, int)` is True (bool subclasses int) while `True < 0` is False, so a
 * stored DynamoDB BOOL passes BOTH arms untouched and json.dumps emits
 * `"usage_count": true` / `false`. Executed against the pinned guard: True -> true,
 * False -> false, Decimal('3') -> 3, -2 -> 0, 'x' -> 0, None -> 0. This is reachable
 * because contact_tags is written by an OUT-OF-REPO writer (contacts-db-backend
 * contact_tagger), so the stored type is not controlled here — which is exactly why the
 * defensive normalizer exists. It carries into TAGS_PUT_OK_200 too: the PUT rebuilds the
 * dictionary from `counts_by_lower` (:464-467), which copies the bool through verbatim.
 * Hence the union below. The rest of the field stays constrained — a string or null
 * still fails.
 */
export const ContactTagEntry = z.object({ value: z.string().min(1), usage_count: z.union([z.number().int().nonnegative(), z.boolean()]) }).strict();

/**
 * Every definition in every org_custom_fields response body comes from
 * normalize_definitions (org_custom_fields.py:472-503), which REBUILDS each entry as
 * exactly {'key','label','type'} — that construction closes the key set, so .strict() is
 * exact rather than assumed. key/label are non-empty (blank keys are skipped at :489; a
 * blank label falls back to the key at :494). type is coerced to DEFAULT_TYPE whenever it
 * is not in ALLOWED_TYPES (:499), so the value space of a SERVED type is exactly
 * {'string'}. Because every field is rebuilt as a str, no DynamoDB Decimal can reach
 * json.dumps — relevant because this file uses a BARE json.dumps with no
 * cls=DecimalEncoder.
 */
export const CustomFieldDefinition = z.object({ key: z.string().min(1), label: z.string().min(1), type: z.literal('string') }).strict();

/**
 * account_session.py:140 and stripe_management_session.py:118 — both return status 200
 * with an empty client_secret when the org has no Stripe account id. Bug-for-bug: an error
 * condition shaped as success.
 */
export const EmptyClientSecretBody = z.object({ client_secret: z.literal('') }).strict();

/**
 * The single return dict literal of _map_nonprofit_to_our_shape (ein_lookup.py:71-82) — 10
 * keys, ONE construction site, so the key set is closed. Every 200 hit variant SPREADS
 * this at the top level, so these are also top-level response keys. Typing per rule 4:
 * legalName and ein are guaranteed strings (`or ""` fallback plus .strip() at :73), but
 * the other eight are `np.get(...) or ""` / `loc.get(...)` over an ARBITRARY Every.org
 * JSON object — a non-string upstream value (number, list, nested object) passes straight
 * through untouched, and city/state yield Python None -> JSON null when the loc dict lacks
 * the key (ein_lookup.py:79-80). Do not narrow them. `address` is whatever object
 * Every.org returned, or {} when it was a string (ein_lookup.py:81).
 */
export const EveryOrgMappedOrg = z.object({ legalName: z.string(), ein: z.string(), nteeCode: z.unknown(), causeCategory: z.unknown(), logoUrl: z.unknown(), websiteUrl: z.unknown(), description: z.unknown(), city: z.unknown(), state: z.unknown(), address: z.unknown() }).strict();

/**
 * P3 multi-currency (CON-328, SPEC §2.5, decisions Q-1): the curated non-USD display codes an org
 * may offer. NOT a legacy shape: accounts-backend ignores `currencySettings` (D-R1); the TS save
 * writes it. Kept equal to `OFFERED_CURRENCIES` minus USD in packages/shared/src/currency/
 * currencies.js by a text-reading parity test, because backends cannot import packages.
 */
export const CURRENCY_SETTINGS_OFFERED_CODES = ['GBP', 'EUR', 'CAD', 'AUD', 'NZD', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'INR', 'PKR', 'BDT'] as const;

/**
 * P3 (CON-328, SPEC §2.5): the org's display-currency setting. Exactly four keys. `offered: null`
 * means the full curated list; a list holds 1-15 distinct curated codes (never USD). The whole
 * object is never `null`. The standard PATCH /nonprofit-settings member accepts it as an optional
 * key and the save echoes it; the LOGO_VERSION member does not accept it.
 */
export const currencySettingsSchema = z.object({ enabled: z.boolean(), detectByLocation: z.boolean(), donorCanChoose: z.boolean(), offered: z.array(z.enum(CURRENCY_SETTINGS_OFFERED_CODES)).min(1).max(15).refine((codes) => new Set(codes).size === codes.length, { message: 'offered must not repeat a currency code' }).nullable() }).strict();
export type CurrencySettings = z.infer<typeof currencySettingsSchema>;

/**
 * The `Attributes` value in NS_OK_200. Key set closed by the literal v2_fields dict in
 * update_merchant_table (nonprofit_settings_dashboard.py:105-115) plus the conditional
 * organizationBaseURLs at :114-115. EVERY key is .optional() because `written = {k: v for
 * k, v in v2_fields.items() if v is not None}` filters out None values.
 * address1/address2/city/state/zip/EIN are z.unknown() because they come from
 * body['addressLine1'] etc. with NO type validation whatsoever (rule 4) — a caller can
 * send a number or an object and it is written and echoed. timezone IS validated (ZoneInfo
 * round-trip via validate_timezone) so z.string() is safe. organizationBaseURLs is
 * validated to a list of http(s) strings <=200 chars, max 50 entries.
 * currencySettings is the P3 addition (CON-328), not legacy: the TS save echoes it only when
 * the request carried it.
 */
export const NonprofitSettingsWrittenAttributes = z.object({ address1: z.unknown().optional(), address2: z.unknown().optional(), city: z.unknown().optional(), state: z.unknown().optional(), zip: z.unknown().optional(), EIN: z.unknown().optional(), timezone: z.string().optional(), organizationBaseURLs: z.array(z.string()).optional(), currencySettings: currencySettingsSchema.optional() }).strict();

/**
 * The IDENTICAL literal appears at stripe_account_status.py:142, account_session.py:127,
 * stripe_management_session.py:110, stripe_account_email.py:79, subscription_cancel.py:97
 * — five handlers, one string, verified by reading each site. Deliberately EXCLUDES
 * billing_plan.py:395, which says 'Organization record not found' (different string,
 * different schema). Safe to share only because all five are hardcoded literals with no
 * interpolation. The variant entries above are written out in full and do not reference
 * this name — it is offered as an optional dedupe, not a dependency.
 */
export const OrgNotFoundBody = z.object({ error: z.literal('Organization not found') }).strict();

/**
 * A single permission string. Used for the ARRAY VALUES inside rolePermissions/defaults in
 * org_role_permissions responses. Closed because every such array is filtered against
 * ALL_PERMISSIONS before serialization: GET at :166 and :174, PUT rejects any non-member
 * at :229-235, and the reset body is built from the ROLE_PERMISSIONS constant. Depends on
 * the ALL_PERMISSIONS enum below.
 */
export const PermissionKey = z.enum(ALL_PERMISSIONS);

/**
 * The element type of PP_GENERATE_OK_200.missingFields. Closed by reading
 * policy_template.build_policy_draft: `missing = [name for name, value in fields.items()
 * if not value]` over a literal five-key `fields` dict, whose keys are exactly the keys of
 * the literal _PLACEHOLDERS dict. Array order follows Python dict insertion order
 * (organizationName, address, email, phone, websiteUrl); the array may be empty or any
 * subset.
 */
export const PolicyMergeField = z.enum(['organizationName', 'address', 'email', 'phone', 'websiteUrl']);

/**
 * The `role` echoed in the CR_DELETE_ROLE_NOT_AVAILABLE_409 body. Closed by the local
 * valid_reassign list literal at org_role_permissions.py:476 and the membership test at
 * :477 — a value outside it 400s before reaching the Clerk call that can raise this 409.
 * 'org:member' is deliberately absent: the reassignment floor is org:editor, which is also
 * the default when the query param is omitted (:466).
 */
export const ReassignRole = z.enum(['org:admin', 'org:finance', 'org:editor', 'org:marketing']);

/**
 * Built by clerk_role_errors.py:82-91; the error text is the module constant
 * ROLE_NOT_AVAILABLE_MESSAGE at :12-15, quoted verbatim including the apostrophe in
 * "organization's" (escaped for the single-quoted Zod literal). Shared by
 * POST_CLERK_USERS_ROLE_NOT_AVAILABLE and PATCH_CLERK_USERS_ROLE_NOT_AVAILABLE. `role` is
 * closed to the 5 Clerk native roles this Lambda can pass to Clerk (see
 * CLERK_NATIVE_ROLES_WRITTEN). SEPARATE-CONCERN WARNING for the generator:
 * role_not_available_response returns NO 'headers' key — both call sites inject
 * CORS_HEADERS afterwards (clerk_users.py:357, :454). That affects the header schema, not
 * this body schema.
 */
export const RoleNotAvailableBody = z.object({ error: z.literal('The selected role is not available for this organization. Add it to the organization\'s Clerk Role Set, then try again.'), code: z.literal('role_not_available'), role: z.enum(['org:admin', 'org:finance', 'org:editor', 'org:marketing', 'org:member']) }).strict();

/**
 * The complete set of OS_INVALID_SLUG_400 bodies. Closed by reading
 * org_slug_utils.validate_slug (functions/org_slug_utils.py:56-71) end to end: five
 * `return '<msg>'` statements plus a final `return None`; the function has no other exit.
 * The two length strings are f-strings over MIN_SLUG_LENGTH = 3 and MAX_SLUG_LENGTH = 63
 * (module constants), so they are fully determined. The last entry contains U+2014 EM
 * DASH, verbatim.
 */
export const SlugValidationError = z.enum(['Organization Slug is required', 'Organization Slug must be at least 3 characters', 'Organization Slug must be at most 63 characters', 'Organization Slug may only contain lowercase letters, numbers, and single hyphens', 'This slug is reserved — choose a different one']);

/**
 * Same literal at account_session.py:148 (status 503), stripe_management_session.py:121
 * (status 500), subscription_cancel.py:105 (status 500). The BODY is shared; the STATUS is
 * not — do not let a shared body schema imply a shared status. stripe_account_email has no
 * such body: it folds that condition into a 200 with an empty email.
 */
export const StripeNotConfiguredBody = z.object({ error: z.literal('Stripe not configured') }).strict();

/**
 * The 5 dispatched Stripe event types for POST /webhooks/stripe-subscription. Closed by
 * the EVENT_HANDLERS dict literal at stripe_subscription_webhook.py:734-740 (exactly 5
 * keys) and the single dispatch site `fn = EVENT_HANDLERS.get(event_type)` at :780. Valid
 * ONLY in the SSW_OK_200 body, where reaching the line proved membership. NOT valid for
 * SSW_EVENT_TYPE_IGNORED_200, whose whole precondition is non-membership.
 */
export const SubscriptionEventType = z.enum(['checkout.session.completed', 'invoice.payment_succeeded', 'invoice.payment_failed', 'customer.subscription.updated', 'customer.subscription.deleted']);

/**
 * The single wire shape emitted by FIVE semantically distinct outcomes of POST
 * /webhooks/stripe: SW_ORG_NOT_FOUND_200 (:740), SW_V2_THIN_EVENT_MISSING_ACCOUNT_ID_200
 * (:882), SW_CLASSIC_MISSING_DATA_OBJECT_200 (:903), SW_UNKNOWN_EVENT_TYPE_IGNORED_200
 * (:908), and SW_PROCESSED_NO_CHANGES_200 (:819). All five call _ok_response() (:677) with
 * changes defaulting to False, so all five serialize to the same bytes. THEY ARE NOT
 * SEPARABLE ON THE WIRE and this contract deliberately does NOT invent a discriminator to
 * tell them apart — only the CloudWatch print line differs. Any consumer needing to
 * distinguish them must read logs, not the response. Emitted as one shared schema
 * referenced by all five variants precisely so the collapse is visible in the contract
 * rather than hidden behind five identical copies.
 */
export const SwOkNoChanges = z.object({ received: z.literal(true), changes: z.literal(false) }).strict();

/**
 * The bare `{'error': str(e)}` shape at account_session.py:216,
 * stripe_management_session.py:154, stripe_account_email.py:98. Arbitrary unvalidated
 * provider/runtime text — Stripe messages routinely embed account and request identifiers.
 * Deliberately EXCLUDES subscription_cancel.py:118, which carries the literal prefix
 * 'Stripe error: ' and is pinned with a regex instead.
 */
export const UnpinnedProviderErrorBody = z.object({ error: z.string() }).strict();

// --------------------------------------------------------------------------
// Variants — one strict decoded-envelope schema per response the code can produce.
// A "decoded envelope" is the Lambda proxy response with `body` JSON-parsed.
// --------------------------------------------------------------------------

// ===== functions/billing_plan/billing_plan.py ============================
/**
 * Response helper: functions/billing_plan/billing_plan.py:96 _response(status_code, body)
 * -> {'statusCode': status_code, 'headers': CORS_HEADERS, 'body': json.dumps(body,
 * cls=DecimalEncoder)} — NOTE: NO Content-Type header is added (unlike every other handler
 * in this group), and DecimalEncoder (:89-93) renders Decimal as FLOAT
 */
/**
 * NOTES: FINDING (rule 4 — this is the biggest one in the group): the 200 body embeds the
 * ENTIRE flattened organization record under BOTH 'merchant' and 'organization'. Its key
 * set is unbounded and tracks whatever the DynamoDB item holds; org_utils' own
 * _SENSITIVE_FIELDS set (virtuousSecretKey, stripeAccountID, zapier*WebhookURL, paypal*
 * fields) is NOT filtered on this path — that redaction exists only for shadow-diff LOGS.
 * A contract must model this as an open passthrough record, never a closed schema. SECOND
 * FINDING: NO write failure has a handler response — a ConditionalCheckFailed or throttle
 * on either update_item becomes a bare gateway 500, and a failure at :372 or :100 (AFTER
 * both writes committed) also becomes a gateway 500 while the data HAS changed. THIRD:
 * _response here omits Content-Type entirely — the only handler in this group that does;
 * that is a genuine z.undefined()-vs-pinned-map distinction per brief rule 7. FOURTH
 * (contrast, worth stating positively): this is the ONLY handler in the group with a fully
 * CLOSED set of error strings and no provider-string leak.
 */

/**
 * site functions/billing_plan/billing_plan.py:410 — Trigger: method == 'OPTIONS' —
 * BUG-FOR-BUG: PINNED DIVERGENCE: preflight is 200 with "{}" here, whereas all four
 * Stripe handlers use 204 with "". Only `PATCH /billing-plan` is routed
 * (ApiStack.ts:1199). — billing_plan.py:410 `_response(200, {})` — status 200 with the
 * body string literally '{}'. This IS valid JSON (an empty object), NOT the empty-string
 * preflight the four Stripe handlers use, so it is pinned as a strict empty object
 * rather than __EMPTY_BODY__.
 */
export const BILLING_PLAN_OPTIONS_200_EMPTY_OBJECT = variant(200, H_PATCH_OPTIONS_UC, z.object({}).strict());

/**
 * site functions/billing_plan/billing_plan.py:413 — Trigger: method is neither 'OPTIONS'
 * nor 'PATCH' (including '' when requestContext.http.method is absent) —
 * billing_plan.py:413 — hardcoded literal.
 */
export const BILLING_PLAN_METHOD_NOT_ALLOWED_405 = variant(405, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:237-240 — Trigger: ANY exception out of
 * _resolve_effective_permissions(event) — bare `except Exception as exc` at :232.
 * Sources: PermissionResolutionError raised at :153/:157/:160/:169/:176/:201 (six
 * distinct Clerk-consistency checks), the SSM get_parameter for CLERK_SECRET_KEY_PATH at
 * :44, or any Clerk SDK/network error surviving clerk_api_call_with_retry (:104-116,
 * which retries ONLY on a rate-limit string match and only once). — BUG-FOR-BUG:
 * FAIL-CLOSED and DELIBERATE (503, not 500). Note the response body NEVER echoes the
 * underlying error — only the exception TYPE NAME is logged (:235), not the message.
 * This is the ONE handler in this group that does NOT leak provider text. The six
 * PermissionResolutionError messages ('Clerk membership response has invalid data',
 * 'Clerk membership response is inconsistent', 'Clerk membership response is ambiguous',
 * 'Clerk membership response does not match caller', 'Clerk membership response does not
 * match organization', 'Clerk membership response has no role') are LOG-ONLY and must
 * NOT appear in any response contract. — billing_plan.py:239 — hardcoded literal,
 * fail-closed 503. The underlying cause is NEVER echoed: only `type(exc).__name__` is
 * logged at :235. The six PermissionResolutionError messages are log-only and must not
 * appear in any response schema.
 */
export const BILLING_PLAN_PERMISSION_RESOLUTION_UNAVAILABLE_503 = variant(503, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Billing permissions are temporarily unavailable') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:243 — Trigger:
 * _resolve_effective_permissions returned an empty org_id — either
 * authorizer.lambda.provider != 'clerk' (:182-183) or provider=='clerk' with an empty
 * orgId (:186, :189-190). A Cognito caller lands here. — BUG-FOR-BUG: Handler-produced
 * 403. — billing_plan.py:243 — hardcoded literal. One of TWO distinct 403 bodies on this
 * route.
 */
export const BILLING_PLAN_NO_CLERK_AUTH_403 = variant(403, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:246 — Trigger: MANAGE_BILLING_PERMISSION
 * ('org:billing:manage_billing', functions/permissions.py:6) not in the resolved
 * permission list — BUG-FOR-BUG: Handler-produced 403, distinct string from the 'Clerk
 * authentication required' 403 — two different 403 bodies on one route. —
 * billing_plan.py:246 — hardcoded literal. Second of the two 403 bodies; a discriminator
 * must test the string, not the status.
 */
export const BILLING_PLAN_MISSING_PERMISSION_403 = variant(403, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Manage billing permission required') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:385 — Trigger:
 * json.loads(event.get('body') or '{}') raised — bare `except Exception` at :384. An
 * absent/empty body defaults to '{}' and does NOT hit this. — billing_plan.py:385 —
 * hardcoded literal. An absent/empty request body defaults to '{}' at :383 and does NOT
 * reach this.
 */
export const BILLING_PLAN_INVALID_JSON_400 = variant(400, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:389 — Trigger: str(body.get('plan') or
 * '').strip().upper() == 'CUSTOM' — case-insensitive and whitespace-tolerant, so '
 * custom ' also hits this — BUG-FOR-BUG: Checked BEFORE the PLAN_CONFIG membership test,
 * so CUSTOM gets its own distinct message rather than the generic one. —
 * billing_plan.py:389 — hardcoded literal. Tested BEFORE the PLAN_CONFIG membership
 * check, and the input is `.strip().upper()`, so ' custom ' also lands here rather than
 * on the generic message.
 */
export const BILLING_PLAN_CUSTOM_REJECTED_400 = variant(400, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('CUSTOM is display-only and cannot be saved here') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:391 — Trigger: requested_plan not in
 * PLAN_CONFIG. PLAN_CONFIG (:61-78) contains exactly ONE key: 'STARTER'. So ANY value
 * other than 'STARTER' (after strip+upper) — including a missing plan key, which
 * normalizes to '' — produces this. — billing_plan.py:391 — hardcoded literal, VERBATIM:
 * lowercase leading 'plan', no trailing period. Reached by any value other than
 * 'STARTER' after strip+upper, including a missing plan key (normalizes to '').
 */
export const BILLING_PLAN_INVALID_PLAN_400 = variant(400, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('plan must be STARTER') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:395 — Trigger:
 * _read_raw_org_by_clerk_org_id returned None (zero Items from the
 * clerkOrganizationId-index query) — BUG-FOR-BUG: No ORGANIZATIONS_TABLE guard in
 * _read_raw_org_by_clerk_org_id (:251-259) — an unset env raises out to a gateway 500,
 * not this 404. — billing_plan.py:395 — hardcoded literal. Note the word 'record': the
 * Stripe handlers say 'Organization not found'. Do not unify.
 */
export const BILLING_PLAN_ORG_RECORD_NOT_FOUND_404 = variant(404, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Organization record not found') }).strict());

/**
 * site functions/billing_plan/billing_plan.py:400 — Trigger: both update_item calls
 * succeeded — BUG-FOR-BUG: TWO pinned issues. (1) The response is serialized from the
 * LOCALLY MUTATED in-memory record, NOT re-read from DynamoDB — if the update partially
 * applied, the response still shows the intended state. (2) The body echoes the ENTIRE
 * org record, which includes fields org_utils itself marks sensitive (_SENSITIVE_FIELDS
 * at org_utils.py: virtuousSecretKey, stripeAccountID, zapierWebhookURL,
 * zapierPayoutWebhookURL, paypalTrackingID, backupPaypalTrackingID, paypalMerchantEmail,
 * backupPaypalMerchantEmail). A contract cannot enumerate this shape closed — model it
 * as a passthrough record. — billing_plan.py:400 `{'merchant': updated, 'organization':
 * updated}` — the OUTER key set is closed (exactly two keys, both bound to the SAME
 * object), so .strict() holds there. Each value is an UNBOUNDED PASSTHROUGH of the
 * entire flattened org record from org_utils.flatten_v2_to_v1_compat (org_utils.py:926):
 * the full V2 DynamoDB item PLUS every V1 flat alias from V2_TO_V1_FIELD_MAP, PLUS the
 * onboarding mirrors, PLUS merchantFAQs, PLUS _add_billing_fee_compat_fields output,
 * PLUS an explicit clerkOrganizationId. Its keys track whatever columns the stored item
 * happens to carry, so z.record(z.unknown()) is used rather than .passthrough().
 * Serialized with cls=DecimalEncoder, so Decimals are JSON FLOATS (not strings). Two
 * cautions for whoever wires the closed key set in: (1) the record is serialized from
 * the LOCALLY MUTATED in-memory dict, not re-read from DynamoDB; (2) it is NOT filtered
 * against org_utils._SENSITIVE_FIELDS — that redaction applies only to shadow-diff logs.
 */
export const BILLING_PLAN_OK_200 = variant(200, H_PATCH_OPTIONS_UC, z.object({ merchant: z.record(z.unknown()), organization: z.record(z.unknown()) }).strict());


// ===== functions/clerk_users/clerk_users.py ==============================
/**
 * Response helper: null — every response is an inline dict literal. Two shared builders
 * exist though: functions/clerk_users/clerk_users.py:107 require_admin(event) -> (org_id,
 * caller_sub, error_response) which returns the two 403 variants, and
 * functions/clerk_role_errors.py:82 role_not_available_response(role) -> {'statusCode':
 * 409, 'body': ...} WITHOUT headers (the caller injects CORS_HEADERS at :357 / :454).
 */
/**
 * NOTES: Contract-critical items: (1) role_not_available_response
 * (clerk_role_errors.py:82) builds a response with NO 'headers' key; both call sites
 * mutate it afterwards (:357, :454). Any new caller that forgets that line ships a
 * headerless 409 — the z.undefined()-vs-map distinction matters here. (2) The
 * overrides-only PATCH branch (:436) REPLACES public_metadata wholesale and silently drops
 * custom_role. (3) PATCH with neither field returns a 200 success having written nothing.
 * (4) POST's CUSTOM:-role validation calls Clerk OUTSIDE the try, so it 502s where PATCH's
 * identical call 500s. (5) handler() at :562 prints the unredacted event including
 * Authorization. (6) The 422-vs-500 split on POST is decided by `'already' in
 * str(e).lower()`. (7) Both DELETE paths are non-atomic multi-call sequences with no
 * compensation.
 */

/**
 * site functions/clerk_users/clerk_users.py:116-120 — Trigger: require_admin ->
 * get_org_id_and_role returns (None,None,None) because authorizer.lambda.provider !=
 * 'clerk' (:98), OR provider IS 'clerk' but authorizer.lambda.orgId is empty. BOTH cases
 * collapse to this same body — the message says 'authentication' but an authenticated
 * Clerk user with no active org gets it too. — BUG-FOR-BUG: Message is misleading for
 * the no-active-org case (a Clerk-authenticated user with no org sees 'Clerk
 * authentication required'). Pinned as-is. — clerk_users.py:119. TWO distinct causes
 * collapse to this one body: provider != 'clerk' (:98) AND provider == 'clerk' but orgId
 * empty (:115). The response cannot distinguish them.
 */
export const CLERK_USERS_NOT_CLERK_PROVIDER = variant(403, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:123-127 — Trigger: authorizer.lambda.orgRole
 * not in ('org:admin', 'admin') — clerk_users.py:126 — orgRole not in
 * ('org:admin','admin').
 */
export const CLERK_USERS_NOT_ADMIN = variant(403, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Admin role required') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:265-269 — Trigger: caller is org admin and
 * the memberships listing succeeded — BUG-FOR-BUG: createdAt/expiresAt are Clerk
 * epoch-millis integers, NOT ISO strings — and degrade to the empty string '' when
 * falsy, so the field is a number|string union. `phone` is CONDITIONALLY PRESENT (absent
 * unless ?withPhones=true and the bulk users.list succeeded), so the key itself is
 * optional, not merely nullable. — clerk_users.py:268. NOT DecimalEncoder — plain
 * json.dumps, values come from the Clerk SDK, not DynamoDB. Both element shapes are
 * closed dict literals (:232-243, :164-173) and are defined in sharedSchemas.
 */
export const GET_CLERK_USERS_OK = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ users: z.array(ClerkMemberRow), invitations: z.array(ClerkInvitationRow) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:273-277 — Trigger: Exception from
 * clerk.organization_memberships.list (the phone enrichment at :254 and the invitations
 * fetch at :262 are each individually swallowed and CANNOT reach this) — BUG-FOR-BUG:
 * Leaks raw Clerk SDK exception text. — f-string source: f'Failed to list users:
 * {str(e)}' (clerk_users.py:276). Only reachable from organization_memberships.list —
 * the phone enrichment (:254) and invitations fetch (:262) are each individually
 * swallowed.
 */
export const GET_CLERK_USERS_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to list users: [\s\S]*$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:344-351 — Trigger: admin, non-empty email,
 * role resolved, and clerk.organization_invitations.create succeeded — f-string source:
 * f'Invitation sent to {email}' (clerk_users.py:348). email is the caller-supplied
 * .strip()'d value — never format-validated anywhere, so the suffix is unbounded.
 */
export const POST_CLERK_USERS_CREATED = variant(201, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.string().regex(/^Invitation sent to .+$/), invitationId: z.string() }).strict());

/**
 * site functions/clerk_users/clerk_users.py:292-296 — Trigger:
 * body.get('email','').strip() is empty
 */
export const POST_CLERK_USERS_EMAIL_REQUIRED = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Email is required') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:306-310 — Trigger: charitystackRole starts
 * with 'CUSTOM:' and the slug after the first ':' is not a key of org
 * public_metadata.customRoles — BUG-FOR-BUG: Wording DIFFERS from the PATCH equivalent,
 * which says `Custom role "{slug}" not found.` (:401). Two different strings for the
 * same condition — both must be pinned separately. — f-string source: f'Custom role
 * "{custom_role_slug}" does not exist for this organization.' (clerk_users.py:309). Note
 * the embedded DOUBLE QUOTES and TRAILING PERIOD. The slug may be empty (a bare
 * 'CUSTOM:' splits to ''), hence .* not .+ . DIFFERENT wording from the PATCH twin — see
 * PATCH_CLERK_USERS_CUSTOM_ROLE_NOT_FOUND.
 */
export const POST_CLERK_USERS_CUSTOM_ROLE_NOT_FOUND = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Custom role ".*" does not exist for this organization\.$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:314-321 — Trigger: charitystackRole neither
 * starts with 'CUSTOM:' nor is in VALID_BUILTIN_ROLES — BUG-FOR-BUG: Python list repr
 * leaking into a client-facing message. The order of the list is insertion order of
 * BUILTIN_ROLE_TO_CLERK (:54-59) — ADMIN, FINANCE, EDITOR, MARKETING — and is
 * load-bearing for the literal. — f-string source: f'Invalid role: {charitystack_role}.
 * Must be one of {VALID_BUILTIN_ROLES} or CUSTOM:<slug>.' (clerk_users.py:319).
 * VALID_BUILTIN_ROLES is a PYTHON LIST interpolated via repr, so the rendered text
 * carries Python list syntax with SINGLE QUOTES verbatim. Its order is the insertion
 * order of BUILTIN_ROLE_TO_CLERK (:54-59) and is load-bearing for this literal.
 */
export const POST_CLERK_USERS_INVALID_ROLE = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Invalid role: [\s\S]*\. Must be one of \['ADMIN', 'FINANCE', 'EDITOR', 'MARKETING'\] or CUSTOM:<slug>\.$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:354-358 ->
 * functions/clerk_role_errors.py:82-91 — Trigger: an Exception from
 * organization_invitations.create for which is_role_not_available_error(e) is True:
 * exc.status_code in (400,404,409,422) AND a structured error whose code is in
 * {organization_role_not_found, organization_role_not_available,
 * organization_role_not_in_role_set}, OR code == 'resource_not_found' with a
 * long_message/longMessage/message that lowercases+strips to 'organization role not
 * found' or 'organization role not found.' — clerk_users.py:356 delegates to
 * clerk_role_errors.py:82-91. `role` is the CLERK NATIVE role ('org:member' etc.), not
 * the CharityStack display role.
 */
export const POST_CLERK_USERS_ROLE_NOT_AVAILABLE = variant(409, ANY_HEADERS, RoleNotAvailableBody);

/**
 * site functions/clerk_users/clerk_users.py:360-366 — Trigger: non-role-not-available
 * Exception whose str(e).lower() CONTAINS the substring 'already' (:361) — BUG-FOR-BUG:
 * Status is chosen by substring-matching the exception text for 'already'. Any unrelated
 * Clerk error containing the word 'already' (e.g. 'organization already at seat limit')
 * is reported as 422. Fully leaks the vendor error string. Pin as-is. —
 * clerk_users.py:365 emits the RAW str(e) with no prefix. The 422 branch is selected
 * purely by `'already' in error_msg.lower()` (:361), so the ONLY thing structurally
 * guaranteed about this body is that it contains 'already' case-insensitively.
 * Everything else is unbounded vendor text.
 */
export const POST_CLERK_USERS_ALREADY_EXISTS_422 = variant(422, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/[Aa][Ll][Rr][Ee][Aa][Dd][Yy]/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:362-366 — Trigger: same site as the 422;
 * status is 500 when 'already' is NOT in str(e).lower() — BUG-FOR-BUG: Inconsistent with
 * the other 500s here, which all prefix. Leaks raw vendor text. — clerk_users.py:365,
 * same site as the 422 — the RAW str(e) with NO 'Failed to ...' prefix, unlike every
 * other 500 in this handler. Status is 500 when 'already' is NOT in str(e).lower().
 * Cannot be narrowed further.
 */
export const POST_CLERK_USERS_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string() }).strict());

/**
 * site functions/clerk_users/clerk_users.py:444-448 — Trigger: admin + userId present,
 * and either (a) charitystackRole given and both Clerk calls succeeded, or (b)
 * permissionOverrides given and the metadata call succeeded, or (c) NEITHER given — in
 * which case NOTHING is written and this 200 is still returned. — BUG-FOR-BUG: SILENT
 * NO-OP SUCCESS: a PATCH body of {"userId":"user_fake"} with neither charitystackRole
 * nor permissionOverrides falls through both branches (:389 elif :434) and returns 200
 * 'User updated successfully' having made zero Clerk calls. The contract must pin this —
 * it is indistinguishable from a real update. — clerk_users.py:447. ALSO returned for
 * the SILENT NO-OP case: a body with neither charitystackRole nor permissionOverrides
 * falls through both branches (:389/:434) and returns this 200 having made zero Clerk
 * calls. The body cannot distinguish a real update from a no-op.
 */
export const PATCH_CLERK_USERS_OK = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('User updated successfully') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:379-383 — Trigger: body.get('userId') falsy
 * — clerk_users.py:382. Distinct literal from the DELETE twin ('userId query parameter
 * is required').
 */
export const PATCH_CLERK_USERS_USERID_REQUIRED = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('userId is required') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:398-402 — Trigger: charitystackRole starts
 * with 'CUSTOM:' and slug not in org customRoles — BUG-FOR-BUG: Different wording from
 * the POST equivalent (see POST_CLERK_USERS_CUSTOM_ROLE_NOT_FOUND). — f-string source:
 * f'Custom role "{custom_role_slug}" not found.' (clerk_users.py:401). SHORTER wording
 * than the POST twin at :309 for the identical condition — two strings, two variants.
 */
export const PATCH_CLERK_USERS_CUSTOM_ROLE_NOT_FOUND = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Custom role ".*" not found\.$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:407-411 — Trigger: charitystackRole
 * non-empty, not CUSTOM:-prefixed, and not in VALID_BUILTIN_ROLES — BUG-FOR-BUG:
 * Divergent from POST's richer message for the identical condition. — f-string source:
 * f'Invalid role: {new_charitystack_role}' (clerk_users.py:410). NO 'Must be one of ...'
 * suffix here, unlike the POST twin at :319.
 */
export const PATCH_CLERK_USERS_INVALID_ROLE = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Invalid role: [\s\S]*$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:451-455 ->
 * functions/clerk_role_errors.py:82-91 — Trigger: `new_clerk_role` is truthy (i.e. a
 * role branch actually ran — NOT the permissionOverrides-only branch) AND
 * is_role_not_available_error(e). If the exception is a role-not-found but
 * new_clerk_role is None (overrides-only path), this variant is SKIPPED and the generic
 * 500 fires instead. — clerk_users.py:453. Body identical to the POST twin. Only fires
 * when new_clerk_role is truthy (:451) — on the permissionOverrides-only path the same
 * exception falls through to the generic 500 instead.
 */
export const PATCH_CLERK_USERS_ROLE_NOT_AVAILABLE = variant(409, ANY_HEADERS, RoleNotAvailableBody);

/**
 * site functions/clerk_users/clerk_users.py:456-461 — Trigger: any other Exception
 * inside the try starting at :386 (includes the get_org_metadata Clerk call and both
 * membership update calls) — BUG-FOR-BUG: PARTIAL-WRITE HAZARD: the role update (:414)
 * and the metadata update (:426) are two separate Clerk calls in one try. If the second
 * fails, the native role is already changed but custom_role/permission_overrides are not
 * — the 500 does not roll back. — f-string source: f'Failed to update user: {str(e)}'
 * (clerk_users.py:460). Same prefix as users.py:178 in a different handler.
 */
export const PATCH_CLERK_USERS_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to update user: [\s\S]*$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:545-549 — Trigger: admin, userId param
 * present, userId != caller_sub, and BOTH organization_memberships.delete and
 * users.delete succeeded
 */
export const DELETE_CLERK_USERS_OK = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('User deleted successfully') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:518-522 — Trigger: no invitationId param AND
 * no userId param — clerk_users.py:521. Distinct from the PATCH literal 'userId is
 * required'.
 */
export const DELETE_CLERK_USERS_USERID_REQUIRED = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('userId query parameter is required') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:526-530 — Trigger: userId == caller_sub
 * (authorizer.lambda.sub)
 */
export const DELETE_CLERK_USERS_SELF_DELETE = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Cannot remove yourself from the organization') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:551-557 — Trigger: Exception from either
 * membership delete or account delete — BUG-FOR-BUG: PARTIAL-EFFECT HAZARD:
 * membership.delete runs first (:534) and users.delete second (:540). If users.delete
 * fails, the member has ALREADY been removed from the org but the caller sees a 500 —
 * retrying will then fail on the membership delete. — f-string source: f'Failed to
 * delete user: {str(e)}' (clerk_users.py:556).
 */
export const DELETE_CLERK_USERS_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to delete user: [\s\S]*$/) }).strict());

/**
 * site functions/clerk_users/clerk_users.py:488-492 — Trigger: invitationId query param
 * present (checked BEFORE userId at :512) and organization_invitations.revoke succeeded
 */
export const DELETE_CLERK_USERS_REVOKE_INVITATION_OK = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('Invitation revoked successfully') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:474-478 — Trigger: STRUCTURALLY UNREACHABLE
 * via delete_user: revoke_invitation is only called at :513 when
 * params.get('invitationId') is already truthy. Reachable only by calling
 * revoke_invitation directly (it is not wired to any route). — BUG-FOR-BUG: Dead branch.
 * Pin it as declared-but-unreachable rather than as a live variant. —
 * clerk_users.py:477. STRUCTURALLY UNREACHABLE: revoke_invitation is only called at :513
 * when invitationId is already truthy, and it is wired to no route of its own. Pin as
 * declared-but-dead.
 */
export const DELETE_CLERK_USERS_REVOKE_INVITATION_ID_REQUIRED = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('invitationId query parameter is required') }).strict());

/**
 * site functions/clerk_users/clerk_users.py:494-500 — Trigger: Exception from
 * organization_invitations.revoke — f-string source: f'Failed to revoke invitation:
 * {str(e)}' (clerk_users.py:499).
 */
export const DELETE_CLERK_USERS_REVOKE_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to revoke invitation: [\s\S]*$/) }).strict());


// ===== functions/clerk_webhooks/clerk_webhooks.py ========================
/**
 * Response helper: functions/clerk_webhooks/clerk_webhooks.py:769 _ok(message) ->
 * {'statusCode': 200, 'headers': CORS_HEADERS, 'body': json.dumps({'message': message})}.
 * Used by EVERY success/no-op path. The 400/503/500 paths build inline dicts.
 */
/**
 * NOTES: BUCKET C — the effect surface is the real contract here, and it is keyed on
 * (event type x data shape x existing-row state x signature outcome), not on the response
 * alone. Every single dispatched path returns HTTP 200 with a distinct message string; the
 * ONLY non-200 responses this endpoint can produce are the 400 (bad signature / missing
 * headers), the 503 (secret unreadable), and the 500 (unhandled exception during
 * dispatch). A contract that pins only status codes captures nothing — the message
 * literals and the write sites are the discriminators. Specific must-not-miss items: (a)
 * the organization.* → ORGANIZATIONS_TABLE update_item at :563 is UNCONDITIONAL and will
 * CREATE a phantom org row on a bad dynamodb_merchant_id; (b) _set_signup_complete
 * performs a Clerk read-modify-write of public_metadata that races the identical operation
 * in update_user_profile.py:106-120; (c) three of the four handlers in this group crash at
 * cold start if SSM is unreadable, but THIS one deliberately does not (lazy + try/except)
 * and returns 503 instead; (d) the signature except clause catches ONLY
 * WebhookVerificationError, so a malformed secret 502s; (e) the module docstring's event
 * list is stale and omits organization.*; (f) NO EMF metric emissions and no
 * put_metric_data anywhere in this file — every signal is a plain print(), so none of the
 * skip/no-op paths is alarmable today.
 */

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:156-160 (status chosen at :154) —
 * Trigger: verify_svix_signature returned a non-None error that does NOT contain the
 * substring 'not configured'. Two distinct causes collapse here: (a) 'Missing Svix
 * headers' — any of svix-id / svix-timestamp / svix-signature absent or empty
 * (:116-117); (b) 'Invalid signature: <svix detail>' — WebhookVerificationError from
 * wh.verify (:129-131), which covers a wrong signature, a stale svix-timestamp outside
 * the tolerance window, AND a malformed body. — clerk_webhooks.py:159. IDENTICAL body to
 * CLERK_WEBHOOK_SECRET_NOT_CONFIGURED_503 — one json.dumps site serves both. The 400/503
 * distinction is carried ENTIRELY by statusCode (chosen at :154 by substring-matching
 * the internal error string); the body carries no discriminator, deliberately, so
 * infrastructure detail is not leaked.
 */
export const CLERK_WEBHOOK_VERIFICATION_FAILED_400 = variant(400, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Webhook verification failed') }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:154-160 — Trigger:
 * get_signing_secret() returned None → verify_svix_signature returns (None, 'Signing
 * secret not configured in SSM') at :107, whose text contains 'not configured'.
 * get_signing_secret returns None only from its own except at :98-100 (SSM read failed /
 * param missing / IAM denied). Deliberately 503 so Clerk retries. — BUG-FOR-BUG: The
 * status is selected by SUBSTRING-MATCHING the internal error string (`'not configured'
 * in error`). Any future error message that happens to contain that phrase silently
 * becomes a 503 instead of a 400 — a stringly-typed control-flow branch. —
 * clerk_webhooks.py:159 — the SAME site and SAME body as
 * CLERK_WEBHOOK_VERIFICATION_FAILED_400. Emitted as a separate variant only because
 * statusCode differs (503 so Clerk retries). The zod is intentionally identical: the
 * distinction is honestly status-only.
 */
export const CLERK_WEBHOOK_SECRET_NOT_CONFIGURED_503 = variant(503, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Webhook verification failed') }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:183-190 — Trigger: signature valid and
 * event_type matches none of the 8 dispatched types — f-string source: f'Event type
 * {event_type} not handled' (clerk_webhooks.py:189) — built INLINE, not via _ok.
 * event_type is payload.get('type','') and is UNVALIDATED caller-controlled data from a
 * signed payload, so it may be empty (rendering 'Event type not handled' with two
 * spaces) or any JSON scalar.
 */
export const CLERK_WEBHOOK_UNHANDLED_EVENT_TYPE = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^Event type [\s\S]* not handled$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:191-197 — Trigger: any Exception
 * raised out of a handle_* function. Concretely reachable from: :419 (re-raise after
 * get_item failure in membership.created), :494 (re-raise after race-recovery update
 * failure), :635 (re-raise after get_item failure in membership.deleted), the unguarded
 * put/update in handle_membership_created (:426, :468), the unguarded org_table.query
 * and update_item in handle_organization_upsert (:552, :563), and the unguarded
 * update_item in handle_user_created's _update_profile_fields (:760). — BUG-FOR-BUG:
 * Leaks raw exception text (table names, boto error text) to an
 * UNAUTHENTICATED-at-the-gateway endpoint. Anyone holding the signing secret sees
 * infrastructure detail; more importantly Clerk logs it. A 500 makes Clerk RETRY, so
 * every one of these is a redelivery trigger. — f-string source: f'Failed to process
 * {event_type}: {str(e)}' (clerk_webhooks.py:196). BOTH halves are unbounded —
 * event_type is caller-supplied and str(e) is raw boto/vendor text. A 500 here makes
 * Clerk RETRY the delivery.
 */
export const CLERK_WEBHOOK_DISPATCH_ERROR_500 = variant(500, H_POST_OPTIONS_UC_2, z.object({ error: z.string().regex(/^Failed to process [\s\S]*: [\s\S]*$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:301 — Trigger: user.created with a
 * non-empty data.id — returned on BOTH the fresh-put path AND the
 * already-exists/update-profile path (there is no distinct message for the conflict
 * branch). — BUG-FOR-BUG: The conflict path (:291-296, which ran an update_item instead
 * of a put_item) returns the SAME 'User created: ...' body as a genuine create. The
 * response cannot distinguish create from update. — f-string source: f'User created:
 * {user_id}' (clerk_webhooks.py:301) via _ok. Returned on BOTH the fresh put_item path
 * AND the already-exists/update-profile conflict path — the body cannot distinguish
 * create from update.
 */
export const USER_CREATED_OK = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User created: .+$/) }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (5 code paths, one wire shape). Five branches
 * (user.created/updated/deleted, organizationMembership.created/deleted) each emit the
 * byte-identical 200 {"message": "Skipped: no user ID"} via _ok(). A recorder capturing
 * the wire CANNOT tell them apart, so modelling them as five variants makes every real
 * instance AMBIGUOUS. Collapsed to one; the five sites are named here. Collapsed sites:
 * functions/clerk_webhooks/clerk_webhooks.py:258 (data.get('id','') falsy);
 * functions/clerk_webhooks/clerk_webhooks.py:317 (data.get('id','') falsy);
 * functions/clerk_webhooks/clerk_webhooks.py:690 (data.get('id','') falsy);
 * functions/clerk_webhooks/clerk_webhooks.py:392 (data.public_user_data.user_id falsy);
 * functions/clerk_webhooks/clerk_webhooks.py:596 (public_user_data.user_id falsy)
 */
export const CLERK_WEBHOOK_SKIPPED_NO_USER_ID = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.literal('Skipped: no user ID') }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:360 — Trigger: update_item succeeded
 * under attribute_exists(userId) — f-string source: f'User updated: {user_id}'
 * (clerk_webhooks.py:360).
 */
export const USER_UPDATED_OK = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User updated: .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:362-365 — Trigger:
 * ConditionalCheckFailedException on attribute_exists(userId) — row absent —
 * BUG-FOR-BUG: Returns 200 so Clerk does NOT retry. If user.created never lands, the
 * profile update is silently lost forever. — f-string source: f'Skipped: user {user_id}
 * does not exist yet' (clerk_webhooks.py:365). 200 means Clerk does NOT retry — if
 * user.created never lands the profile update is lost silently.
 */
export const USER_UPDATED_SKIPPED_NOT_EXIST = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^Skipped: user .+ does not exist yet$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:698 — Trigger: delete_item succeeded
 * under attribute_exists(userId) — f-string source: f'User deleted: {user_id}'
 * (clerk_webhooks.py:698).
 */
export const USER_DELETED_OK = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User deleted: .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:699-702 — Trigger:
 * ConditionalCheckFailedException — row absent (idempotent redelivery) — f-string
 * source: f'No-op: user {user_id} not in DDB' (clerk_webhooks.py:702).
 */
export const USER_DELETED_NOOP_NOT_IN_DDB = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^No-op: user .+ not in DDB$/) }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). Both membership
 * branches emit the identical 200 {"message": "Skipped: no org ID"}. Collapsed sites:
 * functions/clerk_webhooks/clerk_webhooks.py:396 (data.organization.id falsy);
 * functions/clerk_webhooks/clerk_webhooks.py:599 (organization.id falsy)
 */
export const CLERK_WEBHOOK_SKIPPED_NO_ORG_ID = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.literal('Skipped: no org ID') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). Both membership
 * branches emit the identical 200 f'Skipped: no merchant ID for org {org_id}'. Collapsed
 * sites: functions/clerk_webhooks/clerk_webhooks.py:407-409 (organization.public_metadata
 * has neither dynamodb_merchant_id nor dynamodb_merchant_name (both falsy));
 * functions/clerk_webhooks/clerk_webhooks.py:618-620 (neither dynamodb_merchant_id nor
 * dynamodb_merchant_name present in organization.public_metadata)
 */
export const CLERK_WEBHOOK_SKIPPED_NO_MERCHANT_ID = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^Skipped: no merchant ID for org .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:439 — Trigger: row exists AND
 * 'activeOrgId' not in the item → update_item ran — f-string source: f'User {user_id}
 * already exists; activeOrgId set to {merchant_id}' (clerk_webhooks.py:439). Note the
 * SEMICOLON — the sibling at :442 uses a comma.
 */
export const MEMBERSHIP_CREATED_EXISTING_USER_ACTIVE_ORG_SET = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User .+ already exists; activeOrgId set to .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:440-442 — Trigger: row exists AND
 * already has activeOrgId → NO write at all — f-string source: f'User {user_id} already
 * exists, activeOrgId unchanged' (clerk_webhooks.py:442). Note the COMMA versus the
 * semicolon at :439.
 */
export const MEMBERSHIP_CREATED_EXISTING_USER_NOOP = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User .+ already exists, activeOrgId unchanged$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:473 — Trigger: no existing row and
 * put_item succeeded under attribute_not_exists(userId) — f-string source: f'User
 * created via membership: {user_id}' (clerk_webhooks.py:473).
 */
export const MEMBERSHIP_CREATED_NEW_USER = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User created via membership: .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:475-491 — Trigger: put_item hit
 * ConditionalCheckFailedException (user.created won the race) and the follow-up
 * if_not_exists update succeeded — f-string source: f'User {user_id} activeOrgId set via
 * race recovery' (clerk_webhooks.py:491).
 */
export const MEMBERSHIP_CREATED_RACE_RECOVERY = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User .+ activeOrgId set via race recovery$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:515 — Trigger: always — this handler
 * has exactly one return and NO branches. Pure no-op: zero DynamoDB writes. — f-string
 * source: f'Membership update acknowledged: user={user_id} clerk_org={clerk_org_id}'
 * (clerk_webhooks.py:515). BOTH values are unguarded and may be EMPTY, so 'Membership
 * update acknowledged: user= clerk_org=' is a valid rendering — hence * not + . This
 * handler has one return and no branches; zero DynamoDB writes.
 */
export const MEMBERSHIP_UPDATED_ACK = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^Membership update acknowledged: user=[\s\S]* clerk_org=[\s\S]*$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:637-639 — Trigger: get_item returned
 * no Item — f-string source: f'Skipped: user {user_id} does not exist in DDB'
 * (clerk_webhooks.py:639).
 */
export const MEMBERSHIP_DELETED_SKIPPED_USER_NOT_IN_DDB = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^Skipped: user .+ does not exist in DDB$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:642-644 — Trigger: existing row has no
 * activeOrgId (or it is an empty string) — f-string source: f'No-op: user {user_id} had
 * no activeOrgId' (clerk_webhooks.py:644).
 */
export const MEMBERSHIP_DELETED_NOOP_NO_ACTIVE_ORG = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^No-op: user .+ had no activeOrgId$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:646-648 — Trigger: current activeOrgId
 * not in match_candidates (the [dynamodb_merchant_id, dynamodb_merchant_name] non-empty
 * list) — f-string source: f'No-op: user {user_id} activeOrgId points to a different
 * org' (clerk_webhooks.py:648).
 */
export const MEMBERSHIP_DELETED_NOOP_DIFFERENT_ORG = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^No-op: user .+ activeOrgId points to a different org$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:665 — Trigger: activeOrgId matched a
 * candidate and the conditional REMOVE succeeded — f-string source: f'User {user_id}
 * activeOrgId cleared (was {current_active_org})' (clerk_webhooks.py:665). Parentheses
 * are literal and escaped in the regex.
 */
export const MEMBERSHIP_DELETED_CLEARED = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^User .+ activeOrgId cleared \(was .+\)$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:666-669 — Trigger:
 * ConditionalCheckFailedException on ConditionExpression='activeOrgId = :current' —
 * f-string source: f'No-op: activeOrgId changed concurrently for user {user_id}'
 * (clerk_webhooks.py:669).
 */
export const MEMBERSHIP_DELETED_NOOP_CONCURRENT_CHANGE = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^No-op: activeOrgId changed concurrently for user .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:537-538 — Trigger: data.id falsy OR
 * (data.slug or '').strip() empty — clerk_webhooks.py:538.
 */
export const ORGANIZATION_UPSERT_SKIPPED_NO_ID_OR_SLUG = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.literal('Skipped: no organization id or slug') }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:540-542 — Trigger: env
 * ORGANIZATIONS_TABLE empty (default '' at :49) — BUG-FOR-BUG: 200 + silent skip means a
 * misconfigured env var produces NO error signal anywhere — the slug mirror just stops
 * working. — clerk_webhooks.py:542. A 200 on a misconfigured env var — no error signal
 * anywhere.
 */
export const ORGANIZATION_UPSERT_SKIPPED_TABLE_UNCONFIGURED = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.literal('Skipped: organizations table not configured') }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:558-560 — Trigger:
 * public_metadata.dynamodb_merchant_id absent AND the clerkOrganizationId-index query
 * returned no items — f-string source: f'Skipped: no V2 org for {clerk_org_id}'
 * (clerk_webhooks.py:560).
 */
export const ORGANIZATION_UPSERT_SKIPPED_NO_V2_ORG = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^Skipped: no V2 org for .+$/) }).strict());

/**
 * site functions/clerk_webhooks/clerk_webhooks.py:570 — Trigger: org resolved and
 * org_table.update_item succeeded — f-string source: f'organizationSlug mirrored for
 * {org_id}' (clerk_webhooks.py:570). Lowercase leading 'o' is verbatim.
 */
export const ORGANIZATION_UPSERT_OK = variant(200, H_POST_OPTIONS_UC_2, z.object({ message: z.string().regex(/^organizationSlug mirrored for .+$/) }).strict());


// ===== functions/create_org/create_org.py ================================
/**
 * Response helper: null if none — every return is an inline dict literal. There is NO
 * response helper. Two body-shape builders exist but they return the BODY only, not the
 * HTTP envelope: functions/create_org/create_org.py:231 _creation_response(org_record,
 * clerk_org_id) -> dict and the inline literal at :914 and :1200.
 */
/**
 * NOTES: 20 response variants. (1) HEADERS: every non-OPTIONS variant sets the same 3 CORS
 * headers + Content-Type: application/json; the OPTIONS 204 sets the 3 CORS headers and NO
 * Content-Type. Exactly one variant (409) adds Retry-After: "3" (string). No variant omits
 * headers entirely. (2) Access-Control-Allow-Headers here is
 * "Content-Type,Authorization,Idempotency-Key" — DIFFERENT from every other handler in
 * this group, which use "Content-Type,Authorization". Do not share one header constant
 * across the group. (3) EMF: there are ZERO EMF metric emissions in this handler (grep for
 * _aws|CloudWatchMetrics|put_metric_data over functions/create_org and
 * functions/onboarding returned nothing, exit 1). Metrics are produced OUT OF BAND by
 * CloudWatch logs.MetricFilter resources in stacks/ApiStack.ts keyed on structured_log
 * `event` values — createOrgRejectedNoEmailFilter (ApiStack.ts:1944) on
 * '"create_org_rejected_no_email"'. Changing that log line breaks a prod alarm with no
 * code-level signal. (4) SUCCESS DOES NOT MEAN COMPLETE: after the V2 put_item, Stripe
 * account creation, the Stripe accountId write-back, the users-table activeOrgId write,
 * the Clerk signupComplete metadata write, the welcome email, and the EventBridge publish
 * are ALL fire-and-forget. A 200 guarantees only Clerk-org + V2-row. (5) IDEMPOTENCY-KEY
 * is optional; absent it the key is deterministic per (user, lowercased org name) via
 * sha256 — f"legacy-{digest[:48]}" (:134). Two DIFFERENT users creating same-named orgs
 * get different keys (user_id is in the digest). (6) The recovered_org_id read from Clerk
 * public_metadata.dynamodb_merchant_id (:875) is used as a DynamoDB partition key with NO
 * validation against is_valid_organization_id — brief rule 4 finding. (7)
 * statementDescriptor is a blind 22-char truncation of orgName (:698) with no charset
 * validation.
 */

/**
 * site functions/create_org/create_org.py:764 — Trigger:
 * event.requestContext.http.method == "OPTIONS" — BUG-FOR-BUG: 204 with a non-absent
 * body key whose value is "" — API Gateway drops it, but the Lambda return shape
 * literally has body:"". Also: this variant is the ONLY one lacking Content-Type. —
 * create_org.py:764 returns body: "" — not JSON. Also the only create_org variant with
 * no Content-Type header.
 */
export const CREATE_ORG_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC_IDEM);

/**
 * site functions/create_org/create_org.py:768 — Trigger: _get_user_id(event) returns
 * falsy — i.e. event.requestContext.authorizer.lambda.sub is absent/empty. NOTE this is
 * a HANDLER-produced 403 (has CORS + error key), distinct from the authorizer's own
 * {"message":"Unauthorized"} 401. — BUG-FOR-BUG: Status is 403 but the body string is
 * "Unauthorized" (the 401 word). Pinned as-is. — create_org.py:771. Handler-produced 403
 * whose body says 'Unauthorized' (the 401 word) — pinned bug-for-bug. Distinct from the
 * authorizer's own {"message":"Unauthorized"}.
 */
export const CREATE_ORG_NO_SUB_403 = variant(403, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Unauthorized') }).strict());

/**
 * site functions/create_org/create_org.py:777 — Trigger: json.loads(event.get("body") or
 * "{}") raises json.JSONDecodeError — BUG-FOR-BUG: Only json.JSONDecodeError is caught.
 * A non-dict JSON body (e.g. `[]` or `"x"` or `5`) parses fine and then body.get(...)
 * raises AttributeError -> UNCAUGHT -> gateway 500. See uncaughtExceptions. —
 * create_org.py:780. No trailing 'body' word, unlike ein_lookup/website_enrich.
 */
export const CREATE_ORG_INVALID_JSON_400 = variant(400, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Invalid JSON') }).strict());

/**
 * site functions/create_org/create_org.py:786 — Trigger: body.entityType is a non-empty
 * value whose str().strip().lower() is not in VALID_ENTITY_TYPES =
 * {"nonprofit","individual"} (functions/create_org/constants.py:114) — BUG-FOR-BUG: The
 * error message advertises the STORAGE casing ('Individual','Nonprofit') but the
 * accepted input values are the LOWERCASE forms ('individual','nonprofit'). A caller
 * obeying the message literally still gets a 400? No — normalization lowercases, so
 * 'Individual' IS accepted. But the message is misleading about the canonical set. Pin
 * the literal. — create_org.py:789 emits str(ValueError) from _normalize_entity_type.
 * Source f-string is create_org.py:347-348: f"entityType must be one of
 * {sorted(ENTITY_TYPE_STORAGE_VALUES.values())}". ENTITY_TYPE_STORAGE_VALUES.values() =
 * ['Nonprofit','Individual'] (constants.py:115-118); sorted ->
 * ['Individual','Nonprofit']; Python list repr uses single quotes and ', ' separator.
 * Rendered by executing the expression, so the literal is exact and fully closed — a
 * z.literal, not a regex.
 */
export const CREATE_ORG_BAD_ENTITY_TYPE_400 = variant(400, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal("entityType must be one of ['Individual', 'Nonprofit']") }).strict());

/**
 * site functions/create_org/create_org.py:794 — Trigger: (body.get("orgName") or
 * "").strip() is empty — create_org.py:797.
 */
export const CREATE_ORG_MISSING_ORG_NAME_400 = variant(400, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('orgName is required') }).strict());

/**
 * site functions/create_org/create_org.py:816 — Trigger: _get_clerk() returns None —
 * i.e. env CLERK_SECRET_KEY_PATH is unset/empty (:259). NOTE: if CLERK_SECRET_KEY_PATH
 * IS set but ssm.get_parameter raises, that exception is UNCAUGHT (:261) -> gateway 500,
 * not this 503. — create_org.py:819. Fires only when env CLERK_SECRET_KEY_PATH is unset;
 * an SSM failure at create_org.py:261 is UNCAUGHT and becomes a gateway 500 instead.
 */
export const CREATE_ORG_CLERK_NOT_CONFIGURED_503 = variant(503, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Service not configured') }).strict());

/**
 * site functions/create_org/create_org.py:825 — Trigger: The Idempotency-Key request
 * header (case-insensitive match, _header at :112) is non-empty after strip AND
 * uuid.UUID(supplied) raises ValueError/AttributeError
 * (functions/create_org/create_org.py:132) — create_org.py:825 emits str(ValueError)
 * raised at create_org.py:132 — a plain literal, not an f-string.
 */
export const CREATE_ORG_BAD_IDEMPOTENCY_KEY_400 = variant(400, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Idempotency-Key must be a valid UUID') }).strict());

/**
 * site functions/create_org/create_org.py:903 — Trigger: memberships_lookup_failed
 * (clerk.users.get_organization_memberships raised, :833) OR
 * membership_ownership_lookup_failed (clerk.organizations.get or org_table.get_item
 * raised inside the membership loop, :891) — create_org.py:903.
 */
export const CREATE_ORG_MEMBERSHIP_LOOKUP_FAILED_503 = variant(503, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Temporary error checking organization access. Please try again.') }).strict());

/**
 * site functions/create_org/create_org.py:905 — Trigger: env
 * MULTI_ORG_CREATION_ENABLED.lower() NOT in {"1","true","yes"} (default "true" when
 * unset, :898) AND has_previously_created_organization (some Clerk org has created_by ==
 * user_id, :853) AND recovered_clerk_org_id is empty — create_org.py:905.
 * Handler-produced 403 gated on env MULTI_ORG_CREATION_ENABLED.
 */
export const CREATE_ORG_MULTI_ORG_DISABLED_403 = variant(403, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Creating an additional organization is temporarily unavailable.') }).strict());

/**
 * site functions/create_org/create_org.py:911 — Trigger: _claim_creation_request raised
 * any Exception (a non-ConditionalCheckFailed ClientError from users_table.update_item,
 * or a get_item failure inside _creation_state) — create_org.py:911.
 */
export const CREATE_ORG_LOCK_FAILED_503 = variant(503, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Temporary error preparing organization creation. Please try again.') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (3 code paths, one wire shape). All three POST
 * /onboarding/create-org 200s carry the SAME four keys (organizationId, clerkOrgId,
 * activationPath, stripeAccountId) and are not separable on the wire. The fresh-create
 * path narrows activationPath to hasWebsite|doesNotHaveWebsite and all four values to
 * strings; the :890 idempotent replay reads them off the stored item so they are
 * unvalidated; the :913 state-cache replay does not even run activationPath through
 * _normalize_activation_path. The widest of the three (state-cache) therefore CONTAINS the
 * other two, so modelling them separately makes every real success AMBIGUOUS. Collapsed to
 * the widest pin — which is the honest one, because a consumer cannot tell a fresh create
 * from a replay. The narrowing that IS guaranteed per-path is recorded here and in
 * openapi.yaml, not enforced. Collapsed sites: functions/create_org/create_org.py:914
 * (_claim_creation_request returned claimed=False AND creation_state.status == "SUCCEEDED"
 * (i.e. same requestId already completed within CREATION_RESULT_SECONDS = 86400s));
 * functions/create_org/create_org.py:890 (A Clerk org membership of this user has
 * public_metadata.charitystack_creation_request_id == request_id (OR passes the
 * legacy-retry predicate at :865-871), its public_metadata.dynamodb_merchant_id resolves
 * to an existing item in ORGANIZATIONS_TABLE, and that get_item returns an Item.);
 * functions/create_org/create_org.py:1217 (All prior guards passed and the V2 put_item
 * succeeded (or ConditionalCheckFailed-swallowed).)
 */
export const CREATE_ORG_REPLAY_200 = variant(200, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ organizationId: z.unknown(), clerkOrgId: z.unknown(), activationPath: z.unknown(), stripeAccountId: z.unknown() }).strict());

/**
 * site functions/create_org/create_org.py:922 — Trigger: _claim_creation_request
 * returned claimed=False and the state's status is NOT "SUCCEEDED" (an unexpired PENDING
 * / CLERK_CREATED / RECOVERY_REQUIRED lock held by this or another request) —
 * BUG-FOR-BUG: This is the ONLY variant across the whole group that adds a Retry-After
 * header. Its value is the STRING "3", not a number. — create_org.py:922. The only
 * variant in the whole group carrying a Retry-After header (string '3') — a header fact,
 * not part of this body.
 */
export const CREATE_ORG_IN_PROGRESS_409 = variant(409, H_POST_OPTIONS_UC_JSON_RETRY_IDEM, z.object({ error: z.literal('Organization creation is already in progress. Please retry shortly.') }).strict());

/**
 * site functions/create_org/create_org.py:956 — Trigger: body.email is empty after strip
 * AND clerk.users.get(user_id=user_id) raised (:948-949) — create_org.py:959.
 */
export const CREATE_ORG_EMAIL_LOOKUP_FAILED_503 = variant(503, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Temporary error verifying your account. Please try again.') }).strict());

/**
 * site functions/create_org/create_org.py:981 — Trigger: body.email is still empty after
 * the Clerk email-resolution loop (:961-972) — i.e. the Clerk lookup SUCCEEDED but the
 * account has no email_addresses entry with a non-empty email_address. — BUG-FOR-BUG:
 * This path emits the CRITICAL marker create_org_rejected_no_email (:979) which is
 * ALARMED via logs.MetricFilter "createOrgRejectedNoEmailFilter" in
 * stacks/ApiStack.ts:1944 (pattern '"create_org_rejected_no_email"'). A contract rewrite
 * that changes this log line silently kills a prod alarm. — create_org.py:985-986 is a
 * two-part implicit string concatenation: 'A verified email address is required to
 * create an organization. ' + 'Please verify your email and try again.' — exactly one
 * space joins the sentences, no newline. This path also emits the CRITICAL marker
 * create_org_rejected_no_email, alarmed by logs.MetricFilter
 * createOrgRejectedNoEmailFilter (stacks/ApiStack.ts:1944).
 */
export const CREATE_ORG_NO_EMAIL_400 = variant(400, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('A verified email address is required to create an organization. Please verify your email and try again.') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). Both emit {error,
 * code:'INVALID_ORG_INPUT'}. The slug arm's error is one of the 5 closed validate_slug
 * messages; the Clerk arm's is Clerk's uncapped long_message, an open string that CONTAINS
 * the closed set. Not separable. Collapsed to the open pin; the closed 5-member set is
 * retained as SLUG_VALIDATION_ERRORS and asserted in the suite. Collapsed sites:
 * functions/create_org/create_org.py:1032 (clerk.organizations.create raised AND
 * _clerk_validation_message(e) returned a non-empty string — which requires the
 * exception's str() to embed a JSON blob with errors[0].code starting with the literal
 * prefix "form_" (:315)); functions/create_org/create_org.py:808 (body.organizationSlug is
 * non-empty after .strip().lower() AND validate_slug(org_slug) returns a non-None message.
 * NOTE: because the handler lowercases first (:804), the "Organization Slug is required"
 * branch is UNREACHABLE from this call site (empty -> the `if org_slug:` guard skips
 * validation entirely).)
 */
export const CREATE_ORG_INVALID_ORG_INPUT_400 = variant(400, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.string(), code: z.literal('INVALID_ORG_INPUT') }).strict());

/**
 * site functions/create_org/create_org.py:1037 — Trigger: clerk.organizations.create
 * raised AND _clerk_validation_message(e) returned "" (not positively identifiable as a
 * form_* validation error) — create_org.py:1040.
 */
export const CREATE_ORG_CLERK_502 = variant(502, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Could not create organization. Please try again.') }).strict());

/**
 * site functions/create_org/create_org.py:1084 — Trigger: org_table.put_item raised any
 * Exception OTHER than ConditionalCheckFailedException (:1067). A
 * ConditionalCheckFailedException is swallowed with `pass` (:1065-1066) and execution
 * CONTINUES to the success path. — BUG-FOR-BUG: This is a HANDLER-produced 500 with CORS
 * headers, distinguishable from a gateway 500 (which has no CORS and body
 * {"message":"Internal Server Error"}). Also note: the ConditionalCheckFailed swallow
 * means an org row that already exists produces a 200 whose response body is built from
 * the LOCALLY CONSTRUCTED v2_record, not the stored row — the returned activationPath
 * can disagree with what is actually in DynamoDB. — create_org.py:1087. Handler-produced
 * 500 (carries CORS), distinguishable from a gateway 500. A
 * ConditionalCheckFailedException does NOT reach here — it is swallowed at
 * create_org.py:1065-1066 and flows on to the 200.
 */
export const CREATE_ORG_V2_WRITE_FAILED_500 = variant(500, H_POST_OPTIONS_UC_JSON_IDEM, z.object({ error: z.literal('Could not save organization. Please try again.') }).strict());


// ===== functions/get_location/get_location.py ============================
/**
 * Response helper: null — NO helper. handler() at :84 builds the single response dict
 * INLINE with an explicit header map.
 */
/**
 * NOTES: This is the only handler in the group with NO response helper and NO
 * DecimalEncoder — both are contract-relevant. `locationId` is passed through from
 * DynamoDB with no type assertion; the schema must be `z.unknown()` or
 * `z.string().nullable()` with an explicit note that the code does not enforce it (Rule
 * 4). An unauthenticated-shaped or org-less caller gets 200 with an empty array, NOT a 403
 * — do not 'fix' that in the contract.
 */

/**
 * site functions/get_location/get_location.py:84 — Trigger: unconditional — this is the
 * ONLY return in handler() — BUG-FOR-BUG: SERIALIZATION: `json.dumps(results)` at :91
 * uses the STOCK encoder with NO DecimalEncoder (unlike every other handler in this
 * group). If locationId is ever stored as a DynamoDB Number, boto3 returns a
 * decimal.Decimal and json.dumps raises `TypeError: Object of type Decimal is not JSON
 * serializable` → uncaught → gateway 500. Currently latent (locationId is a string in
 * practice) but it is a real pinned defect. ALSO: an unauthorized/unresolvable caller
 * gets 200 [] rather than 403 — the ONLY handler in this group with no failure status. —
 * locationId is `org.get('locationId')` passed through with NO validation and NO
 * coercion — it is whatever the DynamoDB attribute holds (string, null, or a Number), so
 * z.unknown() per rule 4. NOTE the encoder: get_location.py:91 calls json.dumps(results)
 * with the STOCK encoder and defines no DecimalEncoder, so a Number-typed locationId
 * would raise TypeError and produce a gateway 500 rather than a body — latent, but it
 * means a numeric locationId is currently unrepresentable in a successful response. The
 * array is EMPTY ([]) when no org names resolve; this route has no 403/404 path at all,
 * so [] is the unauthorized-shaped response.
 */
export const GET_LOCATION_OK_200 = variant(200, H_OPTIONS_GET_UC, z.array(z.object({ merchantName: z.string(), locationId: z.unknown() }).strict()));


// ===== functions/get_merchant/get_merchant.py ============================
/**
 * Response helper: functions/get_merchant/get_merchant.py:28 _response(status_code, body)
 * -> headers
 * {'Access-Control-Allow-Headers':'Content-Type','Access-Control-Allow-Origin':'*','Access-Control-Allow-Methods':'OPTIONS,GET'}
 * ; body json.dumps(body, cls=DecimalEncoder). Note capital-C 'Content-Type' here vs
 * lowercase 'content-type' in privacy_policy/org_slug.
 */
/**
 * NOTES: **LOUD FINDING — GET /merchant IS A VERBATIM ROW PASSTHROUGH.** The 200 body is
 * `flatten_v2_to_v1_compat(<raw V2 organizations item>)`, which begins with
 * `dict(v2_record)` (a full copy of every attribute on the DynamoDB row) and then ADDS
 * flat V1 aliases on top. There is no allowlist, no projection, and no redaction. A
 * CONTRACT FOR THIS ROUTE CANNOT BE WRITTEN FROM THIS HANDLER ALONE — it requires a full
 * passthrough census of every attribute any writer puts on the V2 organizations table
 * across the whole fleet (accounts, transactions, payouts, paypal, marketing, pricing,
 * clerk_webhooks, backfills). Recommend: enumerate the union of attributes written to
 * ORGANIZATIONS_TABLE and treat the response as `z.object({...known}).passthrough()`, and
 * separately audit whether the org_utils _SENSITIVE_FIELDS set (virtuousSecretKey,
 * stripeAccountID, zapier*WebhookURL, paypalTrackingID, backupPaypalTrackingID,
 * paypalMerchantEmail, backupPaypalMerchantEmail) should be reaching the dashboard at all.
 * Second finding: `get_base_merchant` (:177-185) is a no-op identity function; its `if
 * base_merchant_name == nonprofit_name` guard at :198 is therefore ALWAYS true and the
 * read_organization_v2 fallback at :202 is DEAD CODE. Third: header key casing here is
 * 'Content-Type' (capitalized) while privacy_policy/org_slug/global_custom_inputs' 403
 * path use lowercase 'content-type' — the header maps are genuinely different literals
 * across the group.
 */

/**
 * site functions/get_merchant/get_merchant.py:218 — Trigger: _get_merchant_v2 returned
 * falsy AND _resolve_nonprofit_names(event) returned an empty list — no orgId, no
 * groups, and no resolvable user activeOrgId
 */
export const GET_MERCHANT_NO_ORG_403 = variant(403, H_OPTIONS_GET_UC, z.object({ error: z.literal('No organization membership found') }).strict());

/**
 * site functions/get_merchant/get_merchant.py:219 — Trigger: _get_merchant_v2 returned
 * falsy but at least one nonprofit name resolved — BUG-FOR-BUG: REFLECTION FINDING: when
 * the caller authenticated via the Cognito/groups leg, nonprofit_names[0] is derived
 * from the authorizer's `groups` string (`groups_str[1:-1].split(' ')`, hyphens→spaces)
 * and is echoed into the response body UNVALIDATED. Not attacker-controllable through a
 * Clerk JWT (the name comes from the DynamoDB row there), but it IS an unvalidated
 * passthrough of an authorizer-supplied value into a 404 body. — f-string source:
 * f'Merchant not found: {get_base_merchant(nonprofit_names[0])}'. get_base_merchant is
 * the IDENTITY function (get_merchant.py:185 `return nonprofit_name`), so the suffix is
 * the org name echoed VERBATIM and UNVALIDATED. On the Cognito/groups auth leg that name
 * comes from the authorizer `groups` string (`groups_str[1:-1].split(' ')`,
 * hyphens→spaces) and is reflected into the body — the regex is deliberately open-ended
 * on the suffix (rule 4: do not narrow what the code does not validate).
 */
export const GET_MERCHANT_NOT_FOUND_404 = variant(404, H_OPTIONS_GET_UC, z.object({ error: z.string().regex(/^Merchant not found: /) }).strict());

/**
 * site functions/get_merchant/get_merchant.py:223 — Trigger: _get_merchant_v2 resolved
 * an org record — BUG-FOR-BUG: The response body is NOT a fixed schema — it is whatever
 * attributes the row happens to carry at read time. Adding an attribute to the V2
 * organizations table (by ANY writer in ANY repo) silently widens this
 * public-to-the-dashboard response with zero code change here. org_utils itself names
 * fields it considers SENSITIVE for shadow-diff redaction (_SENSITIVE_FIELDS at
 * functions/org_utils.py:1032: virtuousSecretKey, stripeAccountID, zapierWebhookURL,
 * zapierPayoutWebhookURL, paypalTrackingID, backupPaypalTrackingID, paypalMerchantEmail,
 * backupPaypalMerchantEmail) — that redaction applies ONLY to log diffs, NOT to this
 * response, so those attributes DO pass through to the caller when present on the row. —
 * OPEN PASSTHROUGH — the handler returns the ENTIRE V2 organizations DynamoDB row plus
 * flat V1 aliases. `_response(200, result)` where result =
 * org_utils.flatten_v2_to_v1_compat(item), whose first statement is `result =
 * dict(v2_record) # Start with full V2 record` (org_utils.py:960); it then ADDS
 * V2_TO_V1_FIELD_MAP aliases, the 'merchantFAQs' alias, onboarding mirrors, a normalized
 * entityType, and _add_billing_fee_compat_fields output, and get_merchant may add
 * billingPlan/billingPlanSource. There is NO allowlist, NO projection, NO redaction in
 * the handler. The key set is therefore closed by an APPROVED CENSUS of every writer to
 * ORGANIZATIONS_TABLE (198 rows / 61 top-level keys, run separately), NOT by this
 * handler — do not infer it from the code. Deliberately z.record(z.unknown()) rather
 * than .passthrough() per the hard rule.
 */
export const GET_MERCHANT_OK_200 = variant(200, H_OPTIONS_GET_UC, z.record(z.unknown()));


// ===== functions/global_custom_inputs/global_custom_inputs.py ============
/**
 * Response helper: null — NO helper. Every response dict is built INLINE, and the header
 * maps are NOT consistent across them (see headerSets).
 */
/**
 * NOTES: Three distinct header maps from one route is the load-bearing oddity here. Also:
 * the readOnly gate runs AFTER body parsing, so a malformed body from a read-only caller
 * 500s instead of 403ing; and `customInputs` is written to DynamoDB and echoed back with
 * ZERO validation. The 'V2 read-back confirm' the :124 comment describes does not exist —
 * write_organization_v2_or_raise explicitly does no read-back (org_utils.py:1338). The
 * comment is stale; do not model a read-back in the contract.
 */

/**
 * site functions/global_custom_inputs/global_custom_inputs.py:94 — Trigger:
 * get_nonprofit_names(event) returned []
 */
export const GCI_NO_ORG_403 = variant(403, H_PATCH_LC, z.object({ error: z.literal('No organization membership found') }).strict());

/**
 * site functions/global_custom_inputs/global_custom_inputs.py:108 — Trigger:
 * has_write_permissions(event) False — no authorizer.lambda, or
 * lam.get('readOnly','false').lower() == 'true' — BUG-FOR-BUG: ORDER DEFECT: the
 * readOnly check runs AFTER `body = json.loads(event['body'])` and `custom_inputs =
 * body['customInputs']` at :104-105. A read-only caller with a malformed body gets an
 * uncaught KeyError/JSONDecodeError → gateway 500 instead of the 403. Pin the ordering
 * as-is.
 */
export const GCI_READONLY_403 = variant(403, H_PATCH_LC, z.object({ error: z.literal('You do not have permission to perform this action') }).strict());

/**
 * site functions/global_custom_inputs/global_custom_inputs.py:128 — Trigger: any
 * future.result() raised — write_organization_v2_or_raise raises on unset
 * ORGANIZATIONS_TABLE, no V2 row for the organizationName, unmapped V2 fields, no mapped
 * fields, or a re-raised ClientError/BotoCoreError from the GSI query or update_item —
 * BUG-FOR-BUG: A 500 (PATCH route) emits Access-Control-Allow-Methods: 'POST' — wrong
 * method AND different from the 403's 'PATCH'. Also Access-Control-Allow-Headers flips
 * from lowercase 'content-type' (403) to capitalized 'Content-Type' (500/200) within the
 * SAME route.
 */
export const GCI_WRITE_FAILED_500 = variant(500, H_POST_UC, z.object({ error: z.literal('Failed to persist organization update') }).strict());

/**
 * site functions/global_custom_inputs/global_custom_inputs.py:138 — Trigger: all futures
 * completed without raising — BUG-FOR-BUG: FINDING (Rule 4): `customInputs` reaches BOTH
 * the DynamoDB write and the 200 response completely unvalidated — any JSON value
 * (string, number, deeply-nested object, 300KB blob) is accepted. The only backstop is
 * DynamoDB's 400KB item cap, which would surface as GCI_WRITE_FAILED_500. The response
 * shape is literally 'whatever the caller sent'. — globalCustomInputs is
 * body['customInputs'] ECHOED VERBATIM with ZERO validation — never type-checked,
 * size-checked, or shape-checked before being written to DynamoDB and returned
 * (global_custom_inputs.py:105 → :44 → :47). Any JSON value a caller can send is
 * admissible, so z.unknown() per rule 4; do NOT narrow it to an array or object. One
 * array element per resolved nonprofit name (in practice always length 1).
 */
export const GCI_OK_200 = variant(200, H_POST_UC, z.array(z.object({ Attributes: z.object({ globalCustomInputs: z.unknown() }).strict() }).strict()));

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). global_custom_inputs
 * and nonprofit_settings_dashboard emit the identical 500 {'error': 'Something went
 * wrong'} with the identical header map on a non-PATCH method. Handler-reachable only.
 * Collapsed sites: functions/global_custom_inputs/global_custom_inputs.py:155
 * (event['requestContext']['http']['method'] != 'PATCH'. Only PATCH /global-custom-inputs
 * is declared in ApiStack (stacks/ApiStack.ts:1168), so this is practically unreachable
 * via the gateway.);
 * functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:857
 * (event['requestContext']['http']['method'] != 'PATCH'. Only PATCH /nonprofit-settings is
 * declared (stacks/ApiStack.ts:1165), so practically unreachable.)
 */
export const UNROUTABLE_NON_PATCH_500 = variant(500, H_OPTIONS_POST_GET_LC, z.object({ error: z.literal('Something went wrong') }).strict());


// ===== functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py
/**
 * Response helper: null — NO helper. Eleven inline response dicts with three distinct
 * header maps.
 */
/**
 * NOTES: Highest-variance handler in the group: FOUR distinct header maps on ONE route,
 * and the 200 advertises Access-Control-Allow-Methods 'POST' for a PATCH endpoint. Six
 * REQUIRED body fields (addressLine1, addressLine2, city, state, zip, ein) are read with
 * bracket access and have NO 400 — a missing one is a gateway 500. The readOnly gate is
 * the LAST check, after every validation branch. The 500 body LEAKS raw exception text
 * including DynamoDB table names and org_utils' internal field-map guidance. Partial-write
 * hazard: the early return inside the as_completed loop abandons in-flight futures, so a
 * 500 can still be followed by successful writes from the same batch. Finally, a PATCH
 * here fires side effects on Stripe (Apple Pay domain registration) and on Clerk (org
 * public_metadata PATCH + a CROSS-USER phone attach on the Clerk user identified by an
 * attacker-supplied rc_user_id, guarded fail-closed by clerk_user_belongs_to_org) — all
 * swallowed, none reflected in the response.
 */

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:550 —
 * Trigger: get_nonprofit_names(event) returned []
 */
export const NS_NO_ORG_403 = variant(403, H_PATCH_LC, z.object({ error: z.literal('No organization membership found') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:572 —
 * Trigger: body.get('organizationBaseURLs') is not None and not isinstance(..., list)
 */
export const NS_BASEURLS_NOT_LIST_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('organizationBaseURLs must be a list') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:586 —
 * Trigger: len(sanitized) != len(organization_base_urls), where sanitized keeps only
 * non-empty strs with len(strip()) <= 200 matching VALID_URL_RE =
 * re.compile(r'^https?://', re.IGNORECASE) — BUG-FOR-BUG: The comparison is against the
 * ORIGINAL list length, so an entry that is merely whitespace-only (dropped by the
 * `non_empty` filter) also trips this 400 even though the intent reads as 'silently drop
 * empties'. Pinned as-is.
 */
export const NS_BASEURLS_INVALID_ENTRY_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('One or more organizationBaseURLs entries are invalid (must be http/https URLs, max 200 chars)') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:596 —
 * Trigger: len(sanitized) > 50
 */
export const NS_BASEURLS_TOO_MANY_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('organizationBaseURLs cannot exceed 50 entries') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:612 —
 * Trigger: validate_timezone raised ValueError, i.e. ZoneInfo(tz_string) raised KeyError
 * or ValueError. Default when the key is absent is 'America/Chicago'. — BUG-FOR-BUG:
 * REFLECTION: unvalidated caller input echoed into the response body (Rule 4 finding).
 * Also, ZoneInfo can raise other exception types (e.g. zoneinfo.ZoneInfoNotFoundError is
 * a KeyError subclass so it IS caught; but a non-str input raises TypeError, which is
 * NOT caught) → TypeError escapes to a gateway 500. — f-string source: f'Invalid
 * timezone: {timezone_input}' where timezone_input = body.get('timezone',
 * 'America/Chicago') — the CALLER'S RAW string, reflected unvalidated. Open-ended suffix
 * per rule 4.
 */
export const NS_BAD_TIMEZONE_400 = variant(400, H_PATCH_LC, z.object({ error: z.string().regex(/^Invalid timezone: /) }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:629 —
 * Trigger: brandColor present, non-empty after strip, and not matching
 * VALID_HEX_COLOR_RE = re.compile(r'^#[0-9A-Fa-f]{6}$'). A non-str brandColor is coerced
 * to '' and PASSES (clearing the color). — BUG-FOR-BUG: A non-string brandColor (e.g.
 * 123 or null-not-absent) is silently coerced to '' and WRITTEN, clearing the stored
 * color, rather than 400ing. — The example hex #69995E is part of the literal message
 * text, not a value — reproduce verbatim.
 */
export const NS_BAD_BRAND_COLOR_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('brandColor must be a valid hex color (e.g., #69995E)') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:645 —
 * Trigger: len(org_description.strip()) > 500 (non-str coerced to '' first)
 */
export const NS_ORG_DESCRIPTION_TOO_LONG_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('orgDescription must be 500 characters or fewer') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:661 —
 * Trigger: websiteUrl non-empty after strip AND (len > 200 OR not
 * VALID_URL_RE.match(...)). Empty string is ALLOWED (clears the field).
 */
export const NS_BAD_WEBSITE_URL_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('websiteUrl must be a valid http/https URL (max 200 chars)') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:678 —
 * Trigger: privacyPolicyLink non-empty after strip AND (len > 200 OR not
 * VALID_URL_RE.match(...))
 */
export const NS_BAD_PRIVACY_POLICY_LINK_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('privacyPolicyLink must be a valid http/https URL (max 200 chars)') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:702 —
 * Trigger: rc_phone_raw non-empty but clean_phone_number(rc_phone_raw) returned '' —
 * phonenumbers.parse(s,'US') failed is_possible_number, or the national number had fewer
 * than MIN_PHONE_DIGITS=7 digits, or NumberParseException
 */
export const NS_BAD_REGISTRATION_PHONE_400 = variant(400, H_PATCH_LC, z.object({ error: z.literal('registrationContactPhone must be a valid phone number') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:731 —
 * Trigger: has_write_permissions(event) False — BUG-FOR-BUG: ORDER DEFECT (same class as
 * global_custom_inputs, worse here): the readOnly gate is the LAST check, running AFTER
 * all ten validation branches and after `body['addressLine1']` etc. A read-only caller
 * therefore receives 400s for validation problems before ever learning they cannot
 * write, and a read-only caller with a body missing 'addressLine1' gets an uncaught
 * KeyError → gateway 500.
 */
export const NS_READONLY_403 = variant(403, H_PATCH_LC, z.object({ error: z.literal('You do not have permission to perform this action') }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:793 —
 * Trigger: ANY future in the ThreadPoolExecutor batch raised —
 * write_organization_v2_or_raise (unset env / no V2 row / unmapped key / ClientError /
 * BotoCoreError), update_v2_brand_fields' `raise Exception(f"No V2 org record found for
 * '{nonprofit_name}'")`, its unwrapped update_item, or update_v2_registration_fields'
 * identical raise + unwrapped update_item — BUG-FOR-BUG: INTERNAL DETAIL LEAK: raw
 * exception text (table names, internal field-map guidance, botocore request ids)
 * reaches the HTTP client. Also: the early `return` inside the `for future in
 * as_completed(...)` loop ABANDONS the still-running futures — the ThreadPoolExecutor
 * context manager then blocks on shutdown, and OTHER writes in the same batch may still
 * land AFTER the 500 is composed. A 500 here does NOT mean nothing was written. —
 * f-string source: f'Failed to update {label}: {str(e)}' where label is one of
 * f'merchant:{nonprofit_name}', f'v2_brand:{nonprofit_name}',
 * f'v2_registration:{nonprofit_name}' (the three future_to_label assignments at
 * :758/:763/:770 close the prefix set). The tail is str(e) — RAW EXCEPTION TEXT leaked
 * to the client, which can include botocore ClientError text with the DynamoDB table
 * name, or org_utils' internal guidance f"Unmapped V2 fields in authoritative org write
 * for '{name}': [...] — add them to V1_TO_V2_FIELD_MAP or stop sending them." The regex
 * pins only the determinate prefix; the tail is unbounded.
 */
export const NS_WRITE_FAILED_500 = variant(500, H_PATCH_UC, z.object({ error: z.string().regex(/^Failed to update (merchant|v2_brand|v2_registration):/) }).strict());

/**
 * site functions/nonprofit_settings_dashboard/nonprofit_settings_dashboard.py:839 —
 * Trigger: all futures completed without raising — BUG-FOR-BUG: The 200 emits
 * Access-Control-Allow-Methods 'POST' on a PATCH route, and 'Content-Type' capitalized,
 * while every 400/403 on the same route emits 'PATCH' and lowercase 'content-type'.
 * Three different header maps on one route. Also: the response reports ONLY the flat
 * address/EIN/timezone write — brandColor, orgDescription, websiteUrl, privacyPolicyLink
 * and registrationContact are all written but NOT reflected in the body. — results holds
 * only the merchant-write futures' return values: update_merchant_table returns
 * {'Attributes': written} where written = {k: v for k, v in v2_fields.items() if v is
 * not None} — every key is OPTIONAL because a None-valued field is filtered out. The
 * v2_brand future's return (which carries stripe_account_id / previous_website) is
 * deliberately EXCLUDED from results (captured into v2_brand_pre_states at :787-790),
 * and update_v2_registration_fields returns None, filtered by `elif result is not None`.
 * Consequence: brandColor, orgDescription, websiteUrl, privacyPolicyLink and
 * registrationContact are all WRITTEN but never reflected in the response.
 */
export const NS_OK_200 = variant(200, H_POST_UC, z.array(z.object({ Attributes: NonprofitSettingsWrittenAttributes }).strict()));


// ===== functions/onboarding/account_session/account_session.py ===========
/**
 * Response helper: null if none — no helper; all returns are inline dict literals over
 * module-level CORS_HEADERS (:41-45)
 */
/**
 * NOTES: FINDING for the contract author (brief rule 4): the 500 body is `{"error":
 * str(e)}` — unvalidated provider text. The 200 body's client_secret is likewise a
 * provider value passed through with no validation. Neither can be pinned to a literal or
 * a narrow pattern. SECOND FINDING: :85 calls `boto3.dynamodb.conditions.Key` without the
 * submodule import — a latent AttributeError. THIRD: the `Access-Control-Allow-Methods`
 * header value differs per file ("POST,OPTIONS" here vs "GET,OPTIONS" in
 * account_status/account_email) — headers must be pinned per handler, not shared.
 */

/**
 * site functions/onboarding/account_session/account_session.py:104 — Trigger: method ==
 * "OPTIONS" — BUG-FOR-BUG: No Content-Type header on this variant. Only `POST
 * /stripe/account-session` is registered (ApiStack.ts:1298) so this branch is likely
 * unreachable via the HTTP API. — 204 with `"body": ""` (account_session.py:104).
 */
export const ACCOUNT_SESSION_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC);

/**
 * site functions/onboarding/account_session/account_session.py:107-111 — Trigger: method
 * not in ("OPTIONS", "POST") — including method == "" when requestContext.http.method is
 * absent — BUG-FOR-BUG: Unreachable through API Gateway (only POST is routed), but
 * reachable on a direct Lambda invoke or a synthetic event with no requestContext. A
 * contract must still pin it. — account_session.py:110 — hardcoded literal. Same text as
 * billing_plan's 405 but the two are separate variants with different headers.
 */
export const ACCOUNT_SESSION_METHOD_NOT_ALLOWED_405 = variant(405, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/onboarding/account_session/account_session.py:114-119 — Trigger:
 * _get_clerk_org_id returns "" — provider != "clerk" or orgId empty — BUG-FOR-BUG:
 * Handler-produced 403. STRING DRIFT ACROSS SIBLING HANDLERS: "No organization context"
 * here and in account_status, vs "No org context" in management_session/:106,
 * account_email/:75, subscription_cancel/:90. A contract MUST pin each per-file, not
 * unify. — account_session.py:118 — hardcoded literal, the LONG form (matches
 * account_status, differs from the other three handlers).
 */
export const ACCOUNT_SESSION_NO_ORG_CONTEXT_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('No organization context') }).strict());

/**
 * site functions/onboarding/account_session/account_session.py:124-128 — Trigger:
 * _get_org_by_clerk_org_id returns None (ORGANIZATIONS_TABLE unset -> :80 returns None
 * without querying, or zero Items) — BUG-FOR-BUG: Missing-env misconfiguration is
 * indistinguishable from a genuine miss (same as account_status). —
 * account_session.py:127 — hardcoded literal.
 */
export const ACCOUNT_SESSION_ORG_NOT_FOUND_404 = variant(404, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/onboarding/account_session/account_session.py:145-149 — Trigger:
 * _load_stripe_keys() returns False — STRIPE_SECRET_KEY_PATH env unset (:52-53) OR the
 * SSM get_parameter for the primary key raised (:58-60) — BUG-FOR-BUG: STATUS DRIFT vs
 * siblings: the SAME condition returns 503 here but 500 in stripe_management_session
 * (:121) and 500 in subscription_cancel (:105); in stripe_account_email the same
 * condition instead yields a 200 with {"email": ""} (:86-87). Four handlers, three
 * different outcomes for one failure. Pin each as-is. — account_session.py:148 —
 * hardcoded literal, status 503. The IDENTICAL condition and string is status 500 in
 * management_session and subscription_cancel; pin per-handler.
 */
export const ACCOUNT_SESSION_STRIPE_NOT_CONFIGURED_503 = variant(503, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Stripe not configured') }).strict());

/** Permission resolution is fail-closed: its dependency failure is a 503, denial is a 403. */
export const ACCOUNT_SESSION_PERMISSIONS_UNAVAILABLE_503 = variant(503, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Processor permissions are temporarily unavailable') }).strict());
export const ACCOUNT_SESSION_PERMISSION_DENIED_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Manage processor settings permission required') }).strict());
export const ACCOUNT_SESSION_IDENTITY_CONFLICT_409 = variant(409, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Stripe account identity needs reconciliation') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). Same empty-string
 * containment as MANAGEMENT_SESSION_CLIENT_SECRET_200. Collapsed sites:
 * functions/onboarding/account_session/account_session.py:205-209
 * (stripe.AccountSession.create() succeeded);
 * functions/onboarding/account_session/account_session.py:137-141 (stripe_account_id
 * falsy: both stripeDetails.accountId and the V1 flat stripeAccountID are empty/absent)
 */
export const ACCOUNT_SESSION_CLIENT_SECRET_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ client_secret: z.string() }).strict());

/**
 * site functions/onboarding/account_session/account_session.py:211-217 — Trigger: ANY
 * exception out of the try at :151 — a bare `except Exception as e`. There are NO
 * stripe.error.* specific except clauses anywhere in this file. So
 * stripe.error.InvalidRequestError, AuthenticationError, APIConnectionError,
 * RateLimitError, PermissionError, and non-Stripe bugs (AttributeError, TypeError) ALL
 * collapse to this single 500 with the exception's message in the body. — BUG-FOR-BUG:
 * TWO pinned issues. (1) EVERY Stripe failure mode is flattened to 500 — a 400-class
 * Stripe InvalidRequestError (e.g. bad account id) is reported to the caller as a server
 * error. (2) `str(e)` is an unvalidated provider string reaching the response body
 * verbatim: Stripe error messages routinely embed the account id (acct_...), the request
 * id (req_...), and API-path detail. This is a provider-detail leak to any
 * dashboard-authenticated caller, and it makes the response body UNPINNABLE to a literal
 * — the contract must model it as an arbitrary string. Do NOT narrow. —
 * account_session.py:216 `json.dumps({"error": str(e)})` — UNVALIDATED provider/runtime
 * string, arbitrary text. Reached by a bare `except Exception` at :211 (zero
 * stripe.error.* clauses in the file), so every Stripe failure class AND non-Stripe bugs
 * collapse here. Unpinnable to any literal or regex.
 */
export const ACCOUNT_SESSION_STRIPE_ERROR_500_LEAKS_PROVIDER_STRING = variant(500, H_POST_OPTIONS_UC_JSON, z.object({ error: z.string() }).strict());


// ===== functions/onboarding/ein_lookup/ein_lookup.py =====================
/** Response helper: null if none — all returns are inline dict literals. */
/**
 * NOTES: 15 response variants. NO auth check in the handler at all — there is no
 * _get_clerk_org_id and no sub check; auth is entirely the gateway authorizer's (docstring
 * :10 'Behind dualAuth'), so this handler produces NO 401/403. A not-found is always a 200
 * with found:false, never a 404 — three distinct miss literals. THE FINDING THAT MATTERS
 * MOST: the 502 body's `detail` is str(requests.HTTPError), which embeds the full upstream
 * URL, and that URL carries the Every.org API key as the `apiKey` query parameter (:122,
 * :137) — a provokable secret leak to any authenticated caller. Pin the shape, flag the
 * defect. No writes of any kind (no DynamoDB, no EventBridge, no EMF) — the only effects
 * are one SSM read and outbound HTTPS to Every.org. Input coercion is absent: a non-string
 * query/orgName/ein raises AttributeError into a gateway 500.
 */

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:87 — Trigger: method == "OPTIONS" —
 * BUG-FOR-BUG: no Content-Type — ein_lookup.py:87 returns body: "".
 */
export const EIN_LOOKUP_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC);

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:92 — Trigger: json.loads raises
 * json.JSONDecodeError — ein_lookup.py:95. Note the trailing word 'body' — differs from
 * create_org's and verify_script's 'Invalid JSON'.
 */
export const EIN_LOOKUP_INVALID_JSON_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:103 — Trigger: all three of query /
 * orgName / ein are empty after strip (ein additionally has "-" removed) —
 * ein_lookup.py:106.
 */
export const EIN_LOOKUP_NO_INPUT_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Provide query, orgName, or ein') }).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:111 — Trigger: _get_api_key()
 * returns None — EVERY_ORG_API_KEY env is blank AND (EVERY_ORG_API_KEY_PATH unset OR the
 * SSM get_parameter raised, caught at :46) — BUG-FOR-BUG: An SSM outage produces the
 * same 503 as an unconfigured deploy. — ein_lookup.py:114. An SSM outage (caught at
 * ein_lookup.py:46) yields the same body as an unconfigured deploy.
 */
export const EIN_LOOKUP_NOT_CONFIGURED_503 = variant(503, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('EIN lookup not configured') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). The `query` path and
 * the legacy `orgName` path converge on the same no-results 200 body. Which INPUT key was
 * used is not recoverable from the response. Collapsed sites:
 * functions/onboarding/ein_lookup/ein_lookup.py:160 (body.query non-empty, the EIN branch
 * produced nothing, and _search_by_term returned an empty list);
 * functions/onboarding/ein_lookup/ein_lookup.py:188 (body.query and body.ein both empty,
 * body.orgName non-empty, _search_by_term(org_name, take=6) returned [])
 */
export const EIN_LOOKUP_NO_RESULTS_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ found: z.literal(false), message: z.literal('No nonprofits found') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (3 code paths, one wire shape). Three code paths emit a
 * `found:true` 200 carrying `results` plus the first result spread at top level, and they
 * are NOT separable on the wire. PROVED from captured envelopes: the 9-digit-query EIN
 * path emits results length 1 (`[out]`, ein_lookup.py:153-156) and the search path emits
 * 1..MAX_RESULTS=6 (:165-169, :193-197) — a 1-result search is therefore byte-identical to
 * an EIN hit, so which branch ran is unrecoverable from the response. Modelling them
 * separately made every real EIN hit parse AMBIGUOUS. The legacy `ein`-only path is
 * DIFFERENT and stays separate: it emits NO `results` key at all (:182-185). Collapsed
 * sites: functions/onboarding/ein_lookup/ein_lookup.py:166 (body.query non-empty and
 * _search_by_term(query, take=6) returned >=1 result);
 * functions/onboarding/ein_lookup/ein_lookup.py:153 (body.query is non-empty AND its
 * digit-only reduction is exactly 9 chars AND _lookup_by_ein returned a non-None mapped
 * org); functions/onboarding/ein_lookup/ein_lookup.py:194 (body.query and body.ein empty,
 * body.orgName non-empty, search returned >=1)
 */
export const EIN_LOOKUP_SEARCH_HIT_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ found: z.literal(true), results: z.array(EveryOrgMappedOrg).min(1).max(6) }).merge(EveryOrgMappedOrg).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:176 — Trigger: body.query empty,
 * body.ein non-empty, _lookup_by_ein returned None (upstream 404 or unmappable payload)
 * — ein_lookup.py:178. SINGULAR 'nonprofit' — a distinct literal from the other two miss
 * messages.
 */
export const EIN_LOOKUP_LEGACY_EIN_MISS_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ found: z.literal(false), message: z.literal('No nonprofit found for this EIN') }).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:180 — Trigger: body.query empty,
 * body.ein non-empty, _lookup_by_ein returned a mapped org — BUG-FOR-BUG: Shape
 * inconsistency with the other hit variants — `results` is present on 3 of 4 hit shapes
 * and absent here. A schema must make `results` optional. — ein_lookup.py:183: {"found":
 * True, **out} — NO `results` key. This is the only 200 hit shape lacking it, so
 * `results` cannot be a required field of a shared hit schema.
 */
export const EIN_LOOKUP_LEGACY_EIN_HIT_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ found: z.literal(true) }).merge(EveryOrgMappedOrg).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:200 — Trigger:
 * requests.exceptions.Timeout from either Every.org call (TIMEOUT = 5s, :24) —
 * ein_lookup.py:203. TIMEOUT = 5s (ein_lookup.py:24). 504 here vs verify_script's 502
 * for the same condition.
 */
export const EIN_LOOKUP_TIMEOUT_504 = variant(504, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('EIN lookup timed out') }).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:207 — Trigger:
 * requests.exceptions.HTTPError whose e.response is truthy AND e.response.status_code ==
 * 404 (from the SEARCH call — the EIN call handles 404 internally at :139) —
 * BUG-FOR-BUG: Three different literals mean 'nothing found': "No nonprofits found", "No
 * nonprofit found for this EIN", "Not found". All must be pinned separately. —
 * ein_lookup.py:210. A THIRD distinct miss literal.
 */
export const EIN_LOOKUP_UPSTREAM_404_AS_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ found: z.literal(false), message: z.literal('Not found') }).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:212 — Trigger:
 * requests.exceptions.HTTPError that is not a 404-with-response — BUG-FOR-BUG: CRITICAL
 * FINDING — str(requests.HTTPError) embeds the full request URL, and both Every.org URLs
 * put the API key in the query string (`?apiKey={api_key}`). This response therefore
 * LEAKS THE EVERY.ORG API KEY to any authenticated caller who can provoke a non-404
 * upstream HTTP error. Pin the shape bug-for-bug in the contract, but flag it as a live
 * secret-exposure defect. — UNVALIDATED (rule 4) AND A LIVE SECRET LEAK.
 * ein_lookup.py:215 sets detail = str(e) on a requests.HTTPError; str() of that embeds
 * the full request URL, and BOTH Every.org URLs carry the API key as a query param
 * (ein_lookup.py:122, :137: '?apiKey={api_key}'). So this field can contain the
 * Every.org API key and must stay z.string() — pin the shape bug-for-bug, but flag the
 * defect.
 */
export const EIN_LOOKUP_UPSTREAM_HTTP_ERROR_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('EIN lookup failed'), detail: z.string() }).strict());

/**
 * site functions/onboarding/ein_lookup/ein_lookup.py:219 — Trigger: any other Exception
 * inside the try at :146 — includes requests.exceptions.ConnectionError/SSLError (NOT
 * separately handled here, unlike verify_script), a JSONDecodeError from resp.json(),
 * etc. — BUG-FOR-BUG: HANDLER-produced 500 with CORS. — ein_lookup.py:222. SAME `error`
 * literal as the 502 — the two are distinguished only by status and by the presence of
 * `detail`, so .strict() is what separates them.
 */
export const EIN_LOOKUP_UNEXPECTED_500 = variant(500, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('EIN lookup failed') }).strict());


// ===== functions/onboarding/onboarding_state/onboarding_state.py =========
/**
 * Response helper: null if none — all returns are inline dict literals. NOTE the module
 * defines a LOCAL `_log` at :73 that SHADOWS the `from log_utils import structured_log as
 * _log` import at :38; the local one is identical in shape but uses .isoformat() for ts.
 */
/**
 * NOTES: 12 response variants. PROD 200 body is EXACTLY {"success": true, "updatedFields":
 * string[]} — two keys, confirmed at :277-280; `success` is hardcoded True and never
 * appears as false. THE ONE THING NOT TO MISS: updatedFields is NOT the caller's key set —
 * line :186 injects the derived key "activationPath" into `updates` whenever hasWebsite is
 * supplied, and the response re-reads updates.keys() at :279, so the array can contain a
 * key that is not in ALLOWED_FIELDS and was never sent. ALLOWED_FIELDS has exactly 8
 * members (enumerated above from the set literal at :52-65). CORS here is
 * "Content-Type,Authorization" / "OPTIONS,PATCH" — different from create_org's. No EMF
 * emissions. The GSI query at :208 is OUTSIDE the try, so a DynamoDB read failure is an
 * UNCAUGHT gateway 500, not the handler's own 500. _now_iso here (:121) is a
 * SPACE-separated tz-naive format "%Y-%m-%d %H:%M:%S.%f" — DIFFERENT from create_org's
 * ISO-Z format; two writers stamp updatedAt on the same table in two incompatible formats.
 */

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:129 — Trigger: method
 * == "OPTIONS" — BUG-FOR-BUG: no Content-Type on this variant — onboarding_state.py:129
 * returns body: "".
 */
export const ONBOARDING_STATE_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_OPTIONS_PATCH_UC);

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:132 — Trigger: method
 * != "OPTIONS" and method != "PATCH". In practice unreachable through API Gateway (only
 * PATCH and the implicit OPTIONS are routed) but reachable on direct invoke / a missing
 * requestContext (method defaults to ""). — BUG-FOR-BUG: A malformed event with no
 * requestContext.http.method yields method=="" -> 405, not 500. —
 * onboarding_state.py:135. Unreachable through API Gateway (only PATCH + implicit
 * OPTIONS are routed) but reachable on direct invoke or an event with no
 * requestContext.http.method (defaults to '').
 */
export const ONBOARDING_STATE_METHOD_NOT_ALLOWED_405 = variant(405, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:141 — Trigger:
 * _get_clerk_org_id(event) returns "" — requestContext.authorizer.lambda.orgId
 * absent/empty, or requestContext/authorizer missing (KeyError/TypeError caught at
 * :103). HANDLER-produced 403. — onboarding_state.py:144. Handler-produced 403; same
 * literal as verify_script's.
 */
export const ONBOARDING_STATE_NO_ORG_CONTEXT_403 = variant(403, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('No organization context') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:150 — Trigger:
 * json.loads raises json.JSONDecodeError — BUG-FOR-BUG: A valid non-dict JSON (e.g.
 * `[1,2]`) passes json.loads, then body.items() raises AttributeError -> gateway 500. —
 * onboarding_state.py:154.
 */
export const ONBOARDING_STATE_INVALID_JSON_400 = variant(400, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('Invalid JSON') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:160 — Trigger: `updates
 * = {k:v for k,v in body.items() if k in ALLOWED_FIELDS}` (:158) is empty — no request
 * key intersects ALLOWED_FIELDS. — BUG-FOR-BUG: The message leaks the full allowlist.
 * Also note: the value filter is key-only — an ALLOWED key with a garbage value still
 * counts as 'valid' and reaches DynamoDB unless a type check exists (only
 * driverFlowsCompleted, scriptInstalled, hasWebsite, onboardingSelectedPlan are
 * type-checked). — onboarding_state.py:163 builds this as "No valid fields to update.
 * Allowed: " + ", ".join(sorted(ALLOWED_FIELDS)). ALLOWED_FIELDS is the 8-member set
 * literal at onboarding_state.py:52-65; sorted() is deterministic, so I executed the
 * join and pinned the result as an exact literal rather than a regex.
 */
export const ONBOARDING_STATE_NO_VALID_FIELDS_400 = variant(400, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('No valid fields to update. Allowed: checklistMinimized, driverFlowsCompleted, hasWebsite, lastPublishedFormId, onboardingCompletedSteps, onboardingCurrentStep, onboardingSelectedPlan, scriptInstalled') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:168 — Trigger:
 * "driverFlowsCompleted" in updates and not isinstance(value, dict) —
 * onboarding_state.py:171.
 */
export const ONBOARDING_STATE_DRIVER_FLOWS_NOT_OBJECT_400 = variant(400, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('driverFlowsCompleted must be an object') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:174 — Trigger:
 * "scriptInstalled" in updates and not isinstance(value, bool) —
 * onboarding_state.py:177.
 */
export const ONBOARDING_STATE_SCRIPT_INSTALLED_NOT_BOOL_400 = variant(400, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('scriptInstalled must be a boolean') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:180 — Trigger:
 * "hasWebsite" in updates and not isinstance(value, bool) — onboarding_state.py:183.
 */
export const ONBOARDING_STATE_HAS_WEBSITE_NOT_BOOL_400 = variant(400, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('hasWebsite must be a boolean') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:195 — Trigger:
 * "onboardingSelectedPlan" in updates and its value (uppercased if a str) is not in
 * _ALLOWED_SELECTED_PLAN_VALUES = (None, "STARTER") (:71) — BUG-FOR-BUG: The tuple
 * membership `norm_plan not in (None, "STARTER")` uses == semantics, so a value of
 * `False` or `0` does NOT equal None and IS rejected — correct. But a non-string
 * non-None value like `0` produces this same message even though it isn't a plan name. —
 * onboarding_state.py:199. Note the SINGLE quotes around STARTER inside the message —
 * the Zod literal is double-quoted here to carry them verbatim.
 */
export const ONBOARDING_STATE_PAID_PLAN_REJECTED_400 = variant(400, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal("onboardingSelectedPlan can only be cleared (null or 'STARTER') via this endpoint") }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:211 — Trigger:
 * _find_v2_org_by_clerk_org(clerk_org_id) returned None — either
 * ORGANIZATIONS_TABLE_NAME env is UNSET (:109-110, returns None WITHOUT querying) or the
 * clerkOrganizationId-index GSI query returned zero Items. — BUG-FOR-BUG: FINDING — a
 * MISSING env var (ORGANIZATIONS_TABLE_NAME unset) is indistinguishable from a genuinely
 * absent org: both yield 404 "Organization not found". A misconfigured deploy 404s every
 * request instead of 5xx-ing. — onboarding_state.py:214. Ambiguous by construction: an
 * UNSET ORGANIZATIONS_TABLE_NAME returns None from _find_v2_org_by_clerk_org without
 * querying (onboarding_state.py:109-110), producing the same 404 as a genuinely absent
 * org.
 */
export const ONBOARDING_STATE_ORG_NOT_FOUND_404 = variant(404, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:268 — Trigger: any
 * Exception from the update_item block (:222-261) — BUG-FOR-BUG: HANDLER-produced 500
 * (has CORS), distinct from gateway 500. — onboarding_state.py:271. Handler-produced 500
 * with CORS. Note the GSI READ at onboarding_state.py:208 is OUTSIDE the try, so a read
 * failure is an uncaught gateway 500, not this.
 */
export const ONBOARDING_STATE_V2_WRITE_FAILED_500 = variant(500, H_OPTIONS_PATCH_UC_JSON, z.object({ error: z.literal('Failed to update organization record') }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:274 — Trigger: org
 * resolved and the V2 update_item did not raise. NOTE: if ORGANIZATIONS_TABLE_NAME is
 * unset the write block is SKIPPED by the `if ORGANIZATIONS_TABLE_NAME:` guard at :222 —
 * but that state already returned 404 at :211, so this is unreachable in practice. —
 * BUG-FOR-BUG: FINDING — `updatedFields` is `list(updates.keys())` re-evaluated at :279,
 * NOT the `updated_fields` snapshot taken at :217. Between those two points, :186
 * INJECTS the key "activationPath" into `updates` whenever hasWebsite was supplied. So a
 * request sending {"hasWebsite": true} gets back updatedFields == ["hasWebsite",
 * "activationPath"] — "activationPath" is NOT a member of ALLOWED_FIELDS and was never
 * sent by the caller. The contract must model updatedFields as a superset of the request
 * keys. Ordering is Python dict insertion order: request-key order first, then
 * "activationPath" appended last. Secondary: the log line at :261 uses the STALE
 * `updated_fields` (without activationPath), so log and response disagree. — PROD shape,
 * pinned from the historical source — two keys only; `success` is hardcoded True and never
 * appears as false anywhere in the file. This base-success variant deliberately excludes
 * onboardingCurrentStep; the current source has a second, disjoint success variant below when
 * a step was updated. CAVEAT for the contract author: updatedFields is `list(updates.keys())`
 * re-evaluated at :279, not the `updated_fields` snapshot from :217 — line :186 injects
 * the derived key 'activationPath' whenever hasWebsite was supplied, so the array can
 * contain a key that is NOT in ALLOWED_FIELDS and was never sent by the caller.
 * z.array(z.string()) admits that; do not tighten it to an enum of ALLOWED_FIELDS.
 */
export const ONBOARDING_STATE_SUCCESS_200 = variant(200, H_OPTIONS_PATCH_UC_JSON, z.object({ success: z.literal(true), updatedFields: z.array(z.string()) }).strict());

/**
 * site functions/onboarding/onboarding_state/onboarding_state.py:304-310 — Trigger: the
 * successful update includes onboardingCurrentStep. The response carries the value actually
 * written, not necessarily the requested value: when the request asks for
 * awaiting_stripe_charges or share and V2 stripeDetails.chargesEnabled is true, :248-267
 * level-triggers a rewrite to checklist before persistence. The required field makes this
 * disjoint from ONBOARDING_STATE_SUCCESS_200; a non-step update never implies a step write.
 */
export const ONBOARDING_STATE_STEP_UPDATED_SUCCESS_200 = variant(200, H_OPTIONS_PATCH_UC_JSON, z.object({ success: z.literal(true), updatedFields: z.array(z.string()), onboardingCurrentStep: z.string() }).strict());


// ===== functions/onboarding/verify_script/verify_script.py ===============
/**
 * Response helper: functions/onboarding/verify_script/verify_script.py:155
 * _error_response(status_code, error, detail=None) -> {"statusCode": status_code,
 * "headers": {**CORS_HEADERS, "Content-Type": "application/json"}, "body":
 * json.dumps({"error": error} + {"detail": detail} only when detail is TRUTHY)}; and
 * functions/onboarding/verify_script/verify_script.py:167 _ok_response(body_dict) ->
 * {"statusCode": 200, "headers": {**CORS_HEADERS, "Content-Type": "application/json"},
 * "body": json.dumps(body_dict)}. This is the ONLY handler in the group with real response
 * helpers. NOTE _error_response uses `if detail:` (truthiness), so detail="" or detail=0
 * would be omitted — all call sites pass non-empty literals.
 */
/**
 * NOTES: 13 response variants — the only handler in this group with real response helpers
 * (_error_response :155, _ok_response :167), so its header map is uniform by construction
 * except the OPTIONS 204 which bypasses both. THE THING NOT TO MISS: the two headless 200
 * variants forward ANOTHER LAMBDA'S RESPONSE BODY OPAQUELY (:290, :308) with only `method`
 * injected — the executable contract can guarantee nothing about that body beyond `method:
 * "headless"`, and a headless-side error surfaces as a 200 because payload["statusCode"]
 * is never read (:135-136). Second: this handler's timeout is a 502, while ein_lookup and
 * website_enrich use 504 for the same condition. Third: validate_url's machine-readable
 * `detail` ("ssrf_blocked"/"dns_error") is DISCARDED at :224 even though every other error
 * path here carries one. No DynamoDB, no EventBridge, no EMF; effects are one outbound
 * HTTP GET to a user-supplied URL and up to one lambda:Invoke that forwards the ENTIRE
 * original event (auth context and all) to the headless Lambda. The headlessVerifyFunction
 * has its own error alarm at stacks/ApiStack.ts:1844.
 */

/**
 * site functions/onboarding/verify_script/verify_script.py:203 — Trigger: method ==
 * "OPTIONS" — BUG-FOR-BUG: no Content-Type — verify_script.py:203 returns body: "" — the
 * only return in the file that bypasses both response helpers.
 */
export const VERIFY_SCRIPT_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC);

/**
 * site functions/onboarding/verify_script/verify_script.py:206 — Trigger: method not in
 * ("OPTIONS","POST") — includes the empty-string default when requestContext.http.method
 * is absent — verify_script.py:206 via _error_response(405, ...) with no detail arg, so
 * the `detail` key is absent.
 */
export const VERIFY_SCRIPT_METHOD_NOT_ALLOWED_405 = variant(405, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:211 — Trigger:
 * _get_clerk_org_id returns "" — requestContext.authorizer.lambda.orgId missing
 * (KeyError/TypeError caught at :151). HANDLER-produced 403, same literal as
 * onboarding_state's. — BUG-FOR-BUG: NOTE _get_clerk_org_id here uses direct
 * subscripting event["requestContext"]["authorizer"]["lambda"]["orgId"] (:150) vs
 * onboarding_state's .get() chain — but both catch (KeyError, TypeError). An orgId
 * present but empty-string also yields 403 in both. — verify_script.py:211.
 * Handler-produced 403.
 */
export const VERIFY_SCRIPT_NO_ORG_CONTEXT_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('No organization context') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:217 — Trigger: json.loads
 * raises json.JSONDecodeError — verify_script.py:217.
 */
export const VERIFY_SCRIPT_INVALID_JSON_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Invalid JSON') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:224 — Trigger:
 * validate_url(website_url) returned an error dict — BUG-FOR-BUG: FINDING — validate_url
 * provides a machine-readable `detail` discriminator ("ssrf_blocked", "dns_error") and
 * this handler throws it away, even though _error_response supports a detail arg and
 * every OTHER 502 in this file uses one. Callers cannot distinguish an SSRF block from a
 * DNS failure. — verify_script.py:224 passes only validation_error['error'] as the
 * second positional arg and no third, so validate_url's machine-readable `detail`
 * ('ssrf_blocked' / 'dns_error') is DISCARDED even though _error_response supports it
 * and every 502 in this file uses one.
 */
export const VERIFY_SCRIPT_URL_VALIDATION_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.enum(URL_VALIDATION_ERROR) }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:271 — Trigger:
 * requests.HTTPError whose status_code is NOT in (403, 406, 503) — those three route to
 * the headless fallback instead (:265). status_code is 0 when e.response is None (:264).
 * — verify_script.py:271. Reached only when the upstream status is NOT in (403, 406,
 * 503) — those route to the headless fallback instead.
 */
export const VERIFY_SCRIPT_HTTP_ERROR_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Could not reach website'), detail: z.literal('http_error') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:274 — Trigger:
 * requests.exceptions.Timeout (HTTP_TIMEOUT = 10s, :45) — BUG-FOR-BUG: 502, not 504 —
 * compare ein_lookup and website_enrich which both use 504 for a timeout. Inconsistent
 * across the group; pin as-is. — verify_script.py:274. 502, not 504 — ein_lookup and
 * website_enrich both use 504 for a timeout. Inconsistent across the group; pinned
 * as-is.
 */
export const VERIFY_SCRIPT_TIMEOUT_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Could not reach website'), detail: z.literal('timeout') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:277 — Trigger:
 * requests.exceptions.SSLError — verify_script.py:277.
 */
export const VERIFY_SCRIPT_SSL_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Could not reach website'), detail: z.literal('ssl_error') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:280 — Trigger:
 * requests.exceptions.ConnectionError. NOTE SSLError is a SUBCLASS of ConnectionError in
 * requests, but the SSLError except clause at :275 precedes this one, so ordering is
 * correct. — verify_script.py:280. requests' SSLError is a subclass of ConnectionError,
 * but the SSLError clause at :275 precedes this one, so the ordering is correct and both
 * are reachable.
 */
export const VERIFY_SCRIPT_CONNECTION_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Could not reach website'), detail: z.literal('connection_error') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:283 — Trigger: any other
 * Exception during the fetch/read block (:230-261) — includes a UnicodeDecodeError, a
 * chunked-read failure, etc. — verify_script.py:283.
 */
export const VERIFY_SCRIPT_UNKNOWN_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Could not reach website'), detail: z.literal('unknown_error') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:292 — Trigger: needs_headless
 * is True (either an upstream 403/406/503 at :265, or CHALLENGE_PATTERNS matched the
 * first 3000 chars at :258) AND _try_headless_fallback returned None
 * (HEADLESS_VERIFY_FUNCTION env unset, or the lambda:Invoke / payload parse raised) —
 * BUG-FOR-BUG: A missing HEADLESS_VERIFY_FUNCTION config and a genuine headless failure
 * are indistinguishable — both are detail:"waf_blocked". — verify_script.py:292. A
 * missing HEADLESS_VERIFY_FUNCTION env and a genuine headless failure are
 * indistinguishable — both produce this body.
 */
export const VERIFY_SCRIPT_WAF_BLOCKED_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Could not reach website'), detail: z.literal('waf_blocked') }).strict());

/**
 * site functions/onboarding/verify_script/verify_script.py:290 and
 * functions/onboarding/verify_script/verify_script.py:308 — Trigger: TWO distinct
 * triggers reach the same shape: (a) :287 — needs_headless (WAF 403/406/503 or challenge
 * page) and the fallback returned a dict; (b) :305 — the static fetch SUCCEEDED,
 * `installed` was False, and _looks_like_client_rendered_shell(html) returned True (>=2
 * of 3 app-shell signals, :193), and the fallback returned a dict. — BUG-FOR-BUG:
 * FINDING (brief rule 4) — the response body of a DIFFERENT Lambda is forwarded to the
 * client with no schema check. If the headless Lambda returns {"error": ...} with a
 * non-200 statusCode, this handler still wraps it in a 200 (it reads only
 * payload["body"], never payload["statusCode"] — :135-136). A headless-side 500
 * therefore surfaces to the caller as a 200. — OPAQUE PASSTHROUGH OF ANOTHER LAMBDA'S
 * BODY — only `method` can be guaranteed. verify_script.py:289/:307 mutate the dict
 * parsed from the headless-verify Lambda's own response body (verify_script.py:136) by
 * setting fallback['method'] = 'headless', then re-serialize it wholesale. This handler
 * never validates, filters or reshapes it, so its key set is NOT closed and no other key
 * may be asserted — in particular do NOT assume `installed` or `detectedAt` are present
 * just because the fetch path has them. Expressed as z.record(z.unknown()) plus a
 * .refine on the one guaranteed key, rather than an intersection with a z.object: a
 * .strict() object would reject the unknown keys this variant exists to admit, and
 * .passthrough() is forbidden — so no z.object appears here at all. Two distinct
 * triggers share this shape (verify_script.py:290 WAF path, verify_script.py:308
 * app-shell path). Separately: payload['statusCode'] is never read
 * (verify_script.py:135-136), so a headless-side 500 still surfaces to the caller as a
 * 200 carrying whatever that error body was.
 */
export const VERIFY_SCRIPT_HEADLESS_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.record(z.unknown()).refine((b) => b.method === 'headless', { message: "method must be 'headless'" }));

/**
 * site functions/onboarding/verify_script/verify_script.py:315 — Trigger: the static
 * fetch succeeded and either installed is True, or installed is False and (the page is
 * not an app shell OR the headless fallback returned None) — BUG-FOR-BUG: detectedAt
 * uses .isoformat() ("+00:00" suffix) while create_org's _now_iso uses
 * "%Y-%m-%dT%H:%M:%S.%fZ" ("Z" suffix) — two timestamp formats in the same API surface.
 * — verify_script.py:311-313. `detectedAt` is present ONLY when installed is true; when
 * false the key is ABSENT, not null — hence .optional(), not .nullable(). Its value is
 * datetime.now(timezone.utc).isoformat() (verify_script.py:296), which renders a
 * '+00:00' offset and NOT a trailing 'Z' — the regex mirrors that, including the
 * microsecond part which isoformat() omits when microsecond == 0. Contrast create_org's
 * _now_iso which uses '%Y-%m-%dT%H:%M:%S.%fZ'.
 */
export const VERIFY_SCRIPT_FETCH_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ installed: z.boolean(), method: z.literal('fetch'), detectedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?\+00:00$/).optional() }).strict());


// ===== functions/onboarding/website_enrich/website_enrich.py =============
/** Response helper: null if none — all returns are inline dict literals. */
/**
 * NOTES: 8 response variants. No auth check in the handler (docstring :20 'Behind
 * dualAuth') — produces NO 401/403. THE THING NOT TO MISS: the 200 body's dict literal at
 * :735 contains the key `description` TWICE ({"ok":True, **meta, "brandColor":...,
 * "description": final_description}) — meta already carries `description`, and the
 * explicit trailing key wins. The emitted key set is exactly 7: ok, ogImage,
 * ogDescription, description, themeColor, favicon, brandColor. brandColor is the only
 * nullable; the rest default to "". A 200 is returned even when all three enrichment
 * layers produce nothing. No DynamoDB writes, no EventBridge, no EMF — effects are 3 SSM
 * reads plus outbound HTTP to Brandfetch, the caller's site, an UNVALIDATED favicon URL
 * taken from that site's HTML (an SSRF gap validate_url does not cover), and up to 2
 * billed OpenRouter LLM calls. The LAYER3_ALL_MODELS_FAILED print at :601 is alarmed by
 * logs.MetricFilter "websiteEnrichLayer3FailedFilter" (stacks/ApiStack.ts:1894) — a
 * log-string contract, not an EMF metric. validate_url is called OUTSIDE the handler's
 * try, so a socket.herror/UnicodeError from gethostbyname is an uncaught gateway 500.
 */

/**
 * site functions/onboarding/website_enrich/website_enrich.py:607 — Trigger: method ==
 * "OPTIONS" — BUG-FOR-BUG: no Content-Type — website_enrich.py:607 returns body: "".
 */
export const WEBSITE_ENRICH_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC);

/**
 * site functions/onboarding/website_enrich/website_enrich.py:612 — Trigger: json.loads
 * raises json.JSONDecodeError — website_enrich.py:615.
 */
export const WEBSITE_ENRICH_INVALID_JSON_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/onboarding/website_enrich/website_enrich.py:623 — Trigger:
 * validate_url(url) returned a non-None error dict — BUG-FOR-BUG: The f"Invalid URL:
 * {e}" branch at url_validation.py:78 catches a ValueError from
 * ipaddress.ip_address(resolved_ip) and interpolates the raw exception text —
 * unvalidated passthrough. — website_enrich.py:626 forwards validation_error['error']
 * only — validate_url's `detail` key ('ssrf_blocked' / 'dns_error') is DROPPED.
 * URL_VALIDATION_ERROR is a union because one of its six members is an f-string.
 */
export const WEBSITE_ENRICH_URL_VALIDATION_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.enum(URL_VALIDATION_ERROR) }).strict());

/**
 * site functions/onboarding/website_enrich/website_enrich.py:732 — Trigger: URL
 * validated and the enrichment try-block completed without a
 * Timeout/SSLError/RequestException/other Exception. NOTE a 200 is returned even when
 * EVERY layer failed — all-empty strings + null brandColor is a valid 200. —
 * BUG-FOR-BUG: The duplicate `description` key in the dict literal is a real latent bug:
 * a future edit reordering those keys silently changes which description ships. Also
 * every string value is UNVALIDATED third-party content (Brandfetch, the scraped site's
 * meta tags, or LLM output) — brief rule 4. — EXACTLY 7 KEYS, not 8.
 * website_enrich.py:735 is json.dumps({"ok": True, **meta, "brandColor": brand_color,
 * "description": final_description}) — `meta` (website_enrich.py:631) already carries a
 * `description` key, so the literal names it twice and Python dict-literal semantics
 * make the LATER (trailing) one win. The emitted value is always final_description.
 * brandColor is the only nullable: it is initialized to Python None at
 * website_enrich.py:632 and emitted as JSON null when neither Brandfetch nor favicon
 * extraction produced a colour; the five string fields default to ''. Every string value
 * is unvalidated third-party content (Brandfetch, scraped meta tags, or LLM output) but
 * IS guaranteed str-typed by the '' defaults and the .strip()/slice paths that produce
 * them.
 */
export const WEBSITE_ENRICH_SUCCESS_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ ok: z.literal(true), ogImage: z.string(), ogDescription: z.string(), description: z.string(), themeColor: z.string(), favicon: z.string(), brandColor: z.string().nullable() }).strict());

/**
 * site functions/onboarding/website_enrich/website_enrich.py:738 — Trigger:
 * requests.exceptions.Timeout escapes the enrichment block. The inner Layer-2 handler at
 * :707-713 RE-RAISES Timeout and SSLError specifically (:710-711) so they reach here;
 * all other Layer-2 exceptions are swallowed and enrichment continues. — BUG-FOR-BUG:
 * The Brandfetch call (:310, timeout=3) and the OpenRouter calls (:458, timeout=5) each
 * catch their own exceptions internally, so only the Layer-2 HTML fetch (TIMEOUT=5,
 * :662) and the favicon fetch can produce this 504 — and the favicon fetch's exceptions
 * are caught at :294. So in practice only the Layer-2 HTML fetch reaches it. —
 * website_enrich.py:741. Reachable because the Layer-2 handler re-raises
 * Timeout/SSLError specifically (website_enrich.py:710-711); all other Layer-2
 * exceptions are swallowed and enrichment continues.
 */
export const WEBSITE_ENRICH_TIMEOUT_504 = variant(504, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Request timed out') }).strict());

/**
 * site functions/onboarding/website_enrich/website_enrich.py:745 — Trigger:
 * requests.exceptions.SSLError re-raised from the Layer-2 block (:710-711) —
 * website_enrich.py:748.
 */
export const WEBSITE_ENRICH_SSL_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('SSL error') }).strict());

/**
 * site functions/onboarding/website_enrich/website_enrich.py:752 — Trigger: any other
 * requests.exceptions.RequestException escaping the enrichment block — BUG-FOR-BUG:
 * LIKELY UNREACHABLE — the Layer-2 except at :707 swallows every
 * non-Timeout/non-SSLError exception (`pass` at :713), and the other HTTP callers catch
 * internally. Pin it, but note it may be dead code. — website_enrich.py:755. LIKELY DEAD
 * CODE — the Layer-2 except at website_enrich.py:707 swallows every
 * non-Timeout/non-SSLError exception with `pass` (:713), and
 * Brandfetch/OpenRouter/favicon callers all catch internally. Pinned anyway.
 */
export const WEBSITE_ENRICH_REQUEST_EXCEPTION_502 = variant(502, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Failed to fetch URL') }).strict());

/**
 * site functions/onboarding/website_enrich/website_enrich.py:759 — Trigger: any other
 * Exception in the enrichment block — e.g. a BeautifulSoup parse failure escaping, or an
 * AttributeError from :692's html_meta.get('ogImage')[:50] chain — BUG-FOR-BUG:
 * HANDLER-produced 500 with CORS. — website_enrich.py:762.
 */
export const WEBSITE_ENRICH_UNEXPECTED_500 = variant(500, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Enrichment failed') }).strict());


// ===== functions/org_contact_tags/org_contact_tags.py ====================
/**
 * Response helper: null — no response helper; every return site is an inline dict literal
 * using CORS_HEADERS.
 */
/**
 * NOTES: Five things. (1) GET IS COMPLETELY UNGATED beyond 'is a Clerk org member' — no
 * permission check, deliberate per the module comment. (2) All three 500 bodies LEAK
 * str(e) into the response ("Failed to get/update/reset contact tags: <raw exception>"),
 * unlike the custom-fields sibling. (3) A non-dict JSON PUT body (e.g. `[]`) produces a
 * 500, not a 400, because body.get() raises AttributeError outside the JSONDecodeError
 * branch — real divergence from the sibling handler. (4) The PUT write is UNCONDITIONAL
 * (attribute_exists on PK only, no version counter, no retry loop) so concurrent PUTs
 * silently lose updates; ConditionalCheckFailedException maps to 500. (5) The marketing
 * webhook POST (marketing_tags_notify) is a real outbound effect carrying an HMAC
 * signature and org id, invisible to any boto-write-verb grep.
 */

/**
 * site functions/org_contact_tags/org_contact_tags.py:575 — Trigger:
 * requestContext.http.method == 'OPTIONS' — BUG-FOR-BUG: Dead in production —
 * ApiStack.ts:1226-1234 registers only GET/PUT/DELETE; no OPTIONS route exists. — body
 * is the Python literal '' (org_contact_tags.py:575). No OPTIONS route registered —
 * handler-reachable, not API-reachable.
 */
export const TAGS_OPTIONS_PREFLIGHT_204 = emptyBodyVariant(204, H_GET_PUT_DELETE_OPTIONS_UC);

/**
 * site functions/org_contact_tags/org_contact_tags.py:589 — Trigger: method not in
 * {OPTIONS,GET,PUT,DELETE}; also method==None when requestContext.http missing —
 * BUG-FOR-BUG: Unreachable via the registered routes.
 */
export const TAGS_METHOD_NOT_ALLOWED_405 = variant(405, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:383 — Trigger: get_org_id_and_role
 * returned falsy org_id — provider != 'clerk' (returns (None,None)) or orgId is ''.
 * HANDLER-produced. — BUG-FOR-BUG: GET HAS NO PERMISSION GATE AT ALL — any authenticated
 * Clerk org member of ANY role can read the org's full tag dictionary with usage counts.
 * Deliberate per the module comment at :74-77 ('the permission-free GET'). Contract must
 * NOT model a permission gate here. — the ONLY gate on GET — there is no permission
 * check, so any Clerk org member of any role reads the full dictionary
 */
export const TAGS_GET_CLERK_AUTH_REQUIRED_403 = variant(403, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:392 — Trigger:
 * read_org_by_clerk_org_id returned None (index query had no Items). NOTE: GET checks
 * only `not org` — it does NOT additionally require org.get('organizationId'), unlike
 * PUT (:456) and DELETE (:520). — BUG-FOR-BUG: Asymmetric guard vs PUT/DELETE. An org
 * item present in the GSI but missing organizationId yields 200 on GET and 404 on
 * PUT/DELETE. — GET checks only `not org`; PUT/DELETE additionally require a truthy
 * organizationId — asymmetric guard
 */
export const TAGS_GET_ORG_NOT_FOUND_404 = variant(404, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:400 — Trigger: org found —
 * BUG-FOR-BUG: `isCustomized` is `'contact_tags' in org` (:405) — ATTRIBUTE PRESENCE,
 * not non-emptiness. An org whose contact_tags is a stored empty list reports
 * isCustomized:true with tags:[]. normalize_dictionary (:313) launders: legacy plain
 * strings -> {value, usage_count:0}; Decimal counts -> int; negative or non-int counts
 * -> 0; blank/whitespace values dropped; malformed entries skipped. NOTE it does NOT
 * dedupe — unlike the custom-fields sibling — so duplicate `value` entries CAN be
 * returned if stored. — isCustomized is `'contact_tags' in org` — ATTRIBUTE PRESENCE,
 * not non-emptiness, so a stored empty list reports true with tags:[].
 * normalize_dictionary does NOT dedupe (unlike the custom-fields sibling), so duplicate
 * `value` entries can be returned.
 */
export const TAGS_GET_OK_200 = variant(200, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ tags: z.array(ContactTagEntry), isCustomized: z.boolean(), maxTagLength: z.literal(50), maxTagsPerOrg: z.literal(100) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:413 — Trigger: any Exception from
 * the DynamoDB query / normalize / json.dumps — BUG-FOR-BUG: INTERNAL DETAIL LEAK:
 * str(e) reaches the client body (e.g. a botocore error naming the table ARN and account
 * id). Pin as-is, flag it. Contrast with the custom-fields sibling, which returns a
 * generic string. — f-string source: f'Failed to get contact tags: {str(e)}'. LEAKS the
 * raw exception string (can name the table ARN / account id). [\s\S]* because an
 * exception message may contain newlines.
 */
export const TAGS_GET_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to get contact tags: [\s\S]*$/) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:278 — Trigger: any Exception out
 * of resolve_effective_permissions — SSM failure in _get_clerk, Clerk SDK failure, or
 * PermissionResolutionError. Fails CLOSED. Emits the structured log first.
 */
export const TAGS_MUTATE_PERMISSION_UNAVAILABLE_503 = variant(503, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Contact permissions are temporarily unavailable') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:285 — Trigger:
 * resolve_effective_permissions returned falsy org_id
 */
export const TAGS_MUTATE_CLERK_AUTH_REQUIRED_403 = variant(403, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:292 — Trigger:
 * 'org:contacts:manage_contacts' not in resolved permissions
 */
export const TAGS_MUTATE_PERMISSION_DENIED_403 = variant(403, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Manage contacts permission required') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:441 — Trigger: parsed body has no
 * 'tags' or it is not a list. Deliberate anti-wipe guard (:435-438). — BUG-FOR-BUG: If
 * the parsed body is a NON-DICT JSON value (e.g. `[]` or `"x"` or `3`),
 * `body.get('tags')` raises AttributeError — NOT caught as JSONDecodeError, so it falls
 * to the broad handler and returns a 500 with "Failed to update contact tags: 'list'
 * object has no attribute 'get'" rather than a 400. The custom-fields sibling explicitly
 * guards this (isinstance dict check); this handler does NOT. Real divergence — pin the
 * 500. — single quotes literal. Deliberate anti-wipe guard. A NON-DICT JSON body (e.g.
 * []) does NOT reach here — body.get() raises AttributeError outside the JSONDecodeError
 * branch and yields the 500 instead.
 */
export const TAGS_PUT_TAGS_LIST_REQUIRED_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal("Request body must include a 'tags' list") }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:498 — Trigger:
 * json.JSONDecodeError at :434
 */
export const TAGS_PUT_INVALID_JSON_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:449 (message from
 * validate_tag_names :349) — Trigger: UNREACHABLE from the HTTP path — the caller
 * already returned 400 at :441 if raw_tags is not a list. Dead branch retained for
 * direct calls. — BUG-FOR-BUG: Dead code from the route's perspective; a contract that
 * models it as reachable is wrong. — DEAD from the HTTP path — the caller already 400s
 * at :441 if raw_tags is not a list. Retained for direct calls; a contract modelling it
 * as route-reachable is wrong.
 */
export const TAGS_PUT_NOT_ARRAY_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('tags must be an array') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:449 (message from :352) — Trigger:
 * len(raw_tags) > 100 — counted on the RAW list BEFORE dedupe/empty-drop, so 101 entries
 * of which 99 are duplicates still 400s — BUG-FOR-BUG: Quota measured pre-normalization.
 * A caller can therefore be rejected for a list that would normalize to well under 100.
 * — f-string source: f'Cannot have more than {MAX_TAGS_PER_ORG} tags per organization'
 * with MAX_TAGS_PER_ORG=100. Counted on the RAW list BEFORE dedupe/empty-drop.
 */
export const TAGS_PUT_TOO_MANY_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Cannot have more than 100 tags per organization') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:449 (message from :358) — Trigger:
 * any element of tags is not a str
 */
export const TAGS_PUT_NON_STRING_ENTRY_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Each tag must be a string') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:449 (message from :363) — Trigger:
 * len(trimmed) > 50 — BUG-FOR-BUG: Caller input reflected (truncated to 20 chars).
 * Truncation happens BEFORE any character validation, so the reflected 20 chars can
 * contain ANY characters including <, >, quotes, control chars — the TAG_PATTERN check
 * at :364 runs AFTER this one. FINDING: unvalidated reflection. — f-string source: f'Tag
 * "{trimmed[:20]}..." exceeds {MAX_TAG_LENGTH} characters'. The literal '...' is ALWAYS
 * appended (part of the format string, not a conditional ellipsis). FINDING: the
 * reflected 20 chars are UNFILTERED — TAG_PATTERN runs at :364, AFTER this check at :363
 * — so they can be any characters incl. quotes and control chars.
 */
export const TAGS_PUT_TAG_TOO_LONG_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Tag "[\s\S]{0,20}\.\.\." exceeds 50 characters$/) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:449 (message from :365) — Trigger:
 * TAG_PATTERN /^[A-Za-z0-9 &_-]+$/ did not match the trimmed tag (ASCII-only — non-ASCII
 * letters are REJECTED here, unlike the custom-fields LABEL_PATTERN which is
 * \w+re.UNICODE) — BUG-FOR-BUG: FINDING: the FULL untruncated caller-supplied tag (up to
 * 50 chars) is reflected verbatim into the response body with NO character filtering —
 * this branch fires precisely because the tag failed the filter. Do not narrow this to
 * TAG_PATTERN in the contract. — f-string source (single literal at :365). FINDING: the
 * FULL untruncated tag (up to 50 chars) is reflected verbatim with NO filtering — this
 * branch fires precisely BECAUSE the tag failed TAG_PATTERN. Do not narrow to
 * TAG_PATTERN.
 */
export const TAGS_PUT_TAG_INVALID_CHARS_400 = variant(400, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Tag "[\s\S]*" contains invalid characters\. Allowed: letters, numbers, spaces, hyphens, underscores, ampersands$/) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:457 — Trigger: no org item OR the
 * item lacks a truthy 'organizationId'
 */
export const TAGS_PUT_ORG_NOT_FOUND_404 = variant(404, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:488 — Trigger: update_item
 * succeeded (ConditionExpression held) — BUG-FOR-BUG: NO optimistic-concurrency guard:
 * this is an UNCONDITIONAL SET of the whole list (the only condition is attribute_exists
 * on the PK). Two concurrent PUTs silently lose one. The custom-fields sibling added a
 * version counter for exactly this; contact tags never got one. Also NOT
 * idempotency-guarded and NOT retried. — the write is UNCONDITIONAL (attribute_exists on
 * PK only, no version counter, no retry) so concurrent PUTs silently lose updates
 */
export const TAGS_PUT_OK_200 = variant(200, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ message: z.literal('Contact tags updated successfully'), tags: z.array(ContactTagEntry) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:505 — Trigger: any
 * non-JSONDecodeError Exception, INCLUDING (a) ConditionalCheckFailedException when the
 * org row vanished between read and write — surfaces as a 500, not a 404/409; (b) the
 * AttributeError from a non-dict JSON body (see TAGS_PUT_TAGS_LIST_REQUIRED_400
 * bugForBug). — BUG-FOR-BUG: ConditionalCheckFailedException maps to 500. And str(e)
 * leaks. Both pinned. — f-string source: f'Failed to update contact tags: {str(e)}'.
 * LEAKS str(e). Also absorbs two non-obvious cases: ConditionalCheckFailedException (org
 * row vanished → 500, not 404/409) and the AttributeError from a non-dict JSON body.
 */
export const TAGS_PUT_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to update contact tags: [\s\S]*$/) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:521 — Trigger: no org item OR item
 * lacks a truthy 'organizationId'
 */
export const TAGS_DELETE_ORG_NOT_FOUND_404 = variant(404, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:544 — Trigger: update_item REMOVE
 * succeeded — BUG-FOR-BUG: Idempotent-by-accident: REMOVE on an already-absent attribute
 * succeeds, so a second DELETE also returns 200. Message says 'reset', not 'deleted'. —
 * tags is ALWAYS the empty array literal [] at :549, never populated — pinned as an
 * empty tuple, not z.array(ContactTagEntry). Idempotent by accident: REMOVE on an absent
 * attribute succeeds, so a second DELETE also 200s.
 */
export const TAGS_DELETE_OK_200 = variant(200, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ message: z.literal('Contact tag dictionary reset'), tags: z.tuple([]) }).strict());

/**
 * site functions/org_contact_tags/org_contact_tags.py:555 — Trigger: any Exception,
 * including ConditionalCheckFailedException. No JSONDecodeError branch (no body is
 * read). — BUG-FOR-BUG: str(e) leaked; conditional-check failure surfaces as 500. —
 * f-string source: f'Failed to reset contact tags: {str(e)}'. LEAKS str(e). No
 * JSONDecodeError branch (no body read).
 */
export const TAGS_DELETE_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to reset contact tags: [\s\S]*$/) }).strict());


// ===== functions/org_custom_fields/org_custom_fields.py ==================
/**
 * Response helper: null — no central response helper. Every return site builds the dict
 * literal inline. Two partial helpers exist:
 * functions/org_custom_fields/org_custom_fields.py:616 _conflict_response(action) ->
 * {statusCode:409, headers:CORS_HEADERS, body:{error,code}} and :642
 * _quota_body(definitions) -> a body FRAGMENT (no status/headers) merged via
 * body.update(...). Both use CORS_HEADERS.
 */
/**
 * NOTES: Six things a contract author must not miss. (1) NO OPTIONS ROUTE IS REGISTERED
 * (stacks/ApiStack.ts:1236-1247 registers only the four verbs; grep for '"OPTIONS ' in
 * ApiStack.ts returns 0 hits) — the 204 preflight branch is handler-reachable but not
 * API-reachable. (2) The three 500 bodies here are GENERIC (no str(e)); this is the
 * OPPOSITE of both sibling handlers in this group, which DO leak str(e). Do not unify
 * them. (3) `target_key` on PATCH and DELETE is unvalidated caller input reflected
 * verbatim into the 404 message — do not narrow it to KEY_PATTERN. (4) The PATCH success
 * body is asymmetric: no `count`, no quota fields. (5) The two LABEL_CONFLICT 409s (POST
 * vs PATCH) share a message and code but differ in body keys (`conflictingDefinition`
 * present only on POST). (6) An ABSENT `label` returns "label must be a string", not
 * "label is required" — the isinstance check precedes the emptiness check.
 */

/**
 * site functions/org_custom_fields/org_custom_fields.py:1092 — Trigger:
 * requestContext.http.method == 'OPTIONS' — BUG-FOR-BUG: DEAD CODE in production:
 * stacks/ApiStack.ts:1236-1247 registers only GET/POST/PATCH/DELETE /org-custom-fields;
 * there is NO 'OPTIONS ' route anywhere in ApiStack.ts (grep count 0). API Gateway HTTP
 * API answers preflight itself or 404s; this branch is unreachable via the deployed API.
 * Pin it if the contract models the handler; mark it unreachable if the contract models
 * the ROUTE table. — body is the Python literal '' (org_custom_fields.py:1092), not
 * JSON. Branch is handler-reachable but NOT API-reachable: no OPTIONS route is
 * registered in ApiStack.ts.
 */
export const CF_OPTIONS_PREFLIGHT_204 = emptyBodyVariant(204, H_GET_POST_PATCH_DELETE_OPTIONS_UC);

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). org_custom_fields and
 * clerk_users emit the identical 405 body AND share an identical CORS header map, so
 * within the UNROUTABLE registry they are one wire shape. Both are handler-reachable only;
 * no declared route can produce either. Collapsed sites:
 * functions/org_custom_fields/org_custom_fields.py:1107 (method is not
 * OPTIONS/GET/POST/PATCH/DELETE — includes method == None when requestContext.http is
 * missing); functions/clerk_users/clerk_users.py:575-579 (handler() fell through all four
 * method branches. Unreachable via API Gateway (only the 4 methods are routed at
 * stacks/ApiStack.ts:1202-1213).)
 */
export const UNROUTABLE_METHOD_NOT_ALLOWED_405_GET_POST_PATCH_DELETE = variant(405, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:437 — Trigger: ANY exception out
 * of resolve_effective_permissions(event) — SSM get_parameter failure in _get_clerk
 * (CLERK_SECRET_KEY_PATH unset/denied/throttled), any Clerk SDK error (incl. a 429 that
 * exhausts MAX_RETRIES=2), or PermissionResolutionError from _get_caller_membership.
 * Fails CLOSED. Emits a JSON log line first (see effects).
 *
 * Reached by ALL FOUR verbs: require_any_permission is called at GET :657, POST :696,
 * PATCH :893, DELETE :1011, in every case BEFORE the handler's try block, so this
 * escapes unconditionally on each. Registered on all four registries (one shared
 * variant object; parseAgainst keys matches by the registry KEY, never by identity).
 */
export const CF_PERMISSION_RESOLUTION_UNAVAILABLE_503 = variant(503, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Contact permissions are temporarily unavailable') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:444 — Trigger:
 * resolve_effective_permissions returned falsy org_id — i.e. authorizer.provider !=
 * 'clerk' (returns ('', [])) OR provider=='clerk' but authorizer.orgId is '' / missing.
 * HANDLER-produced 403, distinct from the authorizer's own {"message":"Unauthorized"}.
 *
 * Reached by ALL FOUR verbs (require_any_permission at GET :657, POST :696, PATCH :893,
 * DELETE :1011 — each before the try block). Registered on all four registries.
 */
export const CF_CLERK_AUTH_REQUIRED_403 = variant(403, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:451 (denied_message passed at
 * :660) — Trigger: effective permissions contain NEITHER 'org:contacts:view_contacts'
 * NOR 'org:contacts:import_contacts'
 */
export const CF_LIST_PERMISSION_DENIED_403 = variant(403, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('View or import contacts permission required') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:451 (denied_message passed at
 * :699) — Trigger: 'org:contacts:import_contacts' not in effective permissions
 */
export const CF_CREATE_PERMISSION_DENIED_403 = variant(403, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Import contacts permission required') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:451 (denied_message passed at
 * :896 for PATCH and :1014 for DELETE) — Trigger: 'org:contacts:manage_contacts' not in
 * effective permissions — same literal for PATCH (:896) and DELETE (:1014)
 */
export const CF_MUTATE_PERMISSION_DENIED_403 = variant(403, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Manage contacts permission required') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:634 (_load_org_and_definitions)
 * — Trigger: clerkOrganizationId-index query returned no Items, OR the returned item has
 * no truthy 'organizationId'. Reached by all four verbs (GET :666, POST :741, PATCH
 * :932, DELETE :1034). On mutating verbs it is re-evaluated inside the retry loop, so it
 * can fire on attempt 2+ if the org disappears mid-loop. Registered on all four
 * registries — the earlier GET/POST/PATCH omission made a route-keyed recorder report
 * NO_VARIANT for a real production 404 on three of the four verbs.
 */
export const CF_ORG_NOT_FOUND_404 = variant(404, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:672 — Trigger: permission ok,
 * org found — always returns, even for an empty/absent custom_field_definitions
 * attribute (normalize_definitions returns []) — BUG-FOR-BUG: normalize_definitions
 * (:472) is TOLERANT: non-list -> []; non-dict entries skipped; non-str 'key' skipped;
 * blank/duplicate keys skipped; a missing/blank label falls back to the KEY (:494) so
 * label can equal key; any type not in ('string',) is coerced to 'string' (:499). So
 * stored garbage is silently laundered rather than surfaced. — count/remaining are
 * Python ints from len()/max(), never DynamoDB Decimals — normalize_definitions rebuilds
 * every entry from str fields only, so no Decimal reaches json.dumps (which is bare: no
 * cls=DecimalEncoder anywhere in this file).
 */
export const CF_LIST_OK_200 = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ definitions: z.array(CustomFieldDefinition), count: z.number().int().nonnegative(), maxDefinitionsPerOrg: z.literal(50), remaining: z.number().int().nonnegative(), maxLabelLength: z.literal(50), allowedTypes: z.tuple([z.literal('string')]) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:679 — Trigger: any Exception
 * from read_org_by_clerk_org_id / normalize / json.dumps (e.g. DynamoDB
 * ResourceNotFoundException when ORGANIZATIONS_TABLE is unset, AccessDenied, throttle) —
 * generic — this handler does NOT leak str(e), unlike its two siblings in this group
 */
export const CF_LIST_INTERNAL_ERROR_500 = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Failed to list custom field definitions') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:709 — Trigger:
 * json.loads(event.get('body') or '{}') parsed to a non-dict (e.g. body is `[]`, `"x"`,
 * `3`, `null`). Note body=None or '' -> '{}' -> {} -> passes this check and fails later
 * at validate_label with 'label is required'.
 */
export const CF_CREATE_BODY_NOT_OBJECT_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Request body must be a JSON object') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:869 — Trigger:
 * json.JSONDecodeError from json.loads at :707
 */
export const CF_CREATE_INVALID_JSON_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:717 (message from validate_label
 * :512) — Trigger: body['label'] is not a str (absent -> None -> also not a str... NO:
 * absent gives None which is not str, so this fires). Wait: precedence — `not
 * isinstance(raw_label, str)` is checked FIRST, so an ABSENT label yields this message,
 * not 'label is required'. — BUG-FOR-BUG: Message drift: an ABSENT `label` key returns
 * "label must be a string", not "label is required". "label is required" (:515) is only
 * reachable when label IS a string that trims to empty (e.g. "" or " "). — an ABSENT
 * label produces THIS message, not 'label is required' — the isinstance check at :512
 * precedes the emptiness check at :515
 */
export const CF_CREATE_LABEL_NOT_STRING_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('label must be a string') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:717 (message from validate_label
 * :515) — Trigger: label is a str whose .strip() is empty — only reachable when label IS
 * a string that trims to empty
 */
export const CF_CREATE_LABEL_REQUIRED_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('label is required') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:717 (message from validate_label
 * :517) — Trigger: len(label.strip()) > 50 — f-string source: f'Label exceeds
 * {MAX_LABEL_LENGTH} characters' with MAX_LABEL_LENGTH=50 — fully determined, so pinned
 * as a literal not a regex
 */
export const CF_CREATE_LABEL_TOO_LONG_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Label exceeds 50 characters') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:717 (message from validate_label
 * :519) — Trigger: LABEL_FORBIDDEN_PATTERN /[\x00-\x1f\x7f<>]/ matched, OR LABEL_PATTERN
 * /^[\w &'/().,+-]+$/ (re.UNICODE) did NOT match from the start — BUG-FOR-BUG:
 * LABEL_PATTERN uses .match not .fullmatch, but the pattern is anchored with ^...+$ so
 * it is effectively fullmatch EXCEPT that Python's `$` also matches before a single
 * trailing newline. A label ending in "\n" is stripped by .strip() first, so unreachable
 * in practice. — implicit concatenation of two adjacent literals at
 * org_custom_fields.py:520-521; the apostrophe inside is a real ' character
 */
export const CF_CREATE_LABEL_INVALID_CHARS_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal("Label contains invalid characters. Allowed: letters, numbers, spaces, and & _ ' / ( ) . , + -") }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:717 (message from validate_label
 * :526) — Trigger: derive_key(label) == '' or the derived key fails KEY_PATTERN
 * /^[a-z][a-z0-9_]*$/. Reachable: a label of only allowed punctuation e.g. "&&&" or
 * "..." or "---" slugifies to ''. — BUG-FOR-BUG: CALLER-CONTROLLED STRING REFLECTED INTO
 * THE RESPONSE. `label` reaches the body unmodified except .strip() and the
 * LABEL_PATTERN character-class filter (which already excludes < > and control chars).
 * Quotes are NOT possible since " is not in the allowed class; ' IS allowed and is
 * reflected raw. — f-string source: f'Label "{label}" does not produce a valid field
 * key'. {label} is the trimmed caller label reflected verbatim; it passed LABEL_PATTERN
 * so it cannot contain " < > or control chars, but CAN contain ' — left as .*
 * deliberately, do not narrow.
 */
export const CF_CREATE_LABEL_NO_VALID_KEY_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Label ".*" does not produce a valid field key$/) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:717 (message from validate_label
 * :528) — Trigger: derive_key(label) is in RESERVED_KEYS (a frozenset of 94 keys,
 * :193-242) — BUG-FOR-BUG: Caller-controlled label reflected verbatim (same note as
 * CF_CREATE_LABEL_NO_VALID_KEY_400). — f-string source (two adjacent literals,
 * :528-531): f'"{label}" collides with a standard contact field and cannot be ' 'used as
 * a custom field'
 */
export const CF_CREATE_RESERVED_KEY_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^".*" collides with a standard contact field and cannot be used as a custom field$/) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:726 — Trigger: body.get('type',
 * 'string') not in ('string',). Any non-'string' value (including a list/dict/int) hits
 * this. Note the `in` test on a tuple with an unhashable value is fine (tuple
 * membership, not set). — f-string source: f'type must be one of: {",
 * ".join(ALLOWED_TYPES)}' with ALLOWED_TYPES=('string',) — join over a 1-tuple yields
 * exactly 'string'
 */
export const CF_CREATE_BAD_TYPE_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('type must be one of: string') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:772 — Trigger: a definition with
 * the DERIVED key already exists (:746). Returned INSIDE the retry loop on the first
 * attempt; no write occurs, so no concurrency guard. — BUG-FOR-BUG: STATUS 200 for a
 * POST that creates nothing — deliberate (docstring :28-30), pin as-is. Note
 * `labelChanged` compares with `!=` (case-sensitive) while the create-path collision
 * check at :803 compares with .lower() — two different equality notions for labels in
 * the same function. — 200 for a POST that creates nothing (deliberate). labelChanged
 * uses case-SENSITIVE != at :769 while the collision check at :803 uses .lower() — two
 * different label equality notions in one function.
 */
export const CF_CREATE_REUSED_200 = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('Custom field already exists'), definition: CustomFieldDefinition, reused: z.literal(true), requestedLabel: z.string(), labelChanged: z.boolean(), definitions: z.array(CustomFieldDefinition), count: z.number().int().nonnegative(), maxDefinitionsPerOrg: z.literal(50), remaining: z.number().int().nonnegative(), maxLabelLength: z.literal(50), allowedTypes: z.tuple([z.literal('string')]) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:807 — Trigger: no key match, but
 * some existing definition has d['label'].lower() == label.lower() (:803) — BUG-FOR-BUG:
 * Note the PATCH twin (CF_RENAME_LABEL_CONFLICT_409) has the SAME error text and code
 * but OMITS `conflictingDefinition`. The two 409s are NOT the same shape. — f-string
 * source (two adjacent literals, :812-813): f'Another custom field already uses the
 * label ' f'"{label}"'. NOTE the PATCH twin shares this message and code but OMITS
 * conflictingDefinition — do not share one schema.
 */
export const CF_CREATE_LABEL_CONFLICT_409 = variant(409, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Another custom field already uses the label ".*"$/), code: z.literal('CUSTOM_FIELD_LABEL_CONFLICT'), conflictingDefinition: CustomFieldDefinition }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:824 — Trigger: len(definitions)
 * >= 50 after the reuse and label-collision checks — BUG-FOR-BUG: 409 (not 403/422) for
 * a quota violation — pin as-is. — f-string source (three adjacent literals, :829-831);
 * MAX_DEFINITIONS_PER_ORG=50 makes it fully determined. 409 (not 403/422) for a quota
 * violation — pinned as-is.
 */
export const CF_CREATE_QUOTA_EXCEEDED_409 = variant(409, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Custom field limit reached (50 per organization). Delete an unused field before adding a new one.'), code: z.literal('CUSTOM_FIELD_QUOTA_EXCEEDED'), count: z.number().int().nonnegative(), maxDefinitionsPerOrg: z.literal(50) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:858 — Trigger:
 * _write_definitions succeeded (no ConditionalCheckFailedException) — labelChanged is
 * the hardcoded literal False at :853, never computed on this path
 */
export const CF_CREATE_CREATED_201 = variant(201, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('Custom field created'), definition: CustomFieldDefinition, reused: z.literal(false), requestedLabel: z.string(), labelChanged: z.literal(false), definitions: z.array(CustomFieldDefinition), count: z.number().int().positive(), maxDefinitionsPerOrg: z.literal(50), remaining: z.number().int().nonnegative(), maxLabelLength: z.literal(50), allowedTypes: z.tuple([z.literal('string')]) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:866 -> _conflict_response :616 —
 * Trigger: all MAX_WRITE_ATTEMPTS=5 loop iterations ended in DefinitionsConflictError
 * (ConditionalCheckFailedException) — _conflict_response('create the custom field') at
 * :616. CONTAINS AN EM DASH U+2014, not a hyphen.
 */
export const CF_CREATE_WRITE_CONFLICT_409 = variant(409, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Could not create the custom field — another change to this organization\'s custom fields landed at the same time. Please retry.'), code: z.literal('CUSTOM_FIELD_WRITE_CONFLICT') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:876 — Trigger: any
 * non-JSONDecodeError Exception, including a NON-conditional DynamoDB failure re-raised
 * from _write_definitions :613 — generic — no str(e)
 */
export const CF_CREATE_INTERNAL_ERROR_500 = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Failed to create custom field definition') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:904 — Trigger: parsed body is
 * not a dict
 */
export const CF_RENAME_BODY_NOT_OBJECT_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Request body must be a JSON object') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:987 — Trigger:
 * json.JSONDecodeError at :902
 */
export const CF_RENAME_INVALID_JSON_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:912 — Trigger: body['key']
 * absent, not a str, or trims to empty — the single quotes around key are literal
 * characters in the source string
 */
export const CF_RENAME_KEY_REQUIRED_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal("Request body must include a 'key'") }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:921 (validate_label at :919) —
 * Trigger: validate_label(body.get('label')) returned an error — BUG-FOR-BUG: PATCH
 * DISCARDS the derived key (`_derived_key` at :919) but STILL enforces the
 * key-derivation rules on the new label: a rename to a label whose slug is RESERVED
 * (e.g. renaming a field to "Email") is rejected 400 even though the key is immutable
 * and no collision could occur. This is a real behavioral surprise the contract must
 * pin. — PATCH reuses validate_label wholesale and DISCARDS the derived key, so it still
 * rejects a rename whose label slugifies to a RESERVED key even though the key is
 * immutable — all six validate_label messages are reachable here.
 */
export const CF_RENAME_LABEL_VALIDATION_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.union([z.literal('label must be a string'), z.literal('label is required'), z.literal('Label exceeds 50 characters'), z.literal("Label contains invalid characters. Allowed: letters, numbers, spaces, and & _ ' / ( ) . , + -"), z.string().regex(/^Label ".*" does not produce a valid field key$/), z.string().regex(/^".*" collides with a standard contact field and cannot be used as a custom field$/)]) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:939 — Trigger: no definition has
 * key == target_key — BUG-FOR-BUG: FINDING: `target_key` reaches the response body
 * UNVALIDATED from caller input. Unlike `label`, there is no character-class filter on
 * it at all (only isinstance str + strip). Do not narrow this to KEY_PATTERN in the
 * contract. — f-string source: f'Custom field "{target_key}" not found'. FINDING:
 * target_key is UNVALIDATED caller input — isinstance-str + .strip() only, no
 * KEY_PATTERN check — so arbitrary text incl. quotes and control chars is reflected. Do
 * not narrow.
 */
export const CF_RENAME_NOT_FOUND_404 = variant(404, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Custom field ".*" not found$/) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:951 — Trigger: some OTHER
 * definition (d['key'] != target_key) has d['label'].lower() == label.lower() —
 * BUG-FOR-BUG: Shape divergence from CF_CREATE_LABEL_CONFLICT_409 (same code + same
 * message, different body keys). A single shared schema would be wrong. — same message +
 * code as the POST twin but NO conflictingDefinition key — the shape divergence is real,
 * keep the two schemas separate
 */
export const CF_RENAME_LABEL_CONFLICT_409 = variant(409, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Another custom field already uses the label ".*"$/), code: z.literal('CUSTOM_FIELD_LABEL_CONFLICT') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:972 — Trigger:
 * _write_definitions succeeded — BUG-FOR-BUG: Asymmetric body: PATCH omits
 * count/maxDefinitionsPerOrg/remaining/maxLabelLength/allowedTypes that GET/POST/DELETE
 * all include. `keyUnchanged` is a hardcoded literal true, never false. — asymmetric vs
 * every other 200 in this file: NO count and NO quota fields. keyUnchanged is a
 * hardcoded literal true, never false.
 */
export const CF_RENAME_OK_200 = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('Custom field label updated'), definition: CustomFieldDefinition, definitions: z.array(CustomFieldDefinition), keyUnchanged: z.literal(true) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:984 -> _conflict_response :616 —
 * Trigger: 5 attempts exhausted with DefinitionsConflictError; action='rename the custom
 * field' — _conflict_response('rename the custom field'). EM DASH U+2014.
 */
export const CF_RENAME_WRITE_CONFLICT_409 = variant(409, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Could not rename the custom field — another change to this organization\'s custom fields landed at the same time. Please retry.'), code: z.literal('CUSTOM_FIELD_WRITE_CONFLICT') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:994 — Trigger: any
 * non-JSONDecodeError Exception
 */
export const CF_RENAME_INTERNAL_ERROR_500 = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Failed to rename custom field definition') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:1023 — Trigger:
 * queryStringParameters absent/null, or 'key' absent, or not a str, or trims to empty.
 * Checked BEFORE unquote. — BUG-FOR-BUG: Order bug (benign): the emptiness check runs on
 * the RAW param, then unquote() is applied at :1028. A key of "%20" passes the check
 * (non-empty raw) and then unquotes+strips to '' — reaching the loop with target_key ==
 * '', which matches no definition and returns the 404 with an empty key in the message.
 * — single quotes around key are literal. Order quirk: emptiness is checked on the RAW
 * param before unquote(), so '%20' passes here and 404s later with an empty key in the
 * message.
 */
export const CF_DELETE_KEY_PARAM_REQUIRED_400 = variant(400, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal("A 'key' query parameter is required") }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:1041 — Trigger: filtering out
 * target_key removed nothing (len(remaining) == len(definitions)) — BUG-FOR-BUG:
 * FINDING: unvalidated caller input reflected. Also: because unquote() runs, a
 * percent-encoded payload is DECODED into the response body. — byte-identical literal to
 * the PATCH 404. Here target_key is unquote(raw).strip(), so a percent-encoded payload
 * is DECODED into the body. Unvalidated — do not narrow.
 */
export const CF_DELETE_NOT_FOUND_404 = variant(404, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Custom field ".*" not found$/) }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:1054 — Trigger:
 * _write_definitions succeeded — BUG-FOR-BUG: orphanedValuesWarning is an UNCONDITIONAL
 * constant string on every successful delete, not a conditional field. No quota fields
 * here either (has count but not maxDefinitionsPerOrg/remaining). — f-string source for
 * message: f'Custom field "{target_key}" deleted'. orphanedValuesWarning is three
 * adjacent literals (:1062-1064) joined with single spaces, emitted UNCONDITIONALLY on
 * every successful delete. Has count but NOT the quota fields.
 */
export const CF_DELETE_OK_200 = variant(200, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.string().regex(/^Custom field ".*" deleted$/), definitions: z.array(CustomFieldDefinition), count: z.number().int().nonnegative(), orphanedValuesWarning: z.literal('Values already stored on contacts for this field are not deleted. They remain on the contact records and will be re-adopted if a field with the same key is created again.') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:1069 -> _conflict_response :616
 * — Trigger: 5 attempts exhausted; action='delete the custom field' —
 * _conflict_response('delete the custom field'). EM DASH U+2014.
 */
export const CF_DELETE_WRITE_CONFLICT_409 = variant(409, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Could not delete the custom field — another change to this organization\'s custom fields landed at the same time. Please retry.'), code: z.literal('CUSTOM_FIELD_WRITE_CONFLICT') }).strict());

/**
 * site functions/org_custom_fields/org_custom_fields.py:1073 — Trigger: any Exception.
 * NOTE: delete_definition has NO json.JSONDecodeError branch (it reads no body) — every
 * failure funnels here. — delete_definition has NO JSONDecodeError branch (it reads no
 * body) — every failure funnels here
 */
export const CF_DELETE_INTERNAL_ERROR_500 = variant(500, H_GET_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Failed to delete custom field definition') }).strict());


// ===== functions/org_role_permissions/org_role_permissions.py ============
/**
 * Response helper: PARTIAL: clerk_role_errors.py:82 role_not_available_response(role) ->
 * {'statusCode': 409, 'body': json.dumps({'error','code','role'})} — it returns NO
 * headers; the caller MUTATES the dict at org_role_permissions.py:562
 * (`response['headers'] = CORS_HEADERS`) before returning. Every other return site is an
 * inline dict literal with CORS_HEADERS.
 */
/**
 * NOTES: SEVEN routes were expected but the file implements SIX (GET/PUT/DELETE on
 * /org-role-permissions + POST/PATCH/DELETE on /custom-roles); ApiStack.ts:1217-1256
 * registers exactly those six. Critical items for the contract author: (1)
 * `print(json.dumps(event))` at :578 LOGS THE BEARER TOKEN on every request — both sibling
 * handlers explicitly refuse to do this; this is the group's most serious finding. (2) SSM
 * is read at MODULE IMPORT with an unconfigured client (:54) — an SSM blip is a Lambda
 * INIT error, i.e. a gateway 5xx with NO body and NO CORS headers, for all six routes. (3)
 * get_org_id_and_role (:97) and handler() (:580-581) use DIRECT SUBSCRIPTS on event, so a
 * malformed/absent authorizer context is an UNCAUGHT KeyError -> gateway 500, whereas both
 * siblings return a clean 403. (4) There is NO json.JSONDecodeError branch anywhere:
 * malformed JSON, a bodyless request (json.loads(event['body']) with body None), and a
 * non-dict body ALL become a 500 with a leaked str(e). (5) Two response bodies embed
 * PYTHON LIST REPR inside a JSON string (single quotes): "Invalid permissions: ['x']"
 * (:234, :341) and "reassignRole must be one of: ['org:admin', 'org:finance',
 * 'org:editor', 'org:marketing']" (:481). (6) PUT /org-role-permissions with an
 * absent/empty rolePermissions silently WIPES all customization and returns 200; DELETE
 * /org-role-permissions additionally strips every custom role's permissions while leaving
 * the roles themselves. (7) POST lowercases the slug, PATCH and DELETE do NOT — a
 * mixed-case slug 404s on rename/delete. (8) DELETE /custom-roles is a NON-ATOMIC
 * multi-write across N members (2 Clerk writes each) before the final metadata write; a
 * mid-sweep failure returns 500 with members already reassigned. (9) The 409
 * role-not-available body gets its headers by post-hoc dict MUTATION at :562, not from the
 * helper. (10) The dispatch is a SUBSTRING test `'/custom-roles' in path`, not equality.
 * (11) Authorization here is a raw string compare on the authorizer's orgRole against
 * ('org:admin','admin') with NO Clerk lookup — structurally different from the
 * permission-resolution model in the two sibling handlers, and it can never return 503.
 */

/**
 * site functions/org_role_permissions/org_role_permissions.py:600 — Trigger: Dispatch at
 * :584-598. If '/custom-roles' is IN the path: only POST/PATCH/DELETE match, so GET
 * /custom-roles or PUT /custom-roles falls through to 405. ELSE (any other path): only
 * GET/PUT/DELETE match, so POST or PATCH on /org-role-permissions falls through.
 * Reachable in practice only if a route were added; the registered table
 * (ApiStack.ts:1217-1256) exposes exactly the six matching pairs. — BUG-FOR-BUG: THE
 * DISPATCH IS SUBSTRING-BASED, NOT EXACT: `if '/custom-roles' in path` (:584). Any path
 * CONTAINING that substring routes to the custom-roles half. Harmless today (one
 * registered path) but the contract must model the dispatch as a substring test on
 * requestContext.http.path, not an equality test. — dispatch is a SUBSTRING test
 * `'/custom-roles' in path` (:584), not equality — model it that way
 */
export const RP_METHOD_NOT_ALLOWED_405 = variant(405, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:116 (require_admin) —
 * Trigger: get_org_id_and_role returned falsy org_id — provider != 'clerk' ->
 * (None,None), or orgId is ''
 */
export const RP_ADMIN_CLERK_AUTH_REQUIRED_403 = variant(403, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:123 — Trigger: org_role
 * not in ('org:admin', 'admin') — a RAW STRING comparison on the authorizer's orgRole
 * claim. NOTE: unlike the two sibling handlers in this group, this file does NO Clerk
 * membership lookup, NO custom-role resolution, NO permission_overrides — admin-ness is
 * taken straight from the authorizer context. Both the prefixed and unprefixed spellings
 * are accepted. — BUG-FOR-BUG: STRUCTURAL DIVERGENCE from
 * org_contact_tags/org_custom_fields: those resolve effective permissions through Clerk
 * (and can 503); this one does a pure string compare and can never 503. Two different
 * authorization models on adjacent endpoints. — raw string compare on the authorizer
 * orgRole against ('org:admin','admin') with NO Clerk lookup — this handler can never
 * return 503, unlike its two siblings
 */
export const RP_ADMIN_ROLE_REQUIRED_403 = variant(403, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Admin role required') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:149 — Trigger: falsy
 * org_id — BUG-FOR-BUG: GET is NOT admin-gated — any Clerk org member of any role can
 * read the org's full role-permission matrix, defaults, the entire ALL_PERMISSIONS
 * catalog, and every customRoles entry. — GET is NOT admin-gated — any org member reads
 * the full permission matrix and catalog
 */
export const RP_GET_CLERK_AUTH_REQUIRED_403 = variant(403, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:178 — Trigger: metadata
 * fetched successfully — BUG-FOR-BUG: (a) `customRoles` is passed through with NO shape
 * validation — whatever is in Clerk metadata reaches the client (documented shape is
 * {slug: {label, createdAt}} but nothing enforces it). (b) Stored custom permissions are
 * FILTERED against ALL_PERMISSIONS at :166/:174, so a permission that has since been
 * removed from the catalog silently disappears from the response while remaining in
 * Clerk metadata — the response is NOT the stored state. (c) `for slug in custom_roles`
 * (:171) iterates a dict; if metadata.customRoles were a LIST, slug would be the element
 * and role_key would be 'custom:<element>' — no type guard. — rolePermissions is
 * z.record because its key set is OPEN — the three CUSTOMIZABLE_ROLES plus one dynamic
 * 'custom:<slug>' per stored custom role. Values ARE closed to ALL_PERMISSIONS because
 * :166/:174 filter against it (so a permission since removed from the catalog silently
 * vanishes from the response while remaining in Clerk — the response is NOT the stored
 * state). customRoles is the RAW Clerk metadata value echoed with ZERO validation, so it
 * is pinned z.unknown() — the documented {slug:{label,createdAt}} shape is unenforced and
 * NOTHING constrains the JSON type. `custom_roles = metadata.get('customRoles', {})`
 * (:158) is echoed verbatim at :186 with no isinstance guard anywhere between, and
 * `for slug in custom_roles` (:171) iterates a LIST's elements or a STRING's characters
 * just as happily as a dict's keys. Executed against the pinned handler: {'a':1} -> 200,
 * ['x','y'] -> 200 (role_key becomes 'custom:x'/'custom:y'), 'ab' -> 200
 * ('custom:a'/'custom:b'), [] -> 200 — all four echo customRoles as-is. The previous
 * z.record(z.string(), z.unknown()) pin rejected three of those four real 200s, which
 * CONTRADICTED this very docblock. Precedent for a raw passthrough: EveryOrgMappedOrg
 * and NonprofitSettingsWrittenAttributes. The rest of the body stays .strict().
 */
export const RP_GET_OK_200 = variant(200, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ rolePermissions: z.record(z.string(), z.array(PermissionKey)), defaults: z.object({ 'org:finance': z.array(PermissionKey), 'org:editor': z.array(PermissionKey), 'org:marketing': z.array(PermissionKey) }).strict(), allPermissions: ALL_PERMISSIONS_TUPLE, isCustomized: z.boolean(), customRoles: z.unknown() }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:191 — Trigger: any
 * Exception from get_org_metadata (Clerk down, 429 after MAX_RETRIES=3, org not found in
 * Clerk) or json.dumps — BUG-FOR-BUG: Clerk being unavailable yields 500 with a leaked
 * Clerk error string, NOT a 503. Contrast the 503-fail-closed design in both sibling
 * handlers. — f-string source: f'Failed to get role permissions: {str(e)}'. LEAKS
 * str(e), which here can be a Clerk API error body. Clerk being down yields 500, NOT
 * 503.
 */
export const RP_GET_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to get role permissions: [\s\S]*$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:218 — Trigger: a key of
 * body.rolePermissions is not in set(CUSTOMIZABLE_ROLES) | {f'custom:{slug}' for slug in
 * metadata.customRoles}. Notably 'org:admin' and 'org:member' land here. — BUG-FOR-BUG:
 * FINDING: unvalidated caller input reflected into the body. Also: iteration order of a
 * JSON object determines WHICH invalid role is named when several are invalid. —
 * f-string source: f'Cannot customize role: {role}.' — TRAILING PERIOD is inside the
 * f-string. {role} is a caller-supplied JSON object KEY reflected verbatim
 * (unvalidated). Which invalid role is named depends on JSON object iteration order.
 */
export const RP_PUT_CANNOT_CUSTOMIZE_ROLE_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Cannot customize role: [\s\S]*\.$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:224 — Trigger: role is
 * valid but its value is not a list — BUG-FOR-BUG: Caller-controlled `role` reflected
 * (though here it is constrained to the valid set, so it IS narrowable). — f-string
 * source: f'Permissions for {role} must be an array' — NO trailing period, unlike the
 * sibling 400 above. {role} here IS constrained to the valid role set, but kept wide to
 * mirror the f-string.
 */
export const RP_PUT_PERMS_NOT_ARRAY_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Permissions for [\s\S]* must be an array$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:231 — Trigger: any element
 * of perms is not in ALL_PERMISSIONS — BUG-FOR-BUG: PYTHON REPR LEAKED INTO A JSON
 * STRING — the value is `['x', 'y']` with single quotes, not `["x","y"]`. A schema that
 * models this as a JSON array is wrong; it is a string containing a Python list literal.
 * Also the offending permission strings are caller input reflected verbatim. — f-string
 * source: f'Invalid permissions: {invalid}' where `invalid` is a PYTHON LIST
 * interpolated by str() — the body is a STRING containing Python repr with SINGLE
 * quotes, e.g. "Invalid permissions: ['org:bogus:thing', 'x']" (verified by execution).
 * It is NOT a JSON array; a schema modelling it as one is wrong.
 */
export const RP_PUT_INVALID_PERMISSIONS_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Invalid permissions: \[[\s\S]*\]$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:246 — Trigger:
 * clerk.organizations.update succeeded — BUG-FOR-BUG: (a) `body.get('rolePermissions',
 * {})` defaults to {} — a PUT with `{}` or with no rolePermissions key WIPES the org's
 * entire customization to {} and returns 200 'updated successfully'. There is no
 * anti-wipe guard (contrast org_contact_tags PUT, which explicitly refuses an absent
 * list for exactly this reason). (b) `json.loads(event['body'])` uses DIRECT SUBSCRIPT
 * (:206) — a bodyless PUT gives body=None -> TypeError -> caught by the broad handler ->
 * 500 "Failed to update role permissions: the JSON object must be str, bytes or
 * bytearray, not NoneType". (c) There is no read-modify-write guard on Clerk metadata:
 * get then update, so a concurrent write is lost. — the caller's rolePermissions object
 * echoed back verbatim. z.record because the key set is open (three built-ins plus
 * dynamic custom:<slug>); values are closed to ALL_PERMISSIONS because the :229
 * validation loop rejects anything else. An ABSENT/empty rolePermissions defaults to {}
 * at :207, silently WIPING all customization and still returning this 200 — there is no
 * anti-wipe guard here (contrast the tags PUT).
 */
export const RP_PUT_OK_200 = variant(200, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('Role permissions updated successfully'), rolePermissions: z.record(z.string(), z.array(PermissionKey)) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:256 — Trigger: ANY
 * Exception — there is NO json.JSONDecodeError branch anywhere in this file, so
 * malformed JSON, a None body, a non-dict body (`.items()` AttributeError), and Clerk
 * failures ALL become this 500. — BUG-FOR-BUG: No 400 for malformed JSON on ANY route of
 * this handler. That is the single biggest divergence from the two siblings. — f-string
 * source: f'Failed to update role permissions: {str(e)}'. LEAKS str(e). There is NO
 * JSONDecodeError branch anywhere in this file, so malformed JSON, a bodyless request
 * (json.loads(None) → TypeError 'the JSON object must be str, bytes or bytearray, not
 * NoneType'), and a non-dict body ALL land here as 500 instead of 400.
 */
export const RP_PUT_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to update role permissions: [\s\S]*$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:280 — Trigger:
 * metadata.pop('rolePermissions', None) then clerk.organizations.update succeeded —
 * BUG-FOR-BUG: DESTRUCTIVE SIDE EFFECT ON CUSTOM ROLES: removing the whole
 * rolePermissions map also removes every `custom:<slug>` entry, so all custom roles
 * silently drop to zero permissions while their customRoles entries survive. The
 * response does not mention this. Idempotent (pop with default None). — closed 3-key
 * object here (unlike GET/PUT): built only from CUSTOMIZABLE_ROLES at :285, so custom
 * roles are NOT included. Destructive side effect the body does not mention: popping the
 * whole rolePermissions map also drops every custom:<slug> grant, leaving custom roles
 * at zero permissions while their customRoles entries survive.
 */
export const RP_RESET_OK_200 = variant(200, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.literal('Role permissions reset to defaults'), rolePermissions: z.object({ 'org:finance': z.array(PermissionKey), 'org:editor': z.array(PermissionKey), 'org:marketing': z.array(PermissionKey) }).strict() }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:290 — Trigger: any
 * Exception from get_org_metadata or clerk.organizations.update — f-string source:
 * f'Failed to reset role permissions: {str(e)}'. LEAKS str(e).
 */
export const RP_RESET_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to reset role permissions: [\s\S]*$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:316 — Trigger: slug (=
 * body.get('slug','').strip().lower()) is empty OR fails re.match(r'^[a-z0-9_]+$',
 * slug). NOTE re.match is UNANCHORED AT THE END — but the pattern ends with `+$` so it
 * is effectively anchored, EXCEPT Python's `$` also matches before a trailing newline;
 * .strip() removes that first, so unreachable. — BUG-FOR-BUG: `body.get('slug','')` — if
 * slug is a NON-STRING (e.g. 5 or a list), .strip() raises AttributeError -> caught by
 * the broad handler -> 500, not 400. — a NON-STRING slug does not reach here —
 * body.get('slug','').strip() raises AttributeError → 500, not 400
 */
export const CR_CREATE_INVALID_SLUG_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Invalid slug. Use only lowercase letters, numbers, and underscores.') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:322 — Trigger: len(slug) >
 * 50 (checked AFTER the pattern check, so an invalid-character 51-char slug returns the
 * pattern message instead) — checked AFTER the pattern check, so a 51-char slug with
 * invalid characters returns the pattern message instead
 */
export const CR_CREATE_SLUG_TOO_LONG_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Slug must be 50 characters or less.') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:329 — Trigger: label (=
 * body.get('label','').strip()) is empty OR len > 100. ONE message covers BOTH
 * conditions. — BUG-FOR-BUG: Label content is otherwise UNVALIDATED — any characters
 * (control chars, markup, unicode) up to 100 chars are accepted and stored in Clerk
 * metadata, then echoed in the 201 message. Same non-string AttributeError->500 hazard
 * as slug. — ONE message covers BOTH the empty and the too-long condition. Label content
 * is otherwise unvalidated — any characters up to 100 are stored and echoed in the 201.
 */
export const CR_CREATE_LABEL_INVALID_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Label is required and must be 100 characters or less.') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:337 — Trigger: any element
 * of body.get('permissions', []) is not in ALL_PERMISSIONS. If `permissions` is a
 * STRING, the comprehension iterates its CHARACTERS and every char is invalid —
 * producing e.g. "Invalid permissions: ['a', 'b', 'c']". — BUG-FOR-BUG: String-iteration
 * bug: a non-list `permissions` is not type-checked (unlike PUT /org-role-permissions,
 * which does check isinstance list). A dict value iterates its KEYS. — f-string source:
 * f'Invalid permissions: {invalid}' — PYTHON LIST REPR inside a JSON string (single
 * quotes), same as the PUT twin. Extra hazard here: `permissions` is NOT type-checked
 * (unlike PUT :223), so a STRING value iterates its CHARACTERS and every char reports
 * invalid, e.g. "Invalid permissions: ['a', 'b', 'c']"; a dict iterates its keys.
 */
export const CR_CREATE_INVALID_PERMISSIONS_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Invalid permissions: \[[\s\S]*\]$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:348 — Trigger:
 * len(metadata.customRoles) >= 5 — BUG-FOR-BUG: 400 for a quota violation here, vs 409
 * for the analogous quota in org_custom_fields. Inconsistent across the group; pin both
 * as-is. — f-string source: f'Maximum {MAX_CUSTOM_ROLES} custom roles allowed per
 * organization.' with MAX_CUSTOM_ROLES=5. NOTE 400 here vs 409 for the analogous quota
 * in org_custom_fields — inconsistent across the group, both pinned as-is.
 */
export const CR_CREATE_MAX_ROLES_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Maximum 5 custom roles allowed per organization.') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:355 — Trigger: slug
 * already a key of metadata.customRoles — f-string source: f'A custom role with slug
 * "{slug}" already exists.' — the ONE reflected value in this group that IS safely
 * narrowable: slug already passed /^[a-z0-9_]+$/ and the 50-char cap before reaching
 * here.
 */
export const CR_CREATE_SLUG_EXISTS_409 = variant(409, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^A custom role with slug "[a-z0-9_]{1,50}" already exists\.$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:379 — Trigger:
 * clerk.organizations.update succeeded — BUG-FOR-BUG: The 201 does NOT echo the created
 * permissions or the createdAt timestamp. createdAt is written as
 * datetime.now(timezone.utc).isoformat() (:364) — a NON-DETERMINISTIC value written but
 * never returned. — f-string sources: f'Custom role "{label}" created successfully' and
 * f'custom:{slug}'. label is UNVALIDATED caller text up to 100 chars reflected verbatim,
 * so the message regex stays wide. The 201 does NOT echo the created permissions or the
 * createdAt timestamp — createdAt is written as datetime.now(timezone.utc).isoformat()
 * (:364), non-deterministic and never returned.
 */
export const CR_CREATE_OK_201 = variant(201, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.string().regex(/^Custom role "[\s\S]*" created successfully$/), slug: z.string().regex(/^[a-z0-9_]{1,50}$/), roleKey: z.string().regex(/^custom:[a-z0-9_]{1,50}$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:391 — Trigger: any
 * Exception: bodyless/malformed JSON (json.loads(event['body']) direct subscript, :309),
 * non-string slug/label AttributeError, Clerk update failure — BUG-FOR-BUG: No 400 for
 * malformed JSON. — f-string source: f'Failed to create custom role: {str(e)}'. LEAKS
 * str(e). Absorbs bodyless/malformed JSON and the non-string slug/label AttributeError.
 */
export const CR_CREATE_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to create custom role: [\s\S]*$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:410 — Trigger:
 * body.get('slug','').strip() is empty — BUG-FOR-BUG: PATCH does .strip() but NOT
 * .lower() (:406), unlike POST (:310) which lowercases. So PATCH with slug 'Volunteer'
 * will NOT find a role stored under 'volunteer' and returns 404 — a real create/rename
 * asymmetry. Also PATCH does NOT validate the slug against /^[a-z0-9_]+$/, so arbitrary
 * text reaches the 404 message. — lowercase 's', no trailing period — stylistically
 * unlike every neighbouring message. PATCH does .strip() but NOT .lower() (:406) whereas
 * POST lowercases (:310), so a mixed-case slug 404s on rename.
 */
export const CR_UPDATE_SLUG_REQUIRED_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('slug is required') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:417 — Trigger: new_label
 * empty or len > 100 — byte-identical literal to CR_CREATE_LABEL_INVALID_400
 */
export const CR_UPDATE_LABEL_INVALID_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('Label is required and must be 100 characters or less.') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:427 — Trigger: slug not a
 * key of metadata.customRoles — BUG-FOR-BUG: FINDING: unvalidated caller input
 * reflected. Same literal string is used by DELETE /custom-roles at :488 — the two 404s
 * are byte-identical. — f-string source: f'Custom role "{slug}" not found.' — TRAILING
 * PERIOD. {slug} is unvalidated caller input (strip only, no pattern, no lowercasing),
 * so kept wide. Byte-identical to the DELETE 404 at :491.
 */
export const CR_UPDATE_NOT_FOUND_404 = variant(404, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Custom role "[\s\S]*" not found\.$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:442 — Trigger:
 * clerk.organizations.update succeeded — BUG-FOR-BUG: (a) Body is a bare {message}. (b)
 * `custom_roles[slug]['label'] = new_label` (:433) assumes the stored entry is a MUTABLE
 * DICT — if Clerk metadata holds a string there (hand-edited/legacy), this raises
 * TypeError -> 500. (c) new_label is unvalidated content reflected verbatim. — f-string
 * source: f'Custom role renamed to "{new_label}"'. SINGLE-KEY body — no slug, no
 * roleKey, no updated object. new_label is unvalidated content reflected verbatim.
 */
export const CR_UPDATE_OK_200 = variant(200, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.string().regex(/^Custom role renamed to "[\s\S]*"$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:450 — Trigger: any
 * Exception (bodyless JSON, non-string slug/label, non-dict stored role entry, Clerk
 * failure) — f-string source: f'Failed to update custom role: {str(e)}'. LEAKS str(e).
 * Also catches the TypeError from `custom_roles[slug]['label'] = new_label` (:433) when
 * the stored entry is not a mutable dict.
 */
export const CR_UPDATE_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to update custom role: [\s\S]*$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:469 — Trigger:
 * queryStringParameters absent/null, or 'slug' absent, or trims to empty. NOTE: NO
 * unquote() here (contrast org_custom_fields DELETE, which does unquote), so a
 * percent-encoded slug is compared in its ENCODED form and will 404. — BUG-FOR-BUG:
 * params.get('slug','').strip() — a non-string query value is impossible from API
 * Gateway, so no AttributeError here in practice. — no unquote() here (contrast the
 * custom-fields DELETE), so a percent-encoded slug is compared in its ENCODED form and
 * 404s
 */
export const CR_DELETE_SLUG_REQUIRED_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal('slug query parameter is required') }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:478 — Trigger: the
 * reassignRole query param (default 'org:editor' at :466) is not in that 4-element list
 * — BUG-FOR-BUG: Python list repr inside a JSON string. Also note 'org:member' is NOT
 * accepted — a custom role's holders cannot be demoted to plain member; the minimum
 * reassignment is org:editor (the default). — f-string source: f'reassignRole must be
 * one of: {valid_reassign}' where valid_reassign is a PYTHON LIST interpolated by str().
 * The repr is fully deterministic (verified by execution) so it pins as an exact literal
 * — SINGLE quotes and ', ' separators inside a JSON string, NOT a JSON array. Note
 * 'org:member' is absent: the reassignment floor is org:editor (also the default).
 */
export const CR_DELETE_INVALID_REASSIGN_ROLE_400 = variant(400, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.literal("reassignRole must be one of: ['org:admin', 'org:finance', 'org:editor', 'org:marketing']") }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:488 — Trigger: slug not a
 * key of metadata.customRoles — BUG-FOR-BUG: FINDING: unvalidated reflection. —
 * byte-identical literal to CR_UPDATE_NOT_FOUND_404. slug is unvalidated (strip only,
 * not lowercased, not unquoted).
 */
export const CR_DELETE_NOT_FOUND_404 = variant(404, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Custom role "[\s\S]*" not found\.$/) }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:559-563 ->
 * clerk_role_errors.py:82 — Trigger: is_role_not_available_error(e) is True —
 * exc.status_code in (400,404,409,422) AND a structured error whose code is one of
 * {organization_role_not_found, organization_role_not_available,
 * organization_role_not_in_role_set}, OR code == 'resource_not_found' with a
 * long_message/message that lowercases+strips to exactly 'organization role not found'
 * or 'organization role not found.' (clerk_role_errors.py:45-79) — BUG-FOR-BUG: LATENT
 * UnboundLocalError: the except block at :559-560 references `reassign_role`, but that
 * name is only bound at :466 INSIDE the try. If the exception fires BEFORE :466 — which
 * it can, since :464 `event.get('queryStringParameters', {})` and :465/:466 are the
 * first statements — the except raises UnboundLocalError out of the handler -> UNCAUGHT
 * -> gateway 500 with no body. In practice :464-466 cannot raise (dict .get / str .strip
 * on API Gateway values), so it is latent, not live. Also note
 * `role_not_available_response` returns a dict with only statusCode+body and the caller
 * MUTATES it to add headers; if that mutation were ever removed the 409 would ship with
 * NO headers. — body built by clerk_role_errors.role_not_available_response(:82); the
 * message is ROLE_NOT_AVAILABLE_MESSAGE (two adjacent literals,
 * clerk_role_errors.py:12-15). `role` is the reassignRole value, already validated
 * against the 4-element list at :477, so it narrows to ReassignRole. The helper returns
 * NO headers key — the caller MUTATES the dict at :562 to attach CORS_HEADERS.
 */
export const CR_DELETE_ROLE_NOT_AVAILABLE_409 = variant(409, ANY_HEADERS, z.object({ error: z.literal("The selected role is not available for this organization. Add it to the organization's Clerk Role Set, then try again."), code: z.literal('role_not_available'), role: ReassignRole }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:549 — Trigger: membership
 * sweep completed and the final clerk.organizations.update succeeded — BUG-FOR-BUG:
 * NON-ATOMIC MULTI-WRITE: the loop performs up to 2 Clerk writes PER affected member
 * (role update + metadata update) BEFORE the single metadata update that removes the
 * role. A failure partway leaves members already reassigned while the custom role still
 * exists in metadata — and the caller gets a 500. Retrying is only partially idempotent
 * (already-reassigned members no longer match custom_role == slug so they are skipped).
 * Also: a member whose public_user_data is None is SILENTLY SKIPPED (:514) and not
 * counted, leaving an orphaned custom_role in their metadata. — f-string source:
 * f'Custom role "{slug}" deleted' — NO trailing period, unlike the 404's 'not found.'.
 * reassignedUsers is a Python int counter. Non-atomic multi-write: up to 2 Clerk writes
 * per affected member before the final metadata write, and a member whose
 * public_user_data is None is silently skipped and uncounted.
 */
export const CR_DELETE_OK_200 = variant(200, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ message: z.string().regex(/^Custom role "[\s\S]*" deleted$/), reassignedUsers: z.number().int().nonnegative() }).strict());

/**
 * site functions/org_role_permissions/org_role_permissions.py:565 — Trigger: any
 * Exception NOT matching is_role_not_available_error — including a mid-sweep Clerk
 * failure after partial reassignment — f-string source: f'Failed to delete custom role:
 * {str(e)}'. LEAKS str(e), which here can be a Clerk API error body. Reached on a
 * mid-sweep failure with members already reassigned.
 */
export const CR_DELETE_INTERNAL_ERROR_500 = variant(500, H_GET_PUT_POST_PATCH_DELETE_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to delete custom role: [\s\S]*$/) }).strict());


// ===== functions/org_slug/org_slug.py ====================================
/**
 * Response helper: functions/org_slug/org_slug.py:70 _response(status_code, body) ->
 * headers
 * {'Access-Control-Allow-Headers':'content-type','Access-Control-Allow-Origin':'*','Access-Control-Allow-Methods':'PUT'}
 * ; body json.dumps(body) with the DEFAULT encoder (NO DecimalEncoder — this file defines
 * none).
 */
/**
 * NOTES: Widest status surface in the group (200/400/403/404/409/502/503) and the only
 * handler that mutates an EXTERNAL system (Clerk) before its own datastore. The 502 at
 * :352 is a PARTIAL-SUCCESS status: Clerk holds the new slug while the DynamoDB mirror
 * does not — a contract consumer must NOT model 502 as 'no change'. The 409 is classified
 * by SUBSTRING MATCH on the exception text ('slug' AND one of
 * 'taken'/'exists'/'duplicate'/'422') — fragile in both directions. The 200 body is a
 * discriminated union on `changed`, and `organizationSlug` in it is NOT guaranteed to echo
 * the requested slug (converge-to-Clerk may substitute). No readOnly check on this route —
 * the fine-grained permission is the only write gate. json.dumps here uses the DEFAULT
 * encoder (no DecimalEncoder), which is safe only because every value in every body is a
 * str or bool.
 */

/**
 * site functions/org_slug/org_slug.py:227 — Trigger: resolve_effective_permissions
 * raised anything — PermissionResolutionError, Clerk SDK/network error, SSM failure in
 * _get_clerk(), exhausted 429 retries
 */
export const OS_PERM_UNAVAILABLE_503 = variant(503, H_PUT_LC, z.object({ error: z.literal('Permissions are temporarily unavailable') }).strict());

/**
 * site functions/org_slug/org_slug.py:232 — Trigger: resolve_effective_permissions
 * returned a falsy org_id — authorizer.lambda.provider != 'clerk' or orgId empty. This
 * is the ONLY org-membership gate on this route (there is no separate get_org_from_auth
 * 403).
 */
export const OS_NO_CLERK_403 = variant(403, H_PUT_LC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/org_slug/org_slug.py:235 — Trigger:
 * 'org:settings:manage_organization_settings' not in the resolved permissions —
 * BUG-FOR-BUG: NOTE: this route has NO readOnly (has_write_permissions) check at all,
 * unlike privacy_policy accept/overlay — the fine-grained permission is the sole write
 * gate. — Distinct wording from privacy_policy's equivalent 403 — 'to change the
 * Organization Slug', capitalized. Do not unify the two.
 */
export const OS_PERM_DENIED_403 = variant(403, H_PUT_LC, z.object({ error: z.literal('Organization settings permission required to change the Organization Slug') }).strict());

/**
 * site functions/org_slug/org_slug.py:271 — Trigger: json.loads(event.get('body') or
 * '{}') raised ValueError/TypeError
 */
export const OS_BAD_JSON_400 = variant(400, H_PUT_LC, z.object({ error: z.literal('Request body must be valid JSON') }).strict());

/**
 * site functions/org_slug/org_slug.py:276 (messages from
 * functions/org_slug_utils.py:62,64,66,68,70) — Trigger: validate_slug(slug) returned a
 * non-None message, on slug = (body.get('slug') or '').strip().lower(). Checks in order:
 * not a non-empty str; len < 3; len > 63; not VALID_SLUG_RE ^[a-z0-9]+(?:-[a-z0-9]+)*$;
 * slug in RESERVED_SLUGS (a 48-element frozenset). — BUG-FOR-BUG: `(body.get('slug') or
 * '')` means a non-str truthy slug (e.g. the int 5) survives the `or`, and `.strip()`
 * then raises AttributeError → gateway 500 rather than the 'Organization Slug is
 * required' 400. — The body is one of exactly five literals returned by
 * org_slug_utils.validate_slug — see the SLUG_VALIDATION_ERRORS enum. The two length
 * messages are f-strings over module constants (MIN_SLUG_LENGTH = 3, MAX_SLUG_LENGTH =
 * 63), so they are fully determined and pinned as literals rather than regexes.
 */
export const OS_INVALID_SLUG_400 = variant(400, H_PUT_LC, z.object({ error: SlugValidationError }).strict());

/**
 * site functions/org_slug/org_slug.py:280 — Trigger: _resolve_v2_org(clerk_org_id)
 * returned None — no row on clerkOrganizationId-index — BUG-FOR-BUG: 403 for what is
 * really a 404 (the caller IS authorized; the row is missing). Pinned as-is. — Distinct
 * from the 'No organization membership found' literal used by every other handler in
 * this group — record, not membership. Bug-for-bug: this is a 403 for what is really a
 * 404 (the caller IS authorized; the row is missing).
 */
export const OS_NO_ORG_RECORD_403 = variant(403, H_PUT_LC, z.object({ error: z.literal('No organization record found') }).strict());

/**
 * site functions/org_slug/org_slug.py:284 — Trigger: slug ==
 * str(org.get('organizationSlug') or '').strip() — short-circuits before any Clerk or
 * DynamoDB write — BUG-FOR-BUG: The 200 body shape DIFFERS between the changed and
 * unchanged branches (previousSlug present only when changed=true). A contract must
 * model this as a discriminated union on `changed`, not one optional field. — The
 * changed=false arm of the 200 discriminated union. previousSlug is ABSENT on this
 * branch, and .strict() is what enforces that. Short-circuits before any Clerk or
 * DynamoDB write.
 */
export const OS_UNCHANGED_200 = variant(200, H_PUT_LC, z.object({ organizationSlug: z.string(), changed: z.literal(false), hostedPrivacyUrl: z.string() }).strict());

/**
 * site functions/org_slug/org_slug.py:300 — Trigger: the Clerk organizations.update call
 * raised and _is_duplicate_slug_error(exc) is True — i.e. str(exc).lower() contains
 * 'slug' AND one of 'taken'/'exists'/'duplicate'/'422' — BUG-FOR-BUG: HEURISTIC
 * CLASSIFICATION ON EXCEPTION TEXT. `'422' in text` will also match an unrelated Clerk
 * error whose message merely contains the substring '422' (a request id, a timestamp) as
 * long as 'slug' also appears — a transient failure can be misreported as 409 'already
 * taken'. Conversely a reworded Clerk duplicate error would fall through to the 502. —
 * Contains U+2014 EM DASH, verbatim. Bug-for-bug: this status is chosen by SUBSTRING
 * MATCH on the Clerk exception text (_is_duplicate_slug_error: str(exc).lower() contains
 * 'slug' AND one of 'taken'/'exists'/'duplicate'/'422'), so an unrelated Clerk failure
 * whose message merely contains '422' can be misreported as 409.
 */
export const OS_SLUG_TAKEN_409 = variant(409, H_PUT_LC, z.object({ error: z.literal('That slug is already taken — choose a different one') }).strict());

/**
 * site functions/org_slug/org_slug.py:308 — Trigger: the Clerk organizations.update call
 * raised and _is_duplicate_slug_error is False. Nothing is mirrored — the DynamoDB row
 * is untouched. — Contains U+2014 EM DASH, verbatim. Nothing was mirrored — the DynamoDB
 * row is untouched on this path.
 */
export const OS_CLERK_UPDATE_FAILED_502 = variant(502, H_PUT_LC, z.object({ error: z.literal('Could not update the Organization Slug — try again') }).strict());

/**
 * site functions/org_slug/org_slug.py:352 — Trigger: all THREE mirror update_item
 * attempts raised (with time.sleep(0.1*(attempt+1)) between). **STATE IS SPLIT-BRAIN:
 * Clerk HAS the new slug, DynamoDB does NOT.** Roll-forward-only by design (documented
 * at :312-323). — BUG-FOR-BUG: A 502 here means the write PARTIALLY succeeded — a client
 * must not treat it as 'nothing happened'. The hosted /{slug}/privacy URL 404s until the
 * admin retries or the clerk_webhooks organization.updated mirror converges. — Contains
 * U+2014 EM DASH, verbatim. PARTIAL SUCCESS: Clerk HOLDS the new slug while the DynamoDB
 * mirror does not (roll-forward-only, documented at org_slug.py:312-323). A consumer
 * must NOT model this 502 as 'no change'.
 */
export const OS_MIRROR_UNRESOLVED_502 = variant(502, H_PUT_LC, z.object({ error: z.literal('The slug change did not fully apply — try saving again') }).strict());

/**
 * site functions/org_slug/org_slug.py:393 — Trigger: Clerk update succeeded AND at least
 * one mirror update_item attempt succeeded — BUG-FOR-BUG: `organizationSlug` in the 200
 * is NOT guaranteed to equal the requested slug — the converge-to-Clerk block at
 * :365-391 may substitute Clerk's current value. A client that assumes echo-back is
 * wrong. — The changed=true arm of the 200 discriminated union — previousSlug is
 * REQUIRED here and .strict() plus z.literal(true) is what separates it from
 * OS_UNCHANGED_200. organizationSlug is final_slug, which is NOT guaranteed to equal the
 * requested slug: the converge-to-Clerk block (org_slug.py:365-391) may substitute
 * Clerk's current value. previousSlug is '' when the org had no prior mirror value.
 */
export const OS_CHANGED_200 = variant(200, H_PUT_LC, z.object({ organizationSlug: z.string(), changed: z.literal(true), previousSlug: z.string(), hostedPrivacyUrl: z.string() }).strict());

/**
 * site functions/org_slug/org_slug.py:405 — Trigger: event.requestContext.http.method !=
 * 'PUT'. Only PUT /org-slug is declared (stacks/ApiStack.ts:1193).
 */
export const OS_NON_PUT_404 = variant(404, H_PUT_LC, z.object({ error: z.literal('Not found') }).strict());


// ===== functions/privacy_policy/privacy_policy.py ========================
/**
 * Response helper: functions/privacy_policy/privacy_policy.py:158 _response(status_code,
 * body, methods='POST') -> {'statusCode', 'headers':
 * {'Access-Control-Allow-Headers':'content-type','Access-Control-Allow-Origin':'*','Access-Control-Allow-Methods':
 * methods,'Cache-Control':'no-store'}, 'body': json.dumps(body, cls=DecimalEncoder)}. The
 * `methods` parameter is the ONLY thing that varies between header maps — the other three
 * keys are identical on every response this handler emits.
 */
/**
 * NOTES: BUCKET B — this is the repo's ONLY unauthenticated route (stacks/ApiStack.ts:1188
 * `"GET /public/privacy-policy/{slug}": { function: privacyPolicyFunction, authorizer:
 * "none" }`). Input validation for it is EXACTLY three predicates at :611 — non-empty, len
 * <= MAX_SLUG_LENGTH (63), and VALID_SLUG_RE `^[a-z0-9]+(?:-[a-z0-9]+)*$` (both imported
 * from org_slug_utils). Nothing else about the request is validated; no rate limiting, no
 * origin check (ACAO is '*'). The length cap is load-bearing per the in-code comment:
 * without it a >2048-byte GSI hash key makes DynamoDB throw a ValidationException that
 * surfaces as a 500. Cache-Control: no-store IS present on EVERY response from this
 * lambda, including the public GET (the PE-2048 comment at :651-660 explains why the old
 * public max-age was removed: overlayEnabled is live enforcement state). The public GET's
 * `content` is served RAW to the website; safety depends entirely on sanitize_policy_html
 * having run at accept time — a document written by any OTHER writer of
 * privacyPolicyDocument would bypass that. generate is NOT permission-gated (no readOnly
 * check, no manage_organization_settings) while accept and overlay are both gated on BOTH;
 * this is a real asymmetry a contract must preserve. Finally, get_org_from_auth returns
 * the FLATTENED (flatten_v2_to_v1_compat) row while build_policy_draft's docstring claims
 * it receives the RAW V2 row — flatten happens to preserve the nested `address` map and
 * `registrationContact`, so the merge works, but the docstring is wrong and a future
 * flatten change could silently empty the address line.
 */

/**
 * site functions/privacy_policy/privacy_policy.py:403 — Trigger:
 * get_org_from_auth(event) returns org=None — either
 * requestContext.authorizer.lambda.orgId is empty/absent, or
 * read_organization_v2_by_clerk_org_id found no V2 row (or swallowed a
 * non-Client/BotoCore exception and returned None)
 */
export const PP_GENERATE_NO_ORG_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('No organization membership found') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:410 — Trigger: org resolved; no
 * further gating — generate is READ-ONLY and is NOT gated on readOnly or on
 * org:settings:manage_organization_settings (unlike accept/overlay) — BUG-FOR-BUG:
 * acceptedVersion is DynamoDB Decimal → DecimalEncoder emits a JSON FLOAT (3.0) for a
 * stored version, but a literal int 0 when privacyPolicyDocument is absent. Type is
 * unstable across the two paths. — acceptedVersion is `existing_doc.get('version') or 0`
 * — a DynamoDB Decimal serialized by DecimalEncoder to a JSON FLOAT (e.g. 3.0) when a
 * document exists, or the Python int 0 when it does not. z.number() admits both; do NOT
 * narrow to z.number().int(). draft is the merged policy_template._TEMPLATE HTML;
 * missing merge fields render as the bracketed placeholders '[YOUR ORGANIZATION NAME]',
 * '[YOUR MAILING ADDRESS]', '[YOUR CONTACT EMAIL]', '[YOUR CONTACT PHONE NUMBER]',
 * '[YOUR WEBSITE URL]'. hostedUrl is '' when the org has no slug, else
 * f"https://{website_domain}/{slug}/privacy".
 */
export const PP_GENERATE_OK_200 = variant(200, H_POST_LC_NOSTORE, z.object({ draft: z.string(), hostedUrl: z.string(), organizationSlug: z.string(), missingFields: z.array(PolicyMergeField), privacyPolicyLink: z.string(), acceptedVersion: z.number(), acceptedAt: z.string() }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:424 — Trigger: get_org_from_auth
 * returns org=None
 */
export const PP_ACCEPT_NO_ORG_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('No organization membership found') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:426 — Trigger:
 * has_write_permissions(lam) false, i.e. (lam.get('readOnly','false') or
 * 'false').lower() == 'true'
 */
export const PP_ACCEPT_READONLY_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('You do not have permission to perform this action') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:341 (via
 * require_manage_organization_settings, called at :433) — Trigger:
 * resolve_effective_permissions raised ANY exception — PermissionResolutionError from an
 * inconsistent/ambiguous/mismatched Clerk membership response, a Clerk SDK/network
 * error, an SSM get_parameter failure inside _get_clerk(), or a 429 that exhausted
 * MAX_RETRIES=2
 */
export const PP_ACCEPT_PERM_UNAVAILABLE_503 = variant(503, H_POST_LC_NOSTORE, z.object({ error: z.literal('Permissions are temporarily unavailable') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:346 — Trigger:
 * resolve_effective_permissions returned org_id falsy — get_org_id_and_role saw
 * authorizer.lambda.provider != 'clerk' (returns (None,None)) or an empty orgId.
 * Reachable in practice only for the non-Clerk dualAuth leg, because get_org_from_auth
 * already 403'd on an empty orgId.
 */
export const PP_ACCEPT_NO_CLERK_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:348 — Trigger:
 * 'org:settings:manage_organization_settings' not in the resolved effective permission
 * list
 */
export const PP_ACCEPT_PERM_DENIED_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('Organization settings permission required to publish a privacy policy') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:439 — Trigger:
 * json.loads(event.get('body') or '{}') raised ValueError or TypeError
 */
export const PP_ACCEPT_BAD_JSON_400 = variant(400, H_POST_LC_NOSTORE, z.object({ error: z.literal('Request body must be valid JSON') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:446 (pre-sanitize) and :459
 * (post-sanitize, same literal) — Trigger: len(content.strip()) < 500 where content =
 * body['content'] coerced to '' when not a str; OR, after looks_like_html(content) and
 * sanitize_policy_html(content), len(content.strip()) < 500 — BUG-FOR-BUG: Two distinct
 * code sites emit an IDENTICAL body — a consumer cannot distinguish 'you sent too
 * little' from 'sanitization stripped it below the floor'. — f-string source: f'content
 * must be at least {MIN_POLICY_CHARS} characters' with MIN_POLICY_CHARS = 500 (a module
 * constant, not caller input), so the emitted string is fully determined — z.literal is
 * exact. Two distinct sites emit this byte-identical body (privacy_policy.py:446
 * pre-sanitize, :459 post-sanitize).
 */
export const PP_ACCEPT_TOO_SHORT_400 = variant(400, H_POST_LC_NOSTORE, z.object({ error: z.literal('content must be at least 500 characters') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:450 — Trigger: len(content.strip()) >
 * 25000 — f-string source: f'content must be at most {MAX_POLICY_CHARS} characters' with
 * MAX_POLICY_CHARS = 25000 (module constant), so the string is fully determined.
 */
export const PP_ACCEPT_TOO_LONG_400 = variant(400, H_POST_LC_NOSTORE, z.object({ error: z.literal('content must be at most 25000 characters') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:466 (pre-loop) and :491 (inside the
 * retry loop, IDENTICAL body) — Trigger: hosted_url_for(org) == '' —
 * org.organizationSlug is absent/empty/whitespace. Checked twice: once before the write
 * loop and once per attempt (the retry re-reads the row, so a slug that vanished between
 * attempts also hits it). — BUG-FOR-BUG: Duplicated literal at two sites; the second is
 * only reachable after a ConditionalCheckFailedException retry. — The arrow is U+2192
 * RIGHTWARDS ARROW, verbatim from source. json.dumps defaults to ensure_ascii=True so it
 * goes over the wire as the escape \u2192, but the DECODED body string contains the
 * literal → character — match on the decoded form. Emitted at two sites
 * (privacy_policy.py:466 and :491) with identical bodies.
 */
export const PP_ACCEPT_SLUG_REQUIRED_400 = variant(400, H_POST_LC_NOSTORE, z.object({ error: z.literal('Set your organization username in Settings → Organization before publishing'), code: z.literal('SLUG_REQUIRED') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:556 — Trigger: update_item raised
 * ClientError with Error.Code == 'ConditionalCheckFailedException' AND (attempt == 1,
 * i.e. the retry also failed) OR (attempt == 0 and the re-read
 * read_organization_v2_by_id returned None) — Contains U+2014 EM DASH, verbatim from
 * source.
 */
export const PP_ACCEPT_CONFLICT_409 = variant(409, H_POST_LC_NOSTORE, z.object({ error: z.literal('The privacy policy was updated by someone else — reload and try again') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:560 — Trigger: the conditional
 * update_item succeeded on attempt 0 or attempt 1 — BUG-FOR-BUG: `version` here is a
 * real Python int (prev+1) whereas generate's `acceptedVersion` for the same stored
 * value is a Decimal→float. Same logical field, two JSON types across two routes. —
 * version here IS a true Python int (prev_version + 1 where prev_version =
 * int(existing_doc.get('version') or 0)), so .int() is correct — unlike
 * PP_GENERATE_OK_200.acceptedVersion, which is the same stored field but reaches JSON as
 * a Decimal-derived float. acceptedAt is
 * datetime.datetime.now(datetime.timezone.utc).isoformat().
 */
export const PP_ACCEPT_OK_200 = variant(200, H_POST_LC_NOSTORE, z.object({ hostedUrl: z.string(), version: z.number().int(), acceptedAt: z.string(), privacyPolicyLink: z.string(), privacyPolicyLinkSet: z.boolean() }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:573 — Trigger: get_org_from_auth
 * returns org=None
 */
export const PP_OVERLAY_NO_ORG_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('No organization membership found') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:575 — Trigger:
 * has_write_permissions(lam) is False
 */
export const PP_OVERLAY_READONLY_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('You do not have permission to perform this action') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:341 (via
 * require_manage_organization_settings called at :580) — Trigger: same as
 * PP_ACCEPT_PERM_UNAVAILABLE_503
 */
export const PP_OVERLAY_PERM_UNAVAILABLE_503 = variant(503, H_POST_LC_NOSTORE, z.object({ error: z.literal('Permissions are temporarily unavailable') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:346 — Trigger:
 * resolve_effective_permissions returned a falsy org_id
 */
export const PP_OVERLAY_NO_CLERK_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:348 — Trigger:
 * manage_organization_settings missing — BUG-FOR-BUG: Message says 'to publish a privacy
 * policy' but the caller was toggling the overlay, not publishing. Pinned as-is. —
 * Bug-for-bug: the OVERLAY route reuses the PUBLISH wording
 * (require_manage_organization_settings is shared). Do not reword.
 */
export const PP_OVERLAY_PERM_DENIED_403 = variant(403, H_POST_LC_NOSTORE, z.object({ error: z.literal('Organization settings permission required to publish a privacy policy') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:586 — Trigger: json.loads raised
 * ValueError/TypeError
 */
export const PP_OVERLAY_BAD_JSON_400 = variant(400, H_POST_LC_NOSTORE, z.object({ error: z.literal('Request body must be valid JSON') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:589 — Trigger: not
 * isinstance(body.get('enabled'), bool) — note Python bool check, so 0/1/'true' are all
 * rejected
 */
export const PP_OVERLAY_BAD_ENABLED_400 = variant(400, H_POST_LC_NOSTORE, z.object({ error: z.literal('enabled must be true or false') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:599 — Trigger: unconditional
 * update_item succeeded (no ConditionExpression) — Echoes the request value, which was
 * already isinstance(bool)-validated, so z.boolean() is genuinely closed here.
 */
export const PP_OVERLAY_OK_200 = variant(200, H_POST_LC_NOSTORE, z.object({ privacyOverlayEnabled: z.boolean() }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). The public donor-facing
 * route deliberately returns one indistinguishable 404 for an invalid slug and for a valid
 * slug with no accepted document — an anti-enumeration property. Two variants would make
 * every real 404 AMBIGUOUS and would also misrepresent it as leaking which case occurred.
 * Collapsed sites: functions/privacy_policy/privacy_policy.py:613 (guard condition at
 * :612) (slug = (event.get('pathParameters') or {}).get('slug','') and NOT slug OR
 * len(slug) > MAX_SLUG_LENGTH (63) OR not VALID_SLUG_RE.match(slug) where VALID_SLUG_RE =
 * re.compile(r'^[a-z0-9]+(?:-[a-z0-9]+)*$') from org_slug_utils. This is the FULL
 * unauthenticated input validation for this route.);
 * functions/privacy_policy/privacy_policy.py:618 (guard condition at :617)
 * (_find_org_by_slug(slug) returned None (no row on organizationSlug-index) OR the row's
 * privacyPolicyDocument is not a dict OR document.get('content') is falsy)
 */
export const PP_PUBLIC_GET_NOT_FOUND_404 = variant(404, H_GET_LC_NOSTORE, z.object({ error: z.literal('Not found') }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:642 — Trigger: valid slug + a row on
 * organizationSlug-index + a dict privacyPolicyDocument with truthy content —
 * BUG-FOR-BUG: `version` mixes Decimal→float (stored) with int 1 (fallback). Also: only
 * SIX of the org row's attributes are projected — this route does NOT leak the row
 * (contrast GET /merchant). — BUCKET B — the only unauthenticated 200 in the repo.
 * Exactly seven keys are projected from the org row; this is NOT a passthrough (contrast
 * GET /merchant). version is `document.get('version') or 1` — a Decimal→float via
 * DecimalEncoder when stored, or the int 1 fallback, so z.number() without .int().
 * content is served RAW to the website and is admin-authored HTML; its safety depends
 * entirely on sanitize_policy_html having run at accept time. brandColor is '' or a
 * strict /^#[0-9A-Fa-f]{6}$/ hex; logoUrl is '' or an http(s) URL — both are validated
 * server-side but are still plain strings on the wire.
 */
export const PP_PUBLIC_GET_OK_200 = variant(200, H_GET_LC_NOSTORE, z.object({ organizationName: z.string(), content: z.string(), version: z.number(), acceptedAt: z.string(), brandColor: z.string(), logoUrl: z.string(), overlayEnabled: z.boolean() }).strict());

/**
 * site functions/privacy_policy/privacy_policy.py:676 — Trigger: handler() dispatch fell
 * through all four `if` branches. Practically unreachable through API Gateway (only the
 * four declared routes hit this integration), but it is the ONLY variant emitting
 * Access-Control-Allow-Methods 'GET,POST'. — handler() fallthrough at
 * privacy_policy.py:676. Practically unreachable through API Gateway (only four routes
 * target this integration) but it is the only variant emitting
 * Access-Control-Allow-Methods 'GET,POST'.
 */
export const PP_DISPATCH_NOT_FOUND_404 = variant(404, H_GET_POST_LC_NOSTORE, z.object({ error: z.literal('Not found') }).strict());


// ===== functions/stripe_account_email/stripe_account_email.py ============
/**
 * Response helper: functions/stripe_account_email/stripe_account_email.py:120
 * _response(code, body) -> {"statusCode": code, "headers": {**CORS_HEADERS,
 * "Content-Type": "application/json"}, "body": json.dumps(body)} — plain json.dumps, no
 * custom encoder
 */
/**
 * NOTES: FINDING (rule 4): the 200 `email` is a Stripe-provider string with zero
 * validation, and the 500 `error` is `str(e)`. FINDING (most important in this file):
 * _load_stripe_keys() failure yields 200 {"email":""}, NOT an error — a hard
 * misconfiguration masquerades as success. Any contract that models the 200 as 'org has no
 * Stripe email' is WRONG.
 */

/**
 * site functions/stripe_account_email/stripe_account_email.py:71 — Trigger: method ==
 * "OPTIONS" — BUG-FOR-BUG: No Content-Type; bypasses _response. Only GET is routed
 * (ApiStack.ts:1304). — 204 with `"body": ""` (stripe_account_email.py:71).
 */
export const ACCOUNT_EMAIL_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_GET_OPTIONS_UC);

/**
 * site functions/stripe_account_email/stripe_account_email.py:75 — Trigger:
 * _get_clerk_org_id returns None — provider != "clerk" or orgId absent (:112-117,
 * returns the raw lam.get("orgId")) — BUG-FOR-BUG: Handler-produced 403. —
 * stripe_account_email.py:75 — hardcoded literal, SHORT form.
 */
export const ACCOUNT_EMAIL_NO_ORG_CONTEXT_403 = variant(403, H_GET_OPTIONS_UC_JSON, z.object({ error: z.literal('No org context') }).strict());

/**
 * site functions/stripe_account_email/stripe_account_email.py:79 — Trigger: zero Items
 * from the clerkOrganizationId-index query — BUG-FOR-BUG: Like management_session,
 * _get_org_by_clerk_org_id (:101) has NO ORGANIZATIONS_TABLE guard — an unset env raises
 * out instead of yielding this 404. — stripe_account_email.py:79 — hardcoded literal.
 */
export const ACCOUNT_EMAIL_ORG_NOT_FOUND_404 = variant(404, H_GET_OPTIONS_UC_JSON, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). {email: ''} is admitted
 * by {email: z.string()}. The empty arm is emitted for THREE different reasons (no org,
 * Stripe keys unloadable, Stripe returned no email) and is byte-identical to a real lookup
 * returning an empty email — a hard misconfiguration is indistinguishable from success.
 * Pinned as one variant; see openapi.yaml x-deviations. Collapsed sites:
 * functions/stripe_account_email/stripe_account_email.py:95 (stripe.Account.retrieve()
 * succeeded); functions/stripe_account_email/stripe_account_email.py:87 (`if not
 * stripe_account_id or not _load_stripe_keys()` (:86) — TWO distinct causes collapsed into
 * one response: (a) the org has no Stripe account id, or (b) Stripe is not configured at
 * all (missing STRIPE_SECRET_KEY_PATH env, or the SSM read raised).)
 */
export const ACCOUNT_EMAIL_200 = variant(200, H_GET_OPTIONS_UC_JSON, z.object({ email: z.string() }).strict());

/**
 * site functions/stripe_account_email/stripe_account_email.py:98 — Trigger: ANY
 * exception out of the try at :89 — bare `except Exception as e` at :96. NO
 * stripe.error.* clauses in the file. — BUG-FOR-BUG: All Stripe error classes collapse
 * to 500 with the raw provider message in the body. This is the ONLY 500 in the file, so
 * unlike management_session it is at least unambiguous by status. —
 * stripe_account_email.py:98 `{'error': str(e)}` — UNVALIDATED Stripe/runtime error
 * string. Bare `except Exception` at :96; no typed Stripe clauses.
 */
export const ACCOUNT_EMAIL_STRIPE_ERROR_500_LEAKS_PROVIDER_STRING = variant(500, H_GET_OPTIONS_UC_JSON, z.object({ error: z.string() }).strict());


// ===== functions/stripe_account_status/stripe_account_status.py ==========
/**
 * Response helper: null if none — this handler has NO response helper; every return is an
 * inline dict literal built from module-level CORS_HEADERS (:48-52)
 */
/**
 * NOTES: FINDING (brief rule 4 — unvalidated values reaching the response): the 200 body
 * is assembled from raw DynamoDB attributes with `.get(k, <default>)` only.
 * `stripeAccountId`, `defaultPaymentProcessor`, `detailsSubmitted`, `chargesEnabled`,
 * `payoutsEnabled`, `stripeConnectComplete`, `taxIdProvided` are NOT type-checked — a
 * boolean field stored as the string "true" or a Decimal serializes as that, not as a
 * bool. `requirementsCurrentlyDue` / `requirementsPendingVerification` come from the
 * Stripe provider verbatim (list(...) of provider strings) with no validation. A contract
 * MUST NOT narrow these to strict primitives without documenting that as tightening.
 * `einOnFile` IS derived (bool of a stripped string) and is therefore genuinely boolean.
 * Also note the DecimalEncoder converts Decimal to FLOAT (not string) — a pinned contract
 * must model that.
 */

/**
 * site functions/stripe_account_status/stripe_account_status.py:126 — Trigger:
 * event.requestContext.http.method == "OPTIONS" — BUG-FOR-BUG: 204 with a body key
 * present but empty string; no Content-Type header on this variant (unlike every other
 * variant in this file). NOTE: route is registered as `GET /stripe/account-status` only
 * (ApiStack.ts:1295) — there is no OPTIONS route, so this branch is likely unreachable
 * through the HTTP API unless a CORS/OPTIONS catch-all routes to it. — 204 with `"body":
 * ""` (stripe_account_status.py:126) — empty string, not JSON. Bypasses no helper (this
 * file has none) and emits CORS_HEADERS without Content-Type.
 */
export const ACCOUNT_STATUS_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_GET_OPTIONS_UC);

/**
 * site functions/stripe_account_status/stripe_account_status.py:130-134 — Trigger:
 * _get_org_identity(event) returns falsy — i.e. authorizer.lambda.provider != "clerk",
 * OR provider=="clerk" but lambda.orgId is missing/empty. Cognito callers ALWAYS land
 * here (:120-121 comment 'Cognito fallback not needed'). — BUG-FOR-BUG: HANDLER-produced
 * 403 (not authorizer-produced) — distinct from the gateway {"message":"Unauthorized"}.
 * Note this is 403 for what is effectively a missing-identity condition. —
 * stripe_account_status.py:133 — hardcoded literal. Note the sibling handlers use the
 * SHORTER string 'No org context'; do not unify.
 */
export const ACCOUNT_STATUS_NO_ORG_CONTEXT_403 = variant(403, H_GET_OPTIONS_UC_JSON, z.object({ error: z.literal('No organization context') }).strict());

/**
 * site functions/stripe_account_status/stripe_account_status.py:139-143 — Trigger:
 * _get_org_by_clerk_org_id(clerk_org_id) returns None — either ORGANIZATIONS_TABLE env
 * unset (:95 guard returns None WITHOUT querying), or the clerkOrganizationId-index
 * query returns zero Items. — BUG-FOR-BUG: A MISSING ORGANIZATIONS_TABLE env var is
 * indistinguishable from a genuinely absent org — both produce this 404.
 * Misconfiguration silently presents as 'Organization not found'. —
 * stripe_account_status.py:142 — hardcoded literal.
 */
export const ACCOUNT_STATUS_ORG_NOT_FOUND_404 = variant(404, H_GET_OPTIONS_UC_JSON, z.object({ error: z.literal('Organization not found') }).strict());

/** Both populated Stripe account-id locations disagree; the handler refuses to choose one. */
export const ACCOUNT_STATUS_IDENTITY_CONFLICT_409 = variant(409, H_GET_OPTIONS_UC_JSON, z.object({ error: z.literal('Stripe account identity needs reconciliation') }).strict());

/**
 * site functions/stripe_account_status/stripe_account_status.py:185-189 (body built
 * :152-183) — Trigger: Always, once org lookup succeeds. This is the ONLY success shape;
 * the live-Stripe block only mutates fields in place. — BUG-FOR-BUG: A Stripe failure is
 * NOT an error response: the except at :181-183 swallows EVERY exception (bare `except
 * Exception`), prints `stripe_account_retrieve_failed: {e}`, sets
 * stripeApiAvailable=False, and still returns 200 with the DynamoDB-derived values.
 * Callers cannot distinguish 'Stripe down' from 'live not requested' — both give
 * stripeApiAvailable:false. Also: the live block PARTIALLY mutates response_data before
 * raising, so a mid-block failure can return a MIXED record (e.g.
 * detailsSubmitted/chargesEnabled/payoutsEnabled taken from Stripe, requirements* still
 * the DynamoDB defaults, stripeApiAvailable reset to False at :183). —
 * stripe_account_status.py:152-164. Key set IS closed (a fixed dict literal, only
 * mutated in place at :173-180) so .strict() is correct. The overlay's final capability
 * coercion is `response_data[capability] = response_data[capability] is True`; therefore
 * detailsSubmitted, chargesEnabled, and payoutsEnabled are always booleans on the wire.
 * stripeAccountId, stripeConnectComplete, defaultPaymentProcessor, and taxIdProvided retain
 * their honest DynamoDB-derived widths. stripeApiAvailable and einOnFile are code-derived booleans.
 * requirements* are provider string lists (list() of Stripe values) and are [] unless
 * the live path ran. Serialized with cls=DecimalEncoder which renders Decimal as FLOAT
 * (not string), so any numeric passthrough is a JSON number.
 */
export const ACCOUNT_STATUS_OK_200 = variant(200, H_GET_OPTIONS_UC_JSON, z.object({ stripeAccountId: z.unknown(), detailsSubmitted: z.boolean(), chargesEnabled: z.boolean(), payoutsEnabled: z.boolean(), stripeConnectComplete: z.unknown(), defaultPaymentProcessor: z.unknown(), stripeApiAvailable: z.boolean(), taxIdProvided: z.unknown(), einOnFile: z.boolean(), requirementsCurrentlyDue: z.array(z.string()), requirementsPastDue: z.array(z.string()), requirementsPendingVerification: z.array(z.string()), requirementsDisabledReason: z.string().nullable(), requirementsCurrentDeadline: z.number().int().nullable(), lastCheckedAt: z.string().nullable(), lastWebhookEventAt: z.string().nullable() }).strict());


// ===== functions/stripe_management_session/stripe_management_session.py ==
/**
 * Response helper: functions/stripe_management_session/stripe_management_session.py:176
 * _response(code, body) -> {"statusCode": code, "headers": {**CORS_HEADERS,
 * "Content-Type": "application/json"}, "body": json.dumps(body)} — plain json.dumps, NO
 * custom encoder (a Decimal in body would raise TypeError)
 */
/**
 * NOTES: FINDING (rule 4): both 500 bodies are `{"error": ...}` and one carries an
 * arbitrary provider string; a contract cannot pin the 500 body to a literal. FINDING:
 * this file has NO ORGANIZATIONS_TABLE guard, so misconfiguration escapes as a gateway 500
 * rather than the 404 its siblings return. FINDING: no method guard beyond OPTIONS.
 */

/**
 * site functions/stripe_management_session/stripe_management_session.py:102 — Trigger:
 * method == "OPTIONS" — BUG-FOR-BUG: Bypasses _response — this is the ONLY variant in
 * the file without Content-Type. Only POST is routed (ApiStack.ts:1301) so likely
 * unreachable. — 204 with `"body": ""` (stripe_management_session.py:102) — the only
 * variant in that file that bypasses _response, hence no Content-Type.
 */
export const MANAGEMENT_SESSION_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC);

/**
 * site functions/stripe_management_session/stripe_management_session.py:106 — Trigger:
 * _get_clerk_org_id returns None — provider != "clerk" (:171-173 has NO else other than
 * `return None`) or orgId key absent — BUG-FOR-BUG: This helper returns
 * `lam.get("orgId")` UNWRAPPED (:172) — unlike account_status/:119 which does `or ""`.
 * So an orgId of None and an orgId of "" are both falsy and both 403, but an orgId of
 * e.g. 0 would also 403. Handler-produced 403. — stripe_management_session.py:106 —
 * hardcoded literal, the SHORT form ('org', not 'organization').
 */
export const MANAGEMENT_SESSION_NO_ORG_CONTEXT_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('No org context') }).strict());

/**
 * site functions/stripe_management_session/stripe_management_session.py:110 — Trigger:
 * the clerkOrganizationId-index query returned zero Items — BUG-FOR-BUG: UNLIKE
 * account_status and account_session, this file's _get_org_by_clerk_org_id (:157) has NO
 * guard on ORGANIZATIONS_TABLE being set — it calls dynamodb.Table("") directly. An
 * unset env therefore raises out of the handler (gateway 500) here instead of producing
 * this 404. Divergent behavior for the same misconfiguration across siblings. —
 * stripe_management_session.py:110 — hardcoded literal.
 */
export const MANAGEMENT_SESSION_ORG_NOT_FOUND_404 = variant(404, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). {client_secret: ''} is
 * admitted by {client_secret: z.string()}. Same containment as ACCOUNT_EMAIL_200.
 * Collapsed sites: functions/stripe_management_session/stripe_management_session.py:151
 * (stripe.AccountSession.create() succeeded);
 * functions/stripe_management_session/stripe_management_session.py:118 (stripe_account_id
 * falsy (neither stripeDetails.accountId nor stripeAccountID present))
 */
export const MANAGEMENT_SESSION_CLIENT_SECRET_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ client_secret: z.string() }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (2 code paths, one wire shape). The not-configured
 * arm's literal 'Stripe not configured' is admitted by the sibling's bare {error: str(e)}
 * z.string(). Separable only by string equality, which is not a structural discriminator.
 * Collapsed sites: functions/stripe_management_session/stripe_management_session.py:154
 * (ANY exception out of the try at :123 — bare `except Exception as e` at :152. NO
 * stripe.error.* clauses exist in this file.);
 * functions/stripe_management_session/stripe_management_session.py:121
 * (_load_stripe_keys() returns False (STRIPE_SECRET_KEY_PATH unset, or the primary SSM
 * get_parameter raised))
 */
export const MANAGEMENT_SESSION_ERROR_500 = variant(500, H_POST_OPTIONS_UC_JSON, z.object({ error: z.string() }).strict());
export const MANAGEMENT_SESSION_PERMISSIONS_UNAVAILABLE_503 = variant(503, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Processor permissions are temporarily unavailable') }).strict());
export const MANAGEMENT_SESSION_PERMISSION_DENIED_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Manage processor settings permission required') }).strict());
export const MANAGEMENT_SESSION_IDENTITY_CONFLICT_409 = variant(409, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Stripe account identity needs reconciliation') }).strict());


// ===== functions/stripe_subscription_webhook/stripe_subscription_webhook.py
/**
 * Response helper:
 * functions/stripe_subscription_webhook/stripe_subscription_webhook.py:743
 * `_response(code, body)` -> {statusCode: code, headers: {**CORS_HEADERS, 'Content-Type':
 * 'application/json'}, body: json.dumps(body)}. NOTE the DIFFERENCE from the sibling
 * handler: this one ADDS 'Content-Type': 'application/json' to the CORS map (4 headers,
 * not 3).
 */
/**
 * NOTES: BUCKET C. KEY FINDINGS: (1) NO EMF metric emissions — `git grep -n -E
 * '_aws|CloudWatchMetrics|put_metric_data|EMF' <SHA> --
 * functions/stripe_subscription_webhook functions/product_analytics_events.py
 * functions/plan_catalog_engine.py` returns nothing for these files. Observability is
 * python `logging` (logger at :54-55) plus, in plan_catalog_engine, four LITERAL LOG
 * MARKERS keyed by a CloudWatch MetricFilter: 'ENTITLEMENT_ENGINE_FALLBACK',
 * 'ENTITLEMENT_ENGINE_ABSENT', 'ENTITLEMENT_ENGINE_BROKEN', 'ENTITLEMENT_ENGINE_VERSION'
 * (plan_catalog_engine.py:44-47). These are LOG-STRING metrics, not EMF, and a write-verb
 * grep cannot find them either — they emit at cold start (:94) and on every lazy-import
 * failure (:109, :129). (2) SILENT-DROP-WITH-200 IS THE DOMINANT FAILURE MODE. Every one
 * of the 5 handlers returns None on a mismatch and the caller emits SSW_OK_200. Enumerated
 * drop sites: not _is_ours (:430, :551, :600, :623, :706); mode=='payment' (:435-437);
 * missing organization_id (:440-442, :554, :603, :626, :709); unknown plan_id with the
 * engine present (:466-468); billing_reason=='subscription_create' (:559-560); missing
 * invoice.subscription (:536, :587); unreadable Stripe key (:539, :590); Stripe retrieve
 * failure (:547-549, :596-598). 15 distinct silent-drop paths, all indistinguishable from
 * a real write on the wire. (3) SSW_HANDLER_ERROR_500 LEAKS str(e) INTO THE RESPONSE BODY,
 * including an organization_id via the RuntimeError at :462. (4) HEADER ASYMMETRY: the 204
 * preflight emits 3 headers, every other response emits 4 (Content-Type added by _response
 * at :746). The sibling stripe_webhooks handler emits 3 on ALL responses — the two Stripe
 * webhook routes do NOT share a header contract. (5) FOUR distinct 400 bodies on one route
 * ('Invalid signature', 'Invalid payload', 'Invalid JSON body', 'Event body is not a JSON
 * object'). (6) `_write_sub` is an UNGUARDED read-modify-write of the whole billingDetails
 * map with a non-consistent read — lost-update race between concurrent Stripe deliveries,
 * and no event-id idempotency anywhere (history entries duplicate on Stripe retry). (7)
 * customer.subscription.deleted DELETES
 * billingPeriod/stripeSubscriptionId/stripeCustomerId/cancelAt from billingDetails by
 * omitting them from `details` (:714-719) — the pop-then-reset mechanism makes omission
 * destructive. (8) construct_event's return value is DISCARDED at :763; the handler
 * re-parses the body itself at :772. (9) 2 DynamoDB write sites, 2 read sites, 3
 * EventBridge publishes, 1 outbound HMAC POST, 2 Stripe API reads, 1 SSM read helper
 * serving 3 paths.
 */

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:753 —
 * Trigger: event.get('requestContext', {}).get('http', {}).get('method') == 'OPTIONS'
 * (:752). Before signature verification. — BUG-FOR-BUG: HEADER ASYMMETRY PINNED: the 204
 * emits 3 headers while every _response()-produced status emits 4 (adds Content-Type). A
 * schema must not model one header map for this route. —
 * stripe_subscription_webhook.py:753 returns {'statusCode': 204, 'headers':
 * CORS_HEADERS, 'body': ''}. This branch BYPASSES _response, so it emits 3 headers while
 * every other response on this route emits 4 (Content-Type added at :746).
 */
export const SSW_OPTIONS_PREFLIGHT = emptyBodyVariant(204, ANY_HEADERS);

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:760 —
 * Trigger: _get_secret(STRIPE_WEBHOOK_SECRET_PATH) returned falsy (:758). Causes: env
 * STRIPE_SUBSCRIPTION_WEBHOOK_SECRET_PATH empty/unset -> `if not path: return None`
 * (:90-91); or ssm.get_parameter raised -> logger.error(f'SSM read failed for {path}:
 * {e}') and return None (:99-101). 300s TTL cache at :93. —
 * stripe_subscription_webhook.py:760. Verbatim string DIFFERS from the sibling handler's
 * 'Webhook not configured' at stripe_webhooks.py:844.
 */
export const SSW_WEBHOOK_SECRET_NOT_CONFIGURED_503 = variant(503, H_POST_OPTIONS_UC_JSON_2, z.object({ error: z.literal('Webhook secret not configured') }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:766 —
 * Trigger: `stripe.Webhook.construct_event(body, sig_header, secret)` (:763) raised
 * stripe.error.SignatureVerificationError, caught :764. body = event.get('body','') or
 * '' (:755); sig_header = (event.get('headers') or {}).get('stripe-signature','')
 * (:756). Missing signature header -> '' -> this variant. Logs logger.warning(f'Stripe
 * signature verification failed: {e}'). — BUG-FOR-BUG: NOTE: construct_event's RETURN
 * VALUE IS DISCARDED at :763 — the verified event object is thrown away and the handler
 * re-parses the raw body itself with json.loads at :772. Functionally equivalent for a
 * verified body, but it means the handler operates on its own parse, not on Stripe's. —
 * stripe_subscription_webhook.py:766. Same literal as the sibling handler's 400, but the
 * header map differs (4 keys here vs 3 there) — do not share one response schema across
 * the two routes.
 */
export const SSW_SIGNATURE_VERIFICATION_FAILED_400 = variant(400, H_POST_OPTIONS_UC_JSON_2, z.object({ error: z.literal('Invalid signature') }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:769 —
 * Trigger: construct_event raised any NON-SignatureVerificationError exception, caught
 * by `except Exception as e` (:767). Logs logger.error(f'Webhook parse error: {e}').
 * Distinct body from SSW_SIGNATURE_VERIFICATION_FAILED_400 despite the same status. —
 * stripe_subscription_webhook.py:769, the non-SignatureVerificationError branch at :767.
 */
export const SSW_INVALID_PAYLOAD_400 = variant(400, H_POST_OPTIONS_UC_JSON_2, z.object({ error: z.literal('Invalid payload') }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:775 —
 * Trigger: `json.loads(body) if isinstance(body, str) else body` (:772) raised
 * json.JSONDecodeError, caught :773. Logs logger.error(f'JSON parse failed after
 * signature verify: {e}'). PRACTICALLY UNREACHABLE — construct_event at :763 already
 * parses the same body and would have produced SSW_INVALID_PAYLOAD_400 first. Pinned as
 * a third distinct 400 body regardless. — BUG-FOR-BUG: Third 400 with a third distinct
 * message on one route. Dead-ish path but part of the wire contract. —
 * stripe_subscription_webhook.py:775. PRACTICALLY UNREACHABLE — construct_event at :763
 * parses the same body first and would emit SSW_INVALID_PAYLOAD_400. Pinned anyway: it
 * is part of the declared wire surface and a future SDK change could expose it.
 */
export const SSW_INVALID_JSON_BODY_400 = variant(400, H_POST_OPTIONS_UC_JSON_2, z.object({ error: z.literal('Invalid JSON body') }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:777 —
 * Trigger: `not isinstance(stripe_event, dict)` (:776) — the verified body parsed to a
 * JSON array, string, number, boolean or null. FOURTH distinct 400 body on this route. —
 * stripe_subscription_webhook.py:777 when the verified body parses to a JSON
 * array/string/number/boolean/null. Fourth distinct 400 literal on this one route.
 */
export const SSW_EVENT_BODY_NOT_OBJECT_400 = variant(400, H_POST_OPTIONS_UC_JSON_2, z.object({ error: z.literal('Event body is not a JSON object') }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:782 —
 * Trigger: `fn = EVENT_HANDLERS.get(event_type)` (:780) returned None — event_type is
 * not one of the 5 registered keys. — BUG-FOR-BUG: FINDING (brief rule 4): `event_type`
 * reaches the response body UNVALIDATED from caller input. The value is
 * signature-verified (an attacker cannot forge it without the signing secret) but it is
 * NOT narrowed to any enum, NOT length-capped, and NOT sanitized. A contract must type
 * it as an arbitrary string, not as the 5-value enum. —
 * stripe_subscription_webhook.py:782. RULE 4 FINDING: event_type is
 * stripe_event.get('type', '') (:779) echoed straight back. It is signature-verified
 * (unforgeable without the signing secret) but NEVER narrowed to an enum, length-capped,
 * or sanitized — and the '' default means the empty string is a valid value here.
 * Narrowing it to the 5-value dispatch enum would be WRONG, since this branch is
 * precisely the one where the value is NOT in that enum.
 *
 * F3 REPAIR (critic finding, UPHELD): `event_type` is NOT necessarily a string. The
 * `''` default at :779 fires ONLY when the `type` key is ABSENT; a PRESENT non-string
 * `type` passes through untouched. The guard at :776 constrains the ENVELOPE to a dict
 * and says nothing about the type of `stripe_event['type']`. So a signature-verified
 * body of `{"type": 123}` reaches :780, `EVENT_HANDLERS.get(123)` returns None, and
 * :782 serializes `{"status": "ignored", "event_type": 123}` — which `z.string()`
 * rejects. Reachable only with the signing secret (Stripe itself, or a compromised
 * secret), but bug-for-bug pinning does not exempt reachable shapes.
 *
 * The admitted set is EXACTLY the HASHABLE JSON scalars — string | number | boolean |
 * null — and deliberately NOT `z.unknown()`. A JSON array or object `type` is
 * UNHASHABLE, so `EVENT_HANDLERS.get(event_type)` at :780 raises `TypeError:
 * unhashable type` BEFORE the ignored branch is reached. That raise is OUTSIDE the
 * `try` at :784 (which wraps only `fn(stripe_event)`), so it escapes the handler as a
 * Lambda fault — there is no 200 envelope at all. Pinning `z.unknown()` would admit
 * two wire shapes the source provably cannot emit, and an over-wide pin is as much a
 * defect as an over-narrow one.
 */
export const SSW_EVENT_TYPE_IGNORED_200 = variant(200, H_POST_OPTIONS_UC_JSON_2, z.object({ status: z.literal('ignored'), event_type: z.union([z.string(), z.number(), z.boolean(), z.null()]) }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:788 —
 * Trigger: `fn(stripe_event)` (:785) raised anything, caught by `except Exception as e`
 * (:786). Sources: KeyError from `stripe_event['data']['object']` (:427, :529, :620,
 * :703 — direct subscript, no .get); the deliberate `raise RuntimeError(f'cannot
 * validate plan_id {plan_id!r}: entitlements engine unavailable; failing loud so Stripe
 * retries (org={organization_id})')` at :462-465; any DynamoDB failure in _read_sub
 * (:211 get_item) or _write_sub (:226 get_item, :241 update_item); a NoneType error if
 * env ORGANIZATIONS_TABLE is unset (module-level `table = ... if ORGANIZATIONS_TABLE
 * else None` at :76 makes `table` None -> AttributeError on .get_item). Logs
 * logger.exception(f'handler error for {event_type}: {e}'). — BUG-FOR-BUG: INFORMATION
 * DISCLOSURE PINNED: str(e) is echoed verbatim in the response body. The RuntimeError at
 * :462 embeds the organization_id, so an engine outage returns an internal org id to
 * Stripe. Also embeds boto3/botocore error text (which can name the table) on a DynamoDB
 * failure. A contract must model the body as an open string with the fixed 'Handler
 * error: ' prefix, not a fixed literal. — stripe_subscription_webhook.py:788. f-string
 * source: f'Handler error: {str(e)}'. OPEN string behind a fixed prefix — never a
 * literal. str(e) is the raw exception text and LEAKS INTERNALS: the deliberate
 * RuntimeError at :462-465 embeds an organization_id, and a boto3 fault embeds botocore
 * text that can name the DynamoDB table. The regex is deliberately unanchored at the
 * tail because the suffix is unbounded (str(e) may contain newlines).
 */
export const SSW_HANDLER_ERROR_500 = variant(500, H_POST_OPTIONS_UC_JSON_2, z.object({ error: z.string().regex(/^Handler error: /) }).strict());

/**
 * site functions/stripe_subscription_webhook/stripe_subscription_webhook.py:790 —
 * Trigger: A registered handler ran to completion without raising. CRITICAL: this SAME
 * response is emitted whether the handler wrote to DynamoDB or returned early doing
 * nothing. All 5 handlers have silent early-return paths (see the effects/notes below) —
 * the 200 body carries ZERO signal about whether any effect occurred. — BUG-FOR-BUG: No
 * `changes` flag (unlike the sibling handler's {"received","changes"} shape). A no-op
 * ignore and a full plan write are indistinguishable on the wire. —
 * stripe_subscription_webhook.py:790. Here event_type IS narrowable to the 5-key
 * dispatch enum, because reaching this line required EVENT_HANDLERS.get(event_type) to
 * be non-None (:780-781) — that is the only place in either handler where the type is
 * genuinely closed. NOTE the body carries no `changes` flag: a full plan write and any
 * of the 15 silent-drop paths produce a BYTE-IDENTICAL response.
 */
export const SSW_OK_200 = variant(200, H_POST_OPTIONS_UC_JSON_2, z.object({ status: z.literal('ok'), event_type: SubscriptionEventType }).strict());


// ===== functions/stripe_webhooks/stripe_webhooks.py ======================
/**
 * Response helper: functions/stripe_webhooks/stripe_webhooks.py:677
 * `_ok_response(changes=False)` -> {statusCode:200, headers: CORS_HEADERS, body:
 * json.dumps({"received": True, "changes": changes})};
 * functions/stripe_webhooks/stripe_webhooks.py:685 `_error_response(status, message)` ->
 * {statusCode: status, headers: CORS_HEADERS, body: json.dumps({"error": message})}.
 * Neither helper sets Content-Type — the emitted header map is EXACTLY the 3 CORS keys, no
 * more.
 */
/**
 * NOTES: BUCKET C — response variants AND effect variants are both keyed by (event type x
 * signature outcome x payload branch). KEY FINDINGS: (1) NO EMF metric emissions anywhere
 * in this handler or its imports — `git grep -n -E
 * '_aws|CloudWatchMetrics|put_metric_data|EMF' <SHA> -- functions/stripe_webhooks
 * functions/apple_pay_utils.py functions/stripe_requirement_utils.py
 * functions/product_analytics_events.py` returns ONE hit and it is an unrelated code
 * comment in org_utils.py:65. All observability here is plain `print()` of unstructured
 * f-strings. (2) The handler DOCSTRING at :834 is WRONG about org-not-found (says 500,
 * code returns 200 at :740). (3) FIVE distinct semantic outcomes emit the byte-identical
 * body {"received":true,"changes":false} — org-not-found, no-state-change,
 * v2-missing-account-id, classic-missing-data-object, unknown-event-type. Only the log
 * line differs. (4) `changes:false` does NOT imply zero writes: the telemetry update_item
 * at :797 fires unconditionally on any org-matched event. (5) The :392 whole-map
 * onboardingDetails replace has NO ConditionExpression and the `has_nested_step` variable
 * computed at :369 is DEAD — a lost-update race exists there. (6) 10 write sites total on
 * ORGANIZATIONS_TABLE, plus an UNBOUNDED FULL TABLE SCAN at :195 on every single webhook
 * delivery (no FilterExpression, no ProjectionExpression, no GSI). (7) org_utils.py is NOT
 * imported by this handler — `import` grep shows constants, stripe_requirement_utils,
 * apple_pay_utils only; ApiStack copyFiles (:952-956) confirms only those three are
 * bundled. (8) functions/stripe_webhooks/constants.py is a git SYMLINK (blob content = the
 * literal string `../create_org/constants.py`); the real content is
 * functions/create_org/constants.py, copied in by ApiStack:953. (9) NO idempotency
 * middleware — replay protection is entirely (a) the state-diff comparisons at
 * :752/:757/:762 and (b) the single ConditionExpression at :383. Stripe's event id is
 * never recorded or checked.
 */

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:838 — Trigger:
 * event.get("requestContext", {}).get("http", {}).get("method") == "OPTIONS" (line 837).
 * Checked BEFORE the signature verification — an OPTIONS request is never
 * signature-checked. — BUG-FOR-BUG: 204 With a body key present and set to "" — API
 * Gateway HTTP API tolerates it. Also: the route is declared POST-only in ApiStack (line
 * 1290), so this OPTIONS branch is only reachable via the API's automatic CORS/OPTIONS
 * handling if configured; the branch is pinned as written regardless. —
 * stripe_webhooks.py:838 returns {"statusCode": 204, "headers": CORS_HEADERS, "body":
 * ""} — the body key is present and set to the empty string, which is NOT JSON. Emits
 * the bare 3-key CORS_HEADERS map (no Content-Type).
 */
export const SW_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC_2);

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:844 — Trigger: `secret =
 * _get_webhook_secret()` (line 841) returned falsy. Two causes: (a) env
 * STRIPE_WEBHOOK_SECRET_PATH unset/empty -> _get_webhook_secret returns None at :145
 * after printing "STRIPE_WEBHOOK_SECRET_PATH not configured"; (b) ssm.get_parameter
 * raised -> returns None at :154 after printing f"webhook_secret_load_failed: {e}".
 * NOTE: the 5-min TTL cache at :139 means a previously-loaded secret is reused; the
 * empty-string secret would be falsy on the `if not secret` check at :842 but
 * TRUTHY-cached is impossible since `_webhook_secret is not None` gates the cache — an
 * SSM value of "" would be cached and then fail this check every time, permanently
 * 503-ing until cold start. — stripe_webhooks.py:844 via _error_response(503, 'Webhook
 * not configured'). Distinct from the sibling handler's 'Webhook secret not configured'
 * — do not unify the two.
 */
export const SW_WEBHOOK_SECRET_NOT_CONFIGURED_503 = variant(503, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Webhook not configured') }).strict());

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:853 — Trigger:
 * `stripe.Webhook.construct_event(raw_body, sig_header, secret)` at line 850 raised
 * `stripe.error.SignatureVerificationError`, caught at line 851. raw_body =
 * event.get("body", "") (:846); sig_header = event.get("headers",
 * {}).get("stripe-signature", "") (:847). A MISSING stripe-signature header yields ""
 * which construct_event rejects as SignatureVerificationError -> this variant, NOT a
 * separate 401. — BUG-FOR-BUG: No 401 exists on this handler — an unsigned/forged
 * request gets 400, not 401/403. Also: `event.get("headers", {})` is NOT case-normalized
 * in the handler; API Gateway HTTP API v2 lowercases header names so `stripe-signature`
 * matches, but a direct Lambda invoke with `Stripe-Signature` would miss and fall into
 * this same 400. — stripe_webhooks.py:853. Reached for a forged AND for an absent
 * stripe-signature header (the '' default at :847 fails construct_event). There is no
 * 401/403 on this route.
 */
export const SW_SIGNATURE_VERIFICATION_FAILED_400 = variant(400, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Invalid signature') }).strict());

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:856 — Trigger:
 * `stripe.Webhook.construct_event` raised ANY non-SignatureVerificationError exception,
 * caught by the bare `except Exception as e` at line 854. Reached when raw_body is not
 * valid JSON (ValueError from the SDK's payload parse), when raw_body is bytes/None in
 * an unexpected shape, or on any SDK internal error. DISTINCT MESSAGE from
 * SW_SIGNATURE_VERIFICATION_FAILED_400 — same status, different body string, so the pair
 * must be modeled as two variants not one. — BUG-FOR-BUG: Two different 400 bodies on
 * the same route are only distinguishable by the `error` string; a schema that collapses
 * them loses the distinction. — stripe_webhooks.py:856, the bare `except Exception` at
 * :854. Same 400 status as SW_SIGNATURE_VERIFICATION_FAILED_400 but a DIFFERENT literal
 * — the two are separable on the wire only by this string, so they must stay two
 * schemas.
 */
export const SW_VERIFICATION_GENERIC_ERROR_400 = variant(400, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Verification failed') }).strict());

/**
 * COLLAPSED WIRE-IDENTICAL VARIANT (5 code paths, one wire shape). FIVE distinct outcomes
 * of POST /webhooks/stripe emit the byte-identical 200 {"received": true, "changes":
 * false} via _ok_response(). Only the CloudWatch log line differs. This is the single most
 * consequential collapse in the repo: org-not-found (111 lifetime occurrences) is
 * wire-indistinguishable from a successfully-processed no-op, so a parity recorder cannot
 * detect a cutover that starts silently failing to find orgs. Collapsed sites:
 * functions/stripe_webhooks/stripe_webhooks.py:882 (event_type in _V2_ACCOUNT_EVENT_TYPES
 * (line 872) AND the resolved account_id is falsy (line 880). account_id comes from
 * stripe_event_dict.get("related_object") -> .get("id", "") if dict (:876) else
 * getattr(related, "id", "") (:878).); functions/stripe_webhooks/stripe_webhooks.py:903
 * (event_type == "account.updated" (line 898) AND `account_data` is falsy (line 901).
 * account_data = data.get("object") when data is a dict, else getattr(data, "object",
 * None) (:900), where data = stripe_event_dict.get("data") or {} (:899).);
 * functions/stripe_webhooks/stripe_webhooks.py:908 (FALL-THROUGH: signature verified, but
 * event_type is NOT in _V2_ACCOUNT_EVENT_TYPES and != "account.updated". This is the
 * terminal `return` of handler() with no guarding `if` — it also catches event_type == ""
 * (the .get("type", "") default at :867 when the verified payload has no `type` key).);
 * functions/stripe_webhooks/stripe_webhooks.py:740 (Inside _process_account_update
 * (reached from BOTH the v2-thin path at :893 and the classic path at :905).
 * `_find_org_by_stripe_account(stripe_account_id)` (:737) returned None — no org row in
 * the scanned ORGANIZATIONS_TABLE has stripeDetails.accountId == the event's account id.
 * Prints f"org_not_found stripe={stripe_account_id} — acknowledging (no matching org in
 * DB)".); functions/stripe_webhooks/stripe_webhooks.py:819 (Same site as
 * SW_PROCESSED_WITH_CHANGES_200 but changes_made stayed False — stored DynamoDB state
 * already matches the incoming Stripe state on all four comparisons. Prints
 * f"no_state_change org_id={org_id} — already up to date" at :812.)
 */
export const SW_ACK_NO_CHANGES_200 = variant(200, H_POST_OPTIONS_UC_2, SwOkNoChanges);

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:891 — Trigger: event_type in
 * _V2_ACCOUNT_EVENT_TYPES AND `_fetch_account_for_thin_event(account_id)` (line 886)
 * raised, caught at :887. Two internal causes: (a) `_init_stripe()` returned False ->
 * `raise RuntimeError("Stripe not initialized")` at :703 (env STRIPE_SECRET_KEY_PATH
 * unset, or the SSM get_parameter raised); (b) `stripe.Account.retrieve(account_id)`
 * (:704) raised any Stripe API error. Also emits `traceback.print_exc()` at :889. —
 * BUG-FOR-BUG: Deliberate 500 so Stripe retries. Note the internal RuntimeError message
 * "Stripe not initialized" is NOT surfaced in the body — the body is always the fixed
 * "Account fetch failed". — stripe_webhooks.py:891. The internal RuntimeError text
 * ('Stripe not initialized', :703) is NOT surfaced — the body is always this fixed
 * literal regardless of which sub-cause fired.
 */
export const SW_V2_ACCOUNT_FETCH_FAILED_500 = variant(500, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Account fetch failed') }).strict());

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:817 — Trigger: Any exception raised
 * inside the try block at :750-:812 of _process_account_update, caught by `except
 * Exception as e` at :814. Sources: _handle_charges_enabled_change (:753) DynamoDB
 * update_item at :352/:374/:392/:428 raising anything OTHER than
 * ConditionalCheckFailedException (which is caught at :400);
 * _handle_payouts_enabled_change (:758) update_item at :466/:509; the details_submitted
 * update_item at :764; _update_tax_id_and_email_snapshot's update_item at :618; also
 * _register_apple_pay_domain (:419) is itself total-catch so it does not raise, and
 * _persist_apple_pay_status (:276) swallows at :285. The telemetry update_item at :797
 * is separately wrapped (:808) and CANNOT produce this 500. Emits traceback.print_exc()
 * at :816. — BUG-FOR-BUG: PARTIAL-WRITE HAZARD PINNED: this 500 can fire AFTER some of
 * the org-table writes have already committed (e.g. the charges update at :352
 * succeeded, the payouts update at :466 then failed). Stripe retries the whole event;
 * the writes are individually idempotent so the retry converges, but the intermediate
 * state is observable. — stripe_webhooks.py:817, the `except Exception` at :814. Fixed
 * literal — the underlying exception text is only printed (:815) plus
 * traceback.print_exc() (:816), never returned. Can fire AFTER earlier org-table writes
 * have already committed.
 */
export const SW_PROCESSING_FAILED_500 = variant(500, H_POST_OPTIONS_UC_2, z.object({ error: z.literal('Processing failed') }).strict());

/**
 * site functions/stripe_webhooks/stripe_webhooks.py:819 — Trigger:
 * _process_account_update completed and `changes_made` is True. Set True by ANY of:
 * charges_enabled != stored_charges (:752-754); payouts_enabled != stored_payouts
 * (:757-759); details_submitted and not stored_details (:762-770);
 * _update_tax_id_and_email_snapshot returned True (:780-781). — stripe_webhooks.py:819
 * with changes_made True. `received` is the Python literal True at :681 and is never
 * anything else; `changes` is the bool passed in. Plain json.dumps (no DecimalEncoder)
 * so both serialize as real JSON booleans, not strings.
 */
export const SW_PROCESSED_WITH_CHANGES_200 = variant(200, H_POST_OPTIONS_UC_2, z.object({ received: z.literal(true), changes: z.literal(true) }).strict());


// ===== functions/subscription_cancel/subscription_cancel.py ==============
/**
 * Response helper: functions/subscription_cancel/subscription_cancel.py:51 _response(code,
 * body) -> {'statusCode': code, 'headers': {**CORS_HEADERS, 'Content-Type':
 * 'application/json'}, 'body': json.dumps(body)} — plain json.dumps, no custom encoder
 */
/**
 * NOTES: FINDING (rule 4): the 200 body's cancelAt and currentPeriodEnd are unvalidated
 * Stripe values and can legitimately be JSON null; the 500 body is a prefixed provider
 * string `^Stripe error: .*$`. FINDING: this endpoint gates on a RAW ROLE STRING while
 * PATCH /billing-plan gates on the Clerk permission MANAGE_BILLING_PERMISSION — two
 * authorization models on adjacent billing routes, and they read DIFFERENT authorizer
 * claims ('role' or 'orgRole' here vs 'orgRole' only there). FINDING: a failure at :115
 * (str->json round-trip) returns 500 AFTER the Stripe cancel already committed.
 */

/**
 * site functions/subscription_cancel/subscription_cancel.py:86 — Trigger: method ==
 * 'OPTIONS' — BUG-FOR-BUG: Bypasses _response — no Content-Type. Only POST is routed
 * (ApiStack.ts:1282). — 204 with `'body': ''` (subscription_cancel.py:86) — bypasses
 * _response, so no Content-Type.
 */
export const SUBSCRIPTION_CANCEL_OPTIONS_PREFLIGHT = emptyBodyVariant(204, H_POST_OPTIONS_UC);

/**
 * site functions/subscription_cancel/subscription_cancel.py:90 — Trigger:
 * _get_clerk_org_id returns None — provider != 'clerk' or orgId absent — BUG-FOR-BUG:
 * Handler-produced 403. — subscription_cancel.py:90 — hardcoded literal, SHORT form.
 */
export const SUBSCRIPTION_CANCEL_NO_ORG_CONTEXT_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('No org context') }).strict());

/**
 * site functions/subscription_cancel/subscription_cancel.py:93 — Trigger:
 * _get_clerk_role(event) not in ('org:admin', 'admin') — the role is read as
 * `lam.get('role') or lam.get('orgRole') or ''` (:70), so '' (absent) also 403s —
 * BUG-FOR-BUG: AUTHORIZATION MODEL DIVERGENCE worth pinning: this endpoint uses a RAW
 * ROLE STRING CHECK against a two-element tuple, NOT the permission system billing_plan
 * uses (MANAGE_BILLING_PERMISSION via Clerk). A custom role granted
 * org:billing:manage_billing CANNOT cancel a subscription; only a literal
 * 'org:admin'/'admin' role can. Two billing endpoints, two incompatible authorization
 * models. — subscription_cancel.py:93 — hardcoded literal. Second 403 body on this
 * route; gated on a RAW role-string check against ('org:admin', 'admin'), not on the
 * Clerk permission system PATCH /billing-plan uses.
 */
export const SUBSCRIPTION_CANCEL_NOT_ADMIN_403 = variant(403, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Admin role required to cancel') }).strict());

/**
 * site functions/subscription_cancel/subscription_cancel.py:97 — Trigger: zero Items
 * from the clerkOrganizationId-index query — BUG-FOR-BUG: No ORGANIZATIONS_TABLE guard
 * in _get_org_by_clerk_org_id (:73) — env default is '' (:23), so an unset env raises to
 * a gateway 500 rather than this 404. — subscription_cancel.py:97 — hardcoded literal.
 */
export const SUBSCRIPTION_CANCEL_ORG_NOT_FOUND_404 = variant(404, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Organization not found') }).strict());

/**
 * site functions/subscription_cancel/subscription_cancel.py:102 — Trigger:
 * org.billingDetails.stripeSubscriptionId is absent or falsy — BUG-FOR-BUG: The name
 * says 'active' but the code only checks PRESENCE of the id — a cancelled/expired
 * subscription id still passes this gate and reaches Stripe. —
 * subscription_cancel.py:102 — hardcoded literal. The string says 'active' but the code
 * only checks PRESENCE of billingDetails.stripeSubscriptionId, so an already-cancelled
 * id passes this gate.
 */
export const SUBSCRIPTION_CANCEL_NO_SUBSCRIPTION_400 = variant(400, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('No active subscription to cancel') }).strict());

/**
 * site functions/subscription_cancel/subscription_cancel.py:105 — Trigger:
 * _load_stripe_key() returns False — STRIPE_SECRET_KEY_PATH env unset (:39-40) OR the
 * SSM get_parameter raised (:44-46) — BUG-FOR-BUG: LATENT BUG IN THE CACHE:
 * _load_stripe_key sets `_stripe_loaded = True` only at :47, AFTER the successful SSM
 * read. On the env-unset path (:40) it returns False WITHOUT setting the flag, so every
 * request re-enters. More importantly the flag is set only on success, so the cache
 * never memoizes a failure — behaviorally fine, but note _stripe_loaded is never True
 * with _stripe_key None, meaning line :38 `if _stripe_loaded: return bool(_stripe_key)`
 * can only ever return True. — subscription_cancel.py:105 — hardcoded literal at status
 * 500. Distinguishable from the other 500 on this route because that one is prefixed
 * 'Stripe error: '.
 */
export const SUBSCRIPTION_CANCEL_STRIPE_NOT_CONFIGURED_500 = variant(500, H_POST_OPTIONS_UC_JSON, z.object({ error: z.literal('Stripe not configured') }).strict());

/**
 * site functions/subscription_cancel/subscription_cancel.py:118 — Trigger: ANY exception
 * out of the try at :107 — bare `except Exception as e` at :116. This covers
 * stripe.Subscription.modify (:108) AND the `json.loads(str(sub_raw))` round-trip at
 * :115. NO stripe.error.* clauses exist in this file. — BUG-FOR-BUG: TWO pinned issues.
 * (1) The ONLY handler in this group whose leaked provider string carries a literal
 * PREFIX — a contract must pin the prefix and treat the remainder as arbitrary. Do NOT
 * collapse this to the bare `str(e)` shape used by the three Stripe-session handlers.
 * (2) A JSON-parse failure of the Stripe response at :115 is reported as 'Stripe error:
 * ...' even though the CANCEL ALREADY SUCCEEDED at Stripe — the caller sees a 500 for a
 * mutation that committed. — subscription_cancel.py:118. f-string source text: `f'Stripe
 * error: {str(e)}'`. The literal prefix 'Stripe error: ' IS pinnable; the remainder is
 * an UNVALIDATED Stripe/runtime string and may contain newlines, hence [\s\S]* rather
 * than .*. This is the ONLY leaked provider string in the group with a prefix — do NOT
 * collapse it into the bare z.string() shape used by account_session /
 * management_session / account_email. Reached by a bare `except Exception` at :116 that
 * wraps BOTH stripe.Subscription.modify (:108) and the `json.loads(str(sub_raw))`
 * round-trip (:115), so a parse failure yields this 500 even though the cancel already
 * committed at Stripe.
 */
export const SUBSCRIPTION_CANCEL_STRIPE_ERROR_500_LEAKS_PROVIDER_STRING = variant(500, H_POST_OPTIONS_UC_JSON, z.object({ error: z.string().regex(/^Stripe error: [\s\S]*$/) }).strict());

/**
 * site functions/subscription_cancel/subscription_cancel.py:120-124 — Trigger:
 * stripe.Subscription.modify succeeded and the str->json round-trip parsed —
 * BUG-FOR-BUG: PINNED: `current_period_end` moved off the top-level Subscription object
 * in newer Stripe API versions (it lives on subscription items). No stripe_version is
 * pinned on this call (:108-112 passes only api_key and cancel_at_period_end), so the
 * account's default API version decides whether currentPeriodEnd is an integer or null.
 * The contract must allow null for BOTH keys. Also: nothing in this handler writes
 * billingDetails.cancelAt — the docstring (:4-7) states the WEBHOOK does that, so the
 * 200 here does not imply the org record has been updated. —
 * subscription_cancel.py:120-124. 'scheduled' is hardcoded and is the only value that
 * key ever takes. cancelAt and currentPeriodEnd are ALWAYS PRESENT (plain `.get()` with
 * no default emits JSON null, it does not omit the key) but BOTH NULLABLE — they are
 * unvalidated Stripe values read off `json.loads(str(sub_raw))`, and the modify call at
 * :108 pins NO stripe_version, so the account's default API version decides whether
 * current_period_end still exists at the top level of a Subscription (it moved onto
 * subscription items in newer versions). Unix-epoch seconds when present. Serialized
 * with plain json.dumps (no encoder). Note this handler does NOT write
 * billingDetails.cancelAt — per the module docstring the webhook does, so a 200 here
 * does not imply the org record changed.
 */
export const SUBSCRIPTION_CANCEL_OK_200 = variant(200, H_POST_OPTIONS_UC_JSON, z.object({ status: z.literal('scheduled'), cancelAt: z.number().nullable(), currentPeriodEnd: z.number().nullable() }).strict());


// ===== functions/update_user_profile/update_user_profile.py ==============
/**
 * Response helper: null — all five responses are inline dict literals; no helper exists in
 * this file.
 */
/**
 * NOTES: Read-modify-write of Clerk public_metadata with no optimistic concurrency — two
 * concurrent PATCHes (or a concurrent clerk_webhooks._set_signup_complete, which does the
 * SAME read-modify-write of public_metadata at clerk_webhooks.py:236-239) can lose an
 * update. That cross-handler collision on the identical Clerk field is the finding a
 * contract author must not miss. Also: the 429 retry sleeps up to ~1+2+jitter seconds
 * inside a 30s-timeout Lambda, so a rate-limited call can add several seconds of latency
 * before the 500.
 */

/**
 * site functions/update_user_profile/update_user_profile.py:64-68 — Trigger:
 * requestContext.http.method != 'PATCH'. Unreachable via API Gateway (only PATCH
 * /user-profile is routed at stacks/ApiStack.ts:1214). — update_user_profile.py:67.
 * Unreachable via API Gateway (only PATCH /user-profile routed).
 */
export const USER_PROFILE_METHOD_NOT_ALLOWED = variant(405, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/update_user_profile/update_user_profile.py:74-78 — Trigger:
 * authorizer.lambda.provider != 'clerk' (dualAuth: a Cognito-authorized caller lands
 * here) — update_user_profile.py:77. Same literal as CLERK_USERS_NOT_CLERK_PROVIDER but
 * a different handler.
 */
export const USER_PROFILE_NOT_CLERK_PROVIDER = variant(403, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('Clerk authentication required') }).strict());

/**
 * site functions/update_user_profile/update_user_profile.py:82-86 — Trigger: provider ==
 * 'clerk' but authorizer.lambda.sub is empty
 */
export const USER_PROFILE_NO_USER_ID = variant(403, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('User ID not found in token') }).strict());

/**
 * site functions/update_user_profile/update_user_profile.py:98-102 — Trigger: neither
 * 'phone_number' nor 'address' is a key of the parsed body. Presence-only check (`in
 * body` at :92/:94) — an explicit null or empty string COUNTS as provided and is written
 * through. — update_user_profile.py:101. snake_case names and the parenthesised list are
 * verbatim.
 */
export const USER_PROFILE_NO_FIELDS = variant(400, H_PATCH_OPTIONS_UC, z.object({ error: z.literal('No profile fields provided (phone_number, address)') }).strict());

/**
 * site functions/update_user_profile/update_user_profile.py:122-126 — Trigger:
 * clerk.users.get and clerk.users.update both succeeded — update_user_profile.py:125.
 * Returns nothing about the values actually written.
 */
export const USER_PROFILE_OK = variant(200, H_PATCH_OPTIONS_UC, z.object({ message: z.literal('Profile updated successfully') }).strict());

/**
 * site functions/update_user_profile/update_user_profile.py:128-134 — Trigger: any
 * Exception from clerk.users.get or clerk.users.update (including exhausted 429 retries)
 * — BUG-FOR-BUG: Leaks raw Clerk SDK exception text. — f-string source: f'Failed to
 * update profile: {str(e)}' (update_user_profile.py:133).
 */
export const USER_PROFILE_INTERNAL_ERROR = variant(500, H_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to update profile: [\s\S]*$/) }).strict());


// ===== functions/users/users.py ==========================================
/**
 * Response helper: functions/users/users.py:412 _error(status_code, message) ->
 * {'statusCode': status_code, 'headers': CORS_HEADERS, 'body': json.dumps({'error':
 * message})}. Success responses are built INLINE (no success helper).
 */
/**
 * NOTES: Ordering hazards a contract must preserve: (1) In list_users_by_org the orgId 400
 * is checked BEFORE the sub 403 (:334 then :339) — the reverse of every other route on
 * this Lambda, where the 403 comes first. (2) `count` in GET_USERS_LIST_OK is a real int.
 * (3) The `limit` query param is parsed with int() inside a try/except (ValueError,
 * TypeError) at :349-353 that silently falls back to 200 — a non-numeric limit never
 * produces a 4xx. (4) Negative/zero limit is clamped to 1 by max(1, min(...)) at :351. (5)
 * The pagination loop at :361-367 sets no DynamoDB `Limit`, so it fetches whole GSI pages
 * and trims client-side; the cap is a slice, not a query bound. (6) ALL responses on this
 * handler, success and error, carry CORS_HEADERS — there is no headerless variant. (7) The
 * malformed-JSON-body uncaught exception (item 2 in uncaughtExceptions) is the
 * highest-value finding here: it is a 502 with NO CORS headers, which a contract expecting
 * a 400 will not reproduce.
 */

/**
 * site functions/users/users.py:79-83 — Trigger: handler() router else-branch: method
 * not in {POST,GET,PATCH}. NOTE: only GET/POST/PATCH routes are declared in
 * stacks/ApiStack.ts:1258-1272, so in practice this is unreachable through API Gateway;
 * it is reachable only by direct Lambda invoke. — users.py:82. Unreachable via API
 * Gateway (only GET/POST/PATCH routed); direct-invoke only.
 */
export const USERS_METHOD_NOT_ALLOWED = variant(405, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Method not allowed') }).strict());

/**
 * site functions/users/users.py:102-106 — Trigger: user_id present in
 * authorizer.lambda.sub AND table.get_item returns an Item — users.py:105
 * json.dumps(item, cls=DecimalEncoder) — the raw DynamoDB row is returned with NO
 * projection and NO allowlist, so the key set is OPEN and determined by whatever is
 * stored. Not modelled as an object literal on purpose. DecimalEncoder (users.py:39-44)
 * maps Decimal -> float, so any numeric attribute is a JSON number, not a string.
 */
export const GET_USERS_ME_OK = variant(200, H_GET_POST_PATCH_OPTIONS_UC, z.record(z.unknown()));

/**
 * site functions/users/users.py:94 — Trigger: _get_user_id(event) falsy —
 * requestContext.authorizer.lambda.sub missing/empty. HANDLER-produced 403 (not the
 * authorizer's). — users.py:94 via _error. Handler-produced 403, not the authorizer's.
 */
export const GET_USERS_ME_NO_USER_ID = variant(403, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User ID not found in token') }).strict());

/** site functions/users/users.py:100 — Trigger: get_item returned no 'Item' */
export const GET_USERS_ME_NOT_FOUND = variant(404, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User record not found') }).strict());

/**
 * site functions/users/users.py:109 — Trigger: any Exception from table.get_item or
 * json.dumps — BUG-FOR-BUG: LEAKS raw exception text (boto3 ClientError string, table
 * name, request id) into the client-facing body. — f-string source: f'Failed to get
 * user: {str(e)}' (users.py:109). Suffix is a raw exception string — unbounded, may
 * contain newlines, so [\s\S]* not .*
 */
export const GET_USERS_ME_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to get user: [\s\S]*$/) }).strict());

/**
 * site functions/users/users.py:169-173 — Trigger: at least one of {preferences, phone}
 * present in body, preferences (if present) is a dict, and the row exists — users.py:172
 * json.dumps(response['Attributes'], cls=DecimalEncoder) with ReturnValues='ALL_NEW' —
 * the FULL post-update row. Open key set, same passthrough as GET_USERS_ME_OK.
 */
export const PATCH_USERS_ME_OK = variant(200, H_GET_POST_PATCH_OPTIONS_UC, z.record(z.unknown()));

/** site functions/users/users.py:120 — Trigger: authorizer.lambda.sub missing/empty */
export const PATCH_USERS_ME_NO_USER_ID = variant(403, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User ID not found in token') }).strict());

/**
 * site functions/users/users.py:124 — Trigger: json.loads(event.get('body','{}')) is
 * falsy — i.e. body absent, '{}', '[]', 'null', '0', '""'. Any falsy JSON value hits
 * this, not only an empty object. — BUG-FOR-BUG: A body of literal JSON `null` / `0` /
 * `[]` also yields this 400 rather than a type error — falsy-check, not dict-check. —
 * users.py:124. Triggered by any FALSY parsed body — '{}', 'null', '0', '[]', '""' — not
 * only an empty object.
 */
export const PATCH_USERS_ME_EMPTY_BODY = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Request body is empty') }).strict());

/**
 * site functions/users/users.py:136 — Trigger: body non-empty but contains none of
 * {'preferences','phone'} — f-string source: f'No valid fields provided. Allowed: {",
 * ".join(sorted(allowed_fields))}' (users.py:136). allowed_fields =
 * {'preferences','phone'} (:132); sorted() makes the rendering FULLY DETERMINISTIC, so
 * this is a literal, not a regex.
 */
export const PATCH_USERS_ME_NO_VALID_FIELDS = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('No valid fields provided. Allowed: phone, preferences') }).strict());

/**
 * site functions/users/users.py:140 — Trigger: 'preferences' in the allowlisted update
 * fields AND not isinstance(value, dict)
 */
export const PATCH_USERS_ME_PREFERENCES_NOT_OBJECT = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('preferences must be an object') }).strict());

/**
 * site functions/users/users.py:175 — Trigger: ConditionalCheckFailedException on
 * ConditionExpression='attribute_exists(userId)' — users.py:175 —
 * ConditionalCheckFailedException on attribute_exists(userId). Same literal as
 * GET_USERS_ME_NOT_FOUND.
 */
export const PATCH_USERS_ME_NOT_FOUND = variant(404, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User record not found') }).strict());

/**
 * site functions/users/users.py:178 — Trigger: any non-ConditionalCheckFailed Exception
 * from update_item — BUG-FOR-BUG: Leaks raw exception text. — f-string source: f'Failed
 * to update user: {str(e)}' (users.py:178). NOTE: clerk_users.py:460 emits an IDENTICAL
 * prefix from a different handler — do not merge the two variants.
 */
export const PATCH_USERS_ME_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to update user: [\s\S]*$/) }).strict());

/**
 * site functions/users/users.py:235-244 — Trigger: sub present, orgId present,
 * ORGANIZATIONS_TABLE configured, org row resolved with an organizationId, and the user
 * row exists — users.py:235-243. activeOrgId is the resolved internal organizationId;
 * clerkOrgId is echoed from authorizer.lambda.orgId. `user` is the full ALL_NEW row — an
 * open passthrough, hence z.record(z.unknown()).
 */
export const SYNC_ACTIVE_ORG_OK = variant(200, H_GET_POST_PATCH_OPTIONS_UC, z.object({ activeOrgId: z.string(), clerkOrgId: z.string(), user: z.record(z.unknown()) }).strict());

/** site functions/users/users.py:195 — Trigger: authorizer.lambda.sub missing/empty */
export const SYNC_ACTIVE_ORG_NO_USER_ID = variant(403, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User ID not found in token') }).strict());

/** site functions/users/users.py:199 — Trigger: authorizer.lambda.orgId missing/empty */
export const SYNC_ACTIVE_ORG_NO_CLERK_ORG = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('No active Clerk organization found in token') }).strict());

/**
 * site functions/users/users.py:202 — Trigger: neither env ORGANIZATIONS_TABLE nor
 * ORGANIZATIONS_TABLE_NAME set (module-level resolution at :25)
 */
export const SYNC_ACTIVE_ORG_TABLE_UNCONFIGURED = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Organizations table is not configured') }).strict());

/**
 * site functions/users/users.py:208 — Trigger: any Exception from
 * _read_organization_by_clerk_org_id (org_table.query on clerkOrganizationId-index) —
 * users.py:208 — a fixed literal, NOT an f-string; this 500 does not leak the exception
 * (unlike its siblings).
 */
export const SYNC_ACTIVE_ORG_RESOLVE_FAILED = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Failed to resolve active organization') }).strict());

/**
 * site functions/users/users.py:211 — Trigger: GSI query returned no items — f-string
 * source: f'No organization mapping found for active Clerk org: {clerk_org_id}'
 * (users.py:211). The interpolated value is the authorizer-supplied Clerk org id, echoed
 * back.
 */
export const SYNC_ACTIVE_ORG_NO_MAPPING = variant(404, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^No organization mapping found for active Clerk org: .+$/) }).strict());

/**
 * site functions/users/users.py:215 — Trigger: org row found but has neither
 * 'organizationId' nor 'id'
 */
export const SYNC_ACTIVE_ORG_MISSING_ORG_ID = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Resolved organization is missing organizationId') }).strict());

/**
 * site functions/users/users.py:246 — Trigger: ConditionalCheckFailedException on
 * attribute_exists(userId)
 */
export const SYNC_ACTIVE_ORG_USER_NOT_FOUND = variant(404, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User record not found') }).strict());

/**
 * site functions/users/users.py:249 — Trigger: any other Exception from update_item /
 * json.dumps — BUG-FOR-BUG: Leaks raw exception text. — f-string source: f'Failed to
 * sync active org: {str(e)}' (users.py:249).
 */
export const SYNC_ACTIVE_ORG_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to sync active org: [\s\S]*$/) }).strict());

/**
 * site functions/users/users.py:303-307 — Trigger: sub present, all of
 * email/firstName/lastName truthy in body, preferences (if present) a dict, and no
 * existing row — users.py:279-306 echoes the item AS WRITTEN. CLOSED key set (a literal
 * dict at :279). email/firstName/lastName are z.unknown() because the code only tests
 * TRUTHINESS at :270 — a number, list, or dict passes validation and is echoed back
 * verbatim; do not narrow to z.string(). preferences is z.unknown() for the same reason
 * (dict-checked only when the key is present, :275). avatarUrl/phone are .optional()
 * because None values are stripped at :296. Timestamps come from now_iso() (:47-49)
 * strftime('%Y-%m-%dT%H:%M:%SZ'), hence the exact regex. userId is the JWT sub, never
 * from the body. activeOrgId is deliberately never present.
 */
export const POST_USERS_CREATED = variant(201, H_GET_POST_PATCH_OPTIONS_UC, z.object({ userId: z.string(), email: z.unknown(), firstName: z.unknown(), lastName: z.unknown(), avatarUrl: z.unknown().optional(), phone: z.unknown().optional(), preferences: z.unknown(), createdAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/), updatedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/) }).strict());

/** site functions/users/users.py:264 — Trigger: authorizer.lambda.sub missing/empty */
export const POST_USERS_NO_USER_ID = variant(403, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User ID not found in token') }).strict());

/**
 * site functions/users/users.py:272 — Trigger: any of email/firstName/lastName falsy in
 * body (absent, '', 0, null, [], {}) — f-string source: f'Missing required fields: {",
 * ".join(missing)}' (users.py:272). `missing` is built by a comprehension over required
 * = ['email','firstName','lastName'] (:269-270), so the order is always that source
 * order and the alternation cannot repeat a name.
 */
export const POST_USERS_MISSING_FIELDS = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Missing required fields: (email|firstName|lastName)(, (email|firstName|lastName))*$/) }).strict());

/** site functions/users/users.py:276 — Trigger: 'preferences' in body and not a dict */
export const POST_USERS_PREFERENCES_NOT_OBJECT = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('preferences must be an object') }).strict());

/**
 * site functions/users/users.py:309 — Trigger: ConditionalCheckFailedException on
 * attribute_not_exists(userId) — f-string source: f'User already exists: {user_id}'
 * (users.py:309). Echoes the caller's own JWT sub.
 */
export const POST_USERS_CONFLICT = variant(409, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^User already exists: .+$/) }).strict());

/**
 * site functions/users/users.py:312 — Trigger: any other Exception from put_item
 * (includes DynamoDB ValidationException when a body field carries an unsupported type
 * such as a float) — BUG-FOR-BUG: Leaks raw exception text. — f-string source: f'Failed
 * to create user: {str(e)}' (users.py:312).
 */
export const POST_USERS_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to create user: [\s\S]*$/) }).strict());

/**
 * site functions/users/users.py:372-376 — Trigger: orgId query param present, caller row
 * exists AND caller_record['activeOrgId'] == orgId, and the query succeeds —
 * BUG-FOR-BUG: FINDING: returns FULL user rows for every member of the org (email,
 * phone, preferences, all attributes) with no projection — a member-list endpoint
 * leaking whole records. — users.py:375. OPEN PASSTHROUGH: each array element is a full
 * unprojected DynamoDB row from the activeOrgId-index GSI — every stored attribute of
 * every org member (email, phone, preferences, ...) with no allowlist, so no key set can
 * honestly be pinned. `count` is len(items), a real Python int -> JSON number;
 * DecimalEncoder maps Decimal to float, never to a string, so nothing here is
 * string-typed.
 */
export const GET_USERS_LIST_OK = variant(200, H_GET_POST_PATCH_OPTIONS_UC, z.object({ users: z.array(z.record(z.unknown())), count: z.number().int().nonnegative() }).strict());

/**
 * site functions/users/users.py:334 — Trigger: queryStringParameters absent/null or
 * orgId absent or empty string — users.py:334. Checked BEFORE the sub 403 — a request
 * missing both yields THIS, not GET_USERS_LIST_NO_USER_ID.
 */
export const GET_USERS_LIST_MISSING_ORG_ID = variant(400, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Missing required query parameter: orgId') }).strict());

/**
 * site functions/users/users.py:339 — Trigger: authorizer.lambda.sub missing/empty
 * (checked AFTER the orgId check, so ordering is load-bearing: a request with neither
 * yields the 400, not the 403)
 */
export const GET_USERS_LIST_NO_USER_ID = variant(403, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('User ID not found in token') }).strict());

/**
 * site functions/users/users.py:343 — Trigger: caller's own row missing OR
 * caller_record.get('activeOrgId') != orgId. NOTE: a caller with NO user row gets this
 * 403, not a 404. — users.py:343. A caller with NO user row gets this 403, not a 404.
 */
export const GET_USERS_LIST_FORBIDDEN_ORG = variant(403, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('You do not have access to this organization') }).strict());

/**
 * site functions/users/users.py:346 — Trigger: Exception from the caller-record get_item
 * — users.py:346 — fixed literal, does not leak the exception.
 */
export const GET_USERS_LIST_MEMBERSHIP_CHECK_FAILED = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.literal('Failed to verify org membership') }).strict());

/**
 * site functions/users/users.py:379 — Trigger: Exception from table.query loop or
 * json.dumps — BUG-FOR-BUG: Leaks raw exception text. — f-string source: f'Failed to
 * list users: {str(e)}' (users.py:379). IDENTICAL prefix to
 * GET_CLERK_USERS_INTERNAL_ERROR (clerk_users.py:276) in a different handler — keep the
 * variants separate.
 */
export const GET_USERS_LIST_INTERNAL_ERROR = variant(500, H_GET_POST_PATCH_OPTIONS_UC, z.object({ error: z.string().regex(/^Failed to list users: [\s\S]*$/) }).strict());


// --------------------------------------------------------------------------
// Route registries — one per DECLARED route on api jz7twvotyf. Every registry
// carries the gateway variants plus the handler variants that route can produce.
// --------------------------------------------------------------------------

/** `DELETE /org-contact-tags` — CUSTOM authorizer 0jd4m5 DualAuth. 6 handler variant(s). */
export const DELETE_ORG_CONTACT_TAGS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  TAGS_DELETE_INTERNAL_ERROR_500,
  TAGS_DELETE_OK_200,
  TAGS_DELETE_ORG_NOT_FOUND_404,
  TAGS_MUTATE_CLERK_AUTH_REQUIRED_403,
  TAGS_MUTATE_PERMISSION_DENIED_403,
  TAGS_MUTATE_PERMISSION_UNAVAILABLE_503,
} as const;

/** `PATCH /org-custom-fields` — CUSTOM authorizer 0jd4m5 DualAuth. 13 handler variant(s). */
export const PATCH_ORG_CUSTOM_FIELDS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CF_CLERK_AUTH_REQUIRED_403,
  CF_MUTATE_PERMISSION_DENIED_403,
  CF_ORG_NOT_FOUND_404,
  CF_PERMISSION_RESOLUTION_UNAVAILABLE_503,
  CF_RENAME_BODY_NOT_OBJECT_400,
  CF_RENAME_INTERNAL_ERROR_500,
  CF_RENAME_INVALID_JSON_400,
  CF_RENAME_KEY_REQUIRED_400,
  CF_RENAME_LABEL_CONFLICT_409,
  CF_RENAME_LABEL_VALIDATION_400,
  CF_RENAME_NOT_FOUND_404,
  CF_RENAME_OK_200,
  CF_RENAME_WRITE_CONFLICT_409,
} as const;

/**
 * `POST /privacy-policy/generate` — CUSTOM authorizer 0jd4m5 DualAuth. 2 handler
 * variant(s).
 */
export const POST_PRIVACY_POLICY_GENERATE_VARIANTS = {
  ...GATEWAY_VARIANTS,
  PP_GENERATE_NO_ORG_403,
  PP_GENERATE_OK_200,
} as const;

/**
 * `POST /privacy-policy/accept` — CUSTOM authorizer 0jd4m5 DualAuth. 11 handler
 * variant(s).
 */
export const POST_PRIVACY_POLICY_ACCEPT_VARIANTS = {
  ...GATEWAY_VARIANTS,
  PP_ACCEPT_BAD_JSON_400,
  PP_ACCEPT_CONFLICT_409,
  PP_ACCEPT_NO_CLERK_403,
  PP_ACCEPT_NO_ORG_403,
  PP_ACCEPT_OK_200,
  PP_ACCEPT_PERM_DENIED_403,
  PP_ACCEPT_PERM_UNAVAILABLE_503,
  PP_ACCEPT_READONLY_403,
  PP_ACCEPT_SLUG_REQUIRED_400,
  PP_ACCEPT_TOO_LONG_400,
  PP_ACCEPT_TOO_SHORT_400,
} as const;

/** `POST /users` — CUSTOM authorizer 0jd4m5 DualAuth. 6 handler variant(s). */
export const POST_USERS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  POST_USERS_CONFLICT,
  POST_USERS_CREATED,
  POST_USERS_INTERNAL_ERROR,
  POST_USERS_MISSING_FIELDS,
  POST_USERS_NO_USER_ID,
  POST_USERS_PREFERENCES_NOT_OBJECT,
} as const;

/** `PATCH /custom-roles` — CUSTOM authorizer 0jd4m5 DualAuth. 7 handler variant(s). */
export const PATCH_CUSTOM_ROLES_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CR_UPDATE_INTERNAL_ERROR_500,
  CR_UPDATE_LABEL_INVALID_400,
  CR_UPDATE_NOT_FOUND_404,
  CR_UPDATE_OK_200,
  CR_UPDATE_SLUG_REQUIRED_400,
  RP_ADMIN_CLERK_AUTH_REQUIRED_403,
  RP_ADMIN_ROLE_REQUIRED_403,
} as const;

/**
 * `POST /onboarding/create-org` — CUSTOM authorizer 0jd4m5 DualAuth. 17 handler
 * variant(s).
 */
export const POST_ONBOARDING_CREATE_ORG_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CREATE_ORG_BAD_ENTITY_TYPE_400,
  CREATE_ORG_BAD_IDEMPOTENCY_KEY_400,
  CREATE_ORG_CLERK_502,
  CREATE_ORG_CLERK_NOT_CONFIGURED_503,
  CREATE_ORG_EMAIL_LOOKUP_FAILED_503,
  CREATE_ORG_INVALID_JSON_400,
  CREATE_ORG_INVALID_ORG_INPUT_400,
  CREATE_ORG_IN_PROGRESS_409,
  CREATE_ORG_LOCK_FAILED_503,
  CREATE_ORG_MEMBERSHIP_LOOKUP_FAILED_503,
  CREATE_ORG_MISSING_ORG_NAME_400,
  CREATE_ORG_MULTI_ORG_DISABLED_403,
  CREATE_ORG_NO_EMAIL_400,
  CREATE_ORG_NO_SUB_403,
  CREATE_ORG_OPTIONS_PREFLIGHT,
  CREATE_ORG_REPLAY_200,
  CREATE_ORG_V2_WRITE_FAILED_500,
} as const;

/** `POST /subscription-cancel` — CUSTOM authorizer 0jd4m5 DualAuth. 8 handler variant(s). */
export const POST_SUBSCRIPTION_CANCEL_VARIANTS = {
  ...GATEWAY_VARIANTS,
  SUBSCRIPTION_CANCEL_NOT_ADMIN_403,
  SUBSCRIPTION_CANCEL_NO_ORG_CONTEXT_403,
  SUBSCRIPTION_CANCEL_NO_SUBSCRIPTION_400,
  SUBSCRIPTION_CANCEL_OK_200,
  SUBSCRIPTION_CANCEL_OPTIONS_PREFLIGHT,
  SUBSCRIPTION_CANCEL_ORG_NOT_FOUND_404,
  SUBSCRIPTION_CANCEL_STRIPE_ERROR_500_LEAKS_PROVIDER_STRING,
  SUBSCRIPTION_CANCEL_STRIPE_NOT_CONFIGURED_500,
} as const;

/** `GET /users/me` — CUSTOM authorizer 0jd4m5 DualAuth. 4 handler variant(s). */
export const GET_USERS_ME_VARIANTS = {
  ...GATEWAY_VARIANTS,
  GET_USERS_ME_INTERNAL_ERROR,
  GET_USERS_ME_NOT_FOUND,
  GET_USERS_ME_NO_USER_ID,
  GET_USERS_ME_OK,
} as const;

/**
 * `POST /webhooks/stripe-subscription` — AuthorizationType NONE (no authorizer). 9 handler
 * variant(s).
 */
export const POST_WEBHOOKS_STRIPE_SUBSCRIPTION_VARIANTS = {
  ...GATEWAY_VARIANTS,
  SSW_EVENT_BODY_NOT_OBJECT_400,
  SSW_EVENT_TYPE_IGNORED_200,
  SSW_HANDLER_ERROR_500,
  SSW_INVALID_JSON_BODY_400,
  SSW_INVALID_PAYLOAD_400,
  SSW_OK_200,
  SSW_OPTIONS_PREFLIGHT,
  SSW_SIGNATURE_VERIFICATION_FAILED_400,
  SSW_WEBHOOK_SECRET_NOT_CONFIGURED_503,
} as const;

/** `GET /stripe/account-status` — CUSTOM authorizer 0jd4m5 DualAuth. 5 handler variant(s). */
export const GET_STRIPE_ACCOUNT_STATUS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  ACCOUNT_STATUS_IDENTITY_CONFLICT_409,
  ACCOUNT_STATUS_NO_ORG_CONTEXT_403,
  ACCOUNT_STATUS_OK_200,
  ACCOUNT_STATUS_OPTIONS_PREFLIGHT,
  ACCOUNT_STATUS_ORG_NOT_FOUND_404,
} as const;

/**
 * `POST /privacy-policy/overlay` — CUSTOM authorizer 0jd4m5 DualAuth. 8 handler
 * variant(s).
 */
export const POST_PRIVACY_POLICY_OVERLAY_VARIANTS = {
  ...GATEWAY_VARIANTS,
  PP_OVERLAY_BAD_ENABLED_400,
  PP_OVERLAY_BAD_JSON_400,
  PP_OVERLAY_NO_CLERK_403,
  PP_OVERLAY_NO_ORG_403,
  PP_OVERLAY_OK_200,
  PP_OVERLAY_PERM_DENIED_403,
  PP_OVERLAY_PERM_UNAVAILABLE_503,
  PP_OVERLAY_READONLY_403,
} as const;

/** `PATCH /nonprofit-settings` — CUSTOM authorizer 0jd4m5 DualAuth. 13 handler variant(s). */
export const PATCH_NONPROFIT_SETTINGS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  NS_BAD_BRAND_COLOR_400,
  NS_BAD_PRIVACY_POLICY_LINK_400,
  NS_BAD_REGISTRATION_PHONE_400,
  NS_BAD_TIMEZONE_400,
  NS_BAD_WEBSITE_URL_400,
  NS_BASEURLS_INVALID_ENTRY_400,
  NS_BASEURLS_NOT_LIST_400,
  NS_BASEURLS_TOO_MANY_400,
  NS_NO_ORG_403,
  NS_OK_200,
  NS_ORG_DESCRIPTION_TOO_LONG_400,
  NS_READONLY_403,
  NS_WRITE_FAILED_500,
} as const;

/** `GET /clerk-users` — CUSTOM authorizer 0jd4m5 DualAuth. 2 handler variant(s). */
export const GET_CLERK_USERS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  GET_CLERK_USERS_INTERNAL_ERROR,
  GET_CLERK_USERS_OK,
} as const;

/** `DELETE /clerk-users` — CUSTOM authorizer 0jd4m5 DualAuth. 9 handler variant(s). */
export const DELETE_CLERK_USERS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CLERK_USERS_NOT_ADMIN,
  CLERK_USERS_NOT_CLERK_PROVIDER,
  DELETE_CLERK_USERS_INTERNAL_ERROR,
  DELETE_CLERK_USERS_OK,
  DELETE_CLERK_USERS_REVOKE_INTERNAL_ERROR,
  DELETE_CLERK_USERS_REVOKE_INVITATION_ID_REQUIRED,
  DELETE_CLERK_USERS_REVOKE_INVITATION_OK,
  DELETE_CLERK_USERS_SELF_DELETE,
  DELETE_CLERK_USERS_USERID_REQUIRED,
} as const;

/** `PATCH /clerk-users` — CUSTOM authorizer 0jd4m5 DualAuth. 6 handler variant(s). */
export const PATCH_CLERK_USERS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  PATCH_CLERK_USERS_CUSTOM_ROLE_NOT_FOUND,
  PATCH_CLERK_USERS_INTERNAL_ERROR,
  PATCH_CLERK_USERS_INVALID_ROLE,
  PATCH_CLERK_USERS_OK,
  PATCH_CLERK_USERS_ROLE_NOT_AVAILABLE,
  PATCH_CLERK_USERS_USERID_REQUIRED,
} as const;

/** `POST /website-enrich` — CUSTOM authorizer 0jd4m5 DualAuth. 8 handler variant(s). */
export const POST_WEBSITE_ENRICH_VARIANTS = {
  ...GATEWAY_VARIANTS,
  WEBSITE_ENRICH_INVALID_JSON_400,
  WEBSITE_ENRICH_OPTIONS_PREFLIGHT,
  WEBSITE_ENRICH_REQUEST_EXCEPTION_502,
  WEBSITE_ENRICH_SSL_502,
  WEBSITE_ENRICH_SUCCESS_200,
  WEBSITE_ENRICH_TIMEOUT_504,
  WEBSITE_ENRICH_UNEXPECTED_500,
  WEBSITE_ENRICH_URL_VALIDATION_400,
} as const;

/**
 * `DELETE /org-role-permissions` — CUSTOM authorizer 0jd4m5 DualAuth. 4 handler
 * variant(s).
 */
export const DELETE_ORG_ROLE_PERMISSIONS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  RP_ADMIN_CLERK_AUTH_REQUIRED_403,
  RP_ADMIN_ROLE_REQUIRED_403,
  RP_RESET_INTERNAL_ERROR_500,
  RP_RESET_OK_200,
} as const;

/** `PUT /org-contact-tags` — CUSTOM authorizer 0jd4m5 DualAuth. 13 handler variant(s). */
export const PUT_ORG_CONTACT_TAGS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  TAGS_MUTATE_CLERK_AUTH_REQUIRED_403,
  TAGS_MUTATE_PERMISSION_DENIED_403,
  TAGS_MUTATE_PERMISSION_UNAVAILABLE_503,
  TAGS_PUT_INTERNAL_ERROR_500,
  TAGS_PUT_INVALID_JSON_400,
  TAGS_PUT_NON_STRING_ENTRY_400,
  TAGS_PUT_NOT_ARRAY_400,
  TAGS_PUT_OK_200,
  TAGS_PUT_ORG_NOT_FOUND_404,
  TAGS_PUT_TAGS_LIST_REQUIRED_400,
  TAGS_PUT_TAG_INVALID_CHARS_400,
  TAGS_PUT_TAG_TOO_LONG_400,
  TAGS_PUT_TOO_MANY_400,
} as const;

/** `GET /org-custom-fields` — CUSTOM authorizer 0jd4m5 DualAuth. 6 handler variant(s). */
export const GET_ORG_CUSTOM_FIELDS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CF_CLERK_AUTH_REQUIRED_403,
  CF_LIST_INTERNAL_ERROR_500,
  CF_LIST_OK_200,
  CF_LIST_PERMISSION_DENIED_403,
  CF_ORG_NOT_FOUND_404,
  CF_PERMISSION_RESOLUTION_UNAVAILABLE_503,
} as const;

/** `PUT /org-slug` — CUSTOM authorizer 0jd4m5 DualAuth. 11 handler variant(s). */
export const PUT_ORG_SLUG_VARIANTS = {
  ...GATEWAY_VARIANTS,
  OS_BAD_JSON_400,
  OS_CHANGED_200,
  OS_CLERK_UPDATE_FAILED_502,
  OS_INVALID_SLUG_400,
  OS_MIRROR_UNRESOLVED_502,
  OS_NO_CLERK_403,
  OS_NO_ORG_RECORD_403,
  OS_PERM_DENIED_403,
  OS_PERM_UNAVAILABLE_503,
  OS_SLUG_TAKEN_409,
  OS_UNCHANGED_200,
} as const;

/** `POST /verify-script` — CUSTOM authorizer 0jd4m5 DualAuth. 13 handler variant(s). */
export const POST_VERIFY_SCRIPT_VARIANTS = {
  ...GATEWAY_VARIANTS,
  VERIFY_SCRIPT_CONNECTION_502,
  VERIFY_SCRIPT_FETCH_200,
  VERIFY_SCRIPT_HEADLESS_200,
  VERIFY_SCRIPT_HTTP_ERROR_502,
  VERIFY_SCRIPT_INVALID_JSON_400,
  VERIFY_SCRIPT_METHOD_NOT_ALLOWED_405,
  VERIFY_SCRIPT_NO_ORG_CONTEXT_403,
  VERIFY_SCRIPT_OPTIONS_PREFLIGHT,
  VERIFY_SCRIPT_SSL_502,
  VERIFY_SCRIPT_TIMEOUT_502,
  VERIFY_SCRIPT_UNKNOWN_502,
  VERIFY_SCRIPT_URL_VALIDATION_400,
  VERIFY_SCRIPT_WAF_BLOCKED_502,
} as const;

/** `POST /custom-roles` — CUSTOM authorizer 0jd4m5 DualAuth. 10 handler variant(s). */
export const POST_CUSTOM_ROLES_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CR_CREATE_INTERNAL_ERROR_500,
  CR_CREATE_INVALID_PERMISSIONS_400,
  CR_CREATE_INVALID_SLUG_400,
  CR_CREATE_LABEL_INVALID_400,
  CR_CREATE_MAX_ROLES_400,
  CR_CREATE_OK_201,
  CR_CREATE_SLUG_EXISTS_409,
  CR_CREATE_SLUG_TOO_LONG_400,
  RP_ADMIN_CLERK_AUTH_REQUIRED_403,
  RP_ADMIN_ROLE_REQUIRED_403,
} as const;

/** `GET /merchant` — CUSTOM authorizer 0jd4m5 DualAuth. 3 handler variant(s). */
export const GET_MERCHANT_VARIANTS = {
  ...GATEWAY_VARIANTS,
  GET_MERCHANT_NOT_FOUND_404,
  GET_MERCHANT_NO_ORG_403,
  GET_MERCHANT_OK_200,
} as const;

/** `GET /stripe/account-email` — CUSTOM authorizer 0jd4m5 DualAuth. 5 handler variant(s). */
export const GET_STRIPE_ACCOUNT_EMAIL_VARIANTS = {
  ...GATEWAY_VARIANTS,
  ACCOUNT_EMAIL_200,
  ACCOUNT_EMAIL_NO_ORG_CONTEXT_403,
  ACCOUNT_EMAIL_OPTIONS_PREFLIGHT,
  ACCOUNT_EMAIL_ORG_NOT_FOUND_404,
  ACCOUNT_EMAIL_STRIPE_ERROR_500_LEAKS_PROVIDER_STRING,
} as const;

/**
 * `POST /stripe/management-session` — CUSTOM authorizer 0jd4m5 DualAuth. 8 handler
 * variant(s).
 */
export const POST_STRIPE_MANAGEMENT_SESSION_VARIANTS = {
  ...GATEWAY_VARIANTS,
  MANAGEMENT_SESSION_CLIENT_SECRET_200,
  MANAGEMENT_SESSION_ERROR_500,
  MANAGEMENT_SESSION_IDENTITY_CONFLICT_409,
  MANAGEMENT_SESSION_NO_ORG_CONTEXT_403,
  MANAGEMENT_SESSION_OPTIONS_PREFLIGHT,
  MANAGEMENT_SESSION_ORG_NOT_FOUND_404,
  MANAGEMENT_SESSION_PERMISSION_DENIED_403,
  MANAGEMENT_SESSION_PERMISSIONS_UNAVAILABLE_503,
} as const;

/** `DELETE /custom-roles` — CUSTOM authorizer 0jd4m5 DualAuth. 8 handler variant(s). */
export const DELETE_CUSTOM_ROLES_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CR_DELETE_INTERNAL_ERROR_500,
  CR_DELETE_INVALID_REASSIGN_ROLE_400,
  CR_DELETE_NOT_FOUND_404,
  CR_DELETE_OK_200,
  CR_DELETE_ROLE_NOT_AVAILABLE_409,
  CR_DELETE_SLUG_REQUIRED_400,
  RP_ADMIN_CLERK_AUTH_REQUIRED_403,
  RP_ADMIN_ROLE_REQUIRED_403,
} as const;

/**
 * `POST /stripe/account-session` — CUSTOM authorizer 0jd4m5 DualAuth. 10 handler
 * variant(s).
 */
export const POST_STRIPE_ACCOUNT_SESSION_VARIANTS = {
  ...GATEWAY_VARIANTS,
  ACCOUNT_SESSION_CLIENT_SECRET_200,
  ACCOUNT_SESSION_IDENTITY_CONFLICT_409,
  ACCOUNT_SESSION_METHOD_NOT_ALLOWED_405,
  ACCOUNT_SESSION_NO_ORG_CONTEXT_403,
  ACCOUNT_SESSION_OPTIONS_PREFLIGHT,
  ACCOUNT_SESSION_ORG_NOT_FOUND_404,
  ACCOUNT_SESSION_PERMISSION_DENIED_403,
  ACCOUNT_SESSION_PERMISSIONS_UNAVAILABLE_503,
  ACCOUNT_SESSION_STRIPE_ERROR_500_LEAKS_PROVIDER_STRING,
  ACCOUNT_SESSION_STRIPE_NOT_CONFIGURED_503,
} as const;

/** `GET /users` — CUSTOM authorizer 0jd4m5 DualAuth. 6 handler variant(s). */
export const GET_USERS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  GET_USERS_LIST_FORBIDDEN_ORG,
  GET_USERS_LIST_INTERNAL_ERROR,
  GET_USERS_LIST_MEMBERSHIP_CHECK_FAILED,
  GET_USERS_LIST_MISSING_ORG_ID,
  GET_USERS_LIST_NO_USER_ID,
  GET_USERS_LIST_OK,
} as const;

/** `POST /clerk-users` — CUSTOM authorizer 0jd4m5 DualAuth. 7 handler variant(s). */
export const POST_CLERK_USERS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  POST_CLERK_USERS_ALREADY_EXISTS_422,
  POST_CLERK_USERS_CREATED,
  POST_CLERK_USERS_CUSTOM_ROLE_NOT_FOUND,
  POST_CLERK_USERS_EMAIL_REQUIRED,
  POST_CLERK_USERS_INTERNAL_ERROR,
  POST_CLERK_USERS_INVALID_ROLE,
  POST_CLERK_USERS_ROLE_NOT_AVAILABLE,
} as const;

/**
 * `GET /public/privacy-policy/{slug}` — AuthorizationType NONE (no authorizer). 2 handler
 * variant(s).
 */
export const GET_PUBLIC_PRIVACY_POLICY_SLUG_VARIANTS = {
  ...GATEWAY_VARIANTS,
  PP_PUBLIC_GET_NOT_FOUND_404,
  PP_PUBLIC_GET_OK_200,
} as const;

/** `POST /ein-lookup` — CUSTOM authorizer 0jd4m5 DualAuth. 12 handler variant(s). */
export const POST_EIN_LOOKUP_VARIANTS = {
  ...GATEWAY_VARIANTS,
  EIN_LOOKUP_INVALID_JSON_400,
  EIN_LOOKUP_LEGACY_EIN_HIT_200,
  EIN_LOOKUP_LEGACY_EIN_MISS_200,
  EIN_LOOKUP_NOT_CONFIGURED_503,
  EIN_LOOKUP_NO_INPUT_400,
  EIN_LOOKUP_NO_RESULTS_200,
  EIN_LOOKUP_OPTIONS_PREFLIGHT,
  EIN_LOOKUP_SEARCH_HIT_200,
  EIN_LOOKUP_TIMEOUT_504,
  EIN_LOOKUP_UNEXPECTED_500,
  EIN_LOOKUP_UPSTREAM_404_AS_200,
  EIN_LOOKUP_UPSTREAM_HTTP_ERROR_502,
} as const;

/** `PATCH /users/me` — CUSTOM authorizer 0jd4m5 DualAuth. 7 handler variant(s). */
export const PATCH_USERS_ME_VARIANTS = {
  ...GATEWAY_VARIANTS,
  PATCH_USERS_ME_EMPTY_BODY,
  PATCH_USERS_ME_INTERNAL_ERROR,
  PATCH_USERS_ME_NOT_FOUND,
  PATCH_USERS_ME_NO_USER_ID,
  PATCH_USERS_ME_NO_VALID_FIELDS,
  PATCH_USERS_ME_OK,
  PATCH_USERS_ME_PREFERENCES_NOT_OBJECT,
} as const;

/** `GET /org-role-permissions` — CUSTOM authorizer 0jd4m5 DualAuth. 3 handler variant(s). */
export const GET_ORG_ROLE_PERMISSIONS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  RP_GET_CLERK_AUTH_REQUIRED_403,
  RP_GET_INTERNAL_ERROR_500,
  RP_GET_OK_200,
} as const;

/** `GET /get-location` — CUSTOM authorizer 0jd4m5 DualAuth. 1 handler variant(s). */
export const GET_GET_LOCATION_VARIANTS = {
  ...GATEWAY_VARIANTS,
  GET_LOCATION_OK_200,
} as const;

/** `POST /webhooks/clerk` — AuthorizationType NONE (no authorizer). 26 handler variant(s). */
export const POST_WEBHOOKS_CLERK_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CLERK_WEBHOOK_DISPATCH_ERROR_500,
  CLERK_WEBHOOK_SECRET_NOT_CONFIGURED_503,
  CLERK_WEBHOOK_SKIPPED_NO_MERCHANT_ID,
  CLERK_WEBHOOK_SKIPPED_NO_ORG_ID,
  CLERK_WEBHOOK_SKIPPED_NO_USER_ID,
  CLERK_WEBHOOK_UNHANDLED_EVENT_TYPE,
  CLERK_WEBHOOK_VERIFICATION_FAILED_400,
  MEMBERSHIP_CREATED_EXISTING_USER_ACTIVE_ORG_SET,
  MEMBERSHIP_CREATED_EXISTING_USER_NOOP,
  MEMBERSHIP_CREATED_NEW_USER,
  MEMBERSHIP_CREATED_RACE_RECOVERY,
  MEMBERSHIP_DELETED_CLEARED,
  MEMBERSHIP_DELETED_NOOP_CONCURRENT_CHANGE,
  MEMBERSHIP_DELETED_NOOP_DIFFERENT_ORG,
  MEMBERSHIP_DELETED_NOOP_NO_ACTIVE_ORG,
  MEMBERSHIP_DELETED_SKIPPED_USER_NOT_IN_DDB,
  MEMBERSHIP_UPDATED_ACK,
  ORGANIZATION_UPSERT_OK,
  ORGANIZATION_UPSERT_SKIPPED_NO_ID_OR_SLUG,
  ORGANIZATION_UPSERT_SKIPPED_NO_V2_ORG,
  ORGANIZATION_UPSERT_SKIPPED_TABLE_UNCONFIGURED,
  USER_CREATED_OK,
  USER_DELETED_NOOP_NOT_IN_DDB,
  USER_DELETED_OK,
  USER_UPDATED_OK,
  USER_UPDATED_SKIPPED_NOT_EXIST,
} as const;

/** `PATCH /onboarding/state` — CUSTOM authorizer 0jd4m5 DualAuth. 13 handler variant(s). */
export const PATCH_ONBOARDING_STATE_VARIANTS = {
  ...GATEWAY_VARIANTS,
  ONBOARDING_STATE_DRIVER_FLOWS_NOT_OBJECT_400,
  ONBOARDING_STATE_HAS_WEBSITE_NOT_BOOL_400,
  ONBOARDING_STATE_INVALID_JSON_400,
  ONBOARDING_STATE_METHOD_NOT_ALLOWED_405,
  ONBOARDING_STATE_NO_ORG_CONTEXT_403,
  ONBOARDING_STATE_NO_VALID_FIELDS_400,
  ONBOARDING_STATE_OPTIONS_PREFLIGHT,
  ONBOARDING_STATE_ORG_NOT_FOUND_404,
  ONBOARDING_STATE_PAID_PLAN_REJECTED_400,
  ONBOARDING_STATE_SCRIPT_INSTALLED_NOT_BOOL_400,
  ONBOARDING_STATE_SUCCESS_200,
  ONBOARDING_STATE_STEP_UPDATED_SUCCESS_200,
  ONBOARDING_STATE_V2_WRITE_FAILED_500,
} as const;

/**
 * `POST /users/me/active-org/sync` — CUSTOM authorizer 0jd4m5 DualAuth. 9 handler
 * variant(s).
 */
export const POST_USERS_ME_ACTIVE_ORG_SYNC_VARIANTS = {
  ...GATEWAY_VARIANTS,
  SYNC_ACTIVE_ORG_INTERNAL_ERROR,
  SYNC_ACTIVE_ORG_MISSING_ORG_ID,
  SYNC_ACTIVE_ORG_NO_CLERK_ORG,
  SYNC_ACTIVE_ORG_NO_MAPPING,
  SYNC_ACTIVE_ORG_NO_USER_ID,
  SYNC_ACTIVE_ORG_OK,
  SYNC_ACTIVE_ORG_RESOLVE_FAILED,
  SYNC_ACTIVE_ORG_TABLE_UNCONFIGURED,
  SYNC_ACTIVE_ORG_USER_NOT_FOUND,
} as const;

/** `PATCH /global-custom-inputs` — CUSTOM authorizer 0jd4m5 DualAuth. 4 handler variant(s). */
export const PATCH_GLOBAL_CUSTOM_INPUTS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  GCI_NO_ORG_403,
  GCI_OK_200,
  GCI_READONLY_403,
  GCI_WRITE_FAILED_500,
} as const;

/** `PATCH /billing-plan` — CUSTOM authorizer 0jd4m5 DualAuth. 10 handler variant(s). */
export const PATCH_BILLING_PLAN_VARIANTS = {
  ...GATEWAY_VARIANTS,
  BILLING_PLAN_CUSTOM_REJECTED_400,
  BILLING_PLAN_INVALID_JSON_400,
  BILLING_PLAN_INVALID_PLAN_400,
  BILLING_PLAN_METHOD_NOT_ALLOWED_405,
  BILLING_PLAN_MISSING_PERMISSION_403,
  BILLING_PLAN_NO_CLERK_AUTH_403,
  BILLING_PLAN_OK_200,
  BILLING_PLAN_OPTIONS_200_EMPTY_OBJECT,
  BILLING_PLAN_ORG_RECORD_NOT_FOUND_404,
  BILLING_PLAN_PERMISSION_RESOLUTION_UNAVAILABLE_503,
} as const;

/** `PATCH /user-profile` — CUSTOM authorizer 0jd4m5 DualAuth. 6 handler variant(s). */
export const PATCH_USER_PROFILE_VARIANTS = {
  ...GATEWAY_VARIANTS,
  USER_PROFILE_INTERNAL_ERROR,
  USER_PROFILE_METHOD_NOT_ALLOWED,
  USER_PROFILE_NOT_CLERK_PROVIDER,
  USER_PROFILE_NO_FIELDS,
  USER_PROFILE_NO_USER_ID,
  USER_PROFILE_OK,
} as const;

/** `PUT /org-role-permissions` — CUSTOM authorizer 0jd4m5 DualAuth. 7 handler variant(s). */
export const PUT_ORG_ROLE_PERMISSIONS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  RP_ADMIN_CLERK_AUTH_REQUIRED_403,
  RP_ADMIN_ROLE_REQUIRED_403,
  RP_PUT_CANNOT_CUSTOMIZE_ROLE_400,
  RP_PUT_INTERNAL_ERROR_500,
  RP_PUT_INVALID_PERMISSIONS_400,
  RP_PUT_OK_200,
  RP_PUT_PERMS_NOT_ARRAY_400,
} as const;

/** `POST /webhooks/stripe` — AuthorizationType NONE (no authorizer). 8 handler variant(s). */
export const POST_WEBHOOKS_STRIPE_VARIANTS = {
  ...GATEWAY_VARIANTS,
  SW_ACK_NO_CHANGES_200,
  SW_OPTIONS_PREFLIGHT,
  SW_PROCESSED_WITH_CHANGES_200,
  SW_PROCESSING_FAILED_500,
  SW_SIGNATURE_VERIFICATION_FAILED_400,
  SW_V2_ACCOUNT_FETCH_FAILED_500,
  SW_VERIFICATION_GENERIC_ERROR_400,
  SW_WEBHOOK_SECRET_NOT_CONFIGURED_503,
} as const;

/** `GET /org-contact-tags` — CUSTOM authorizer 0jd4m5 DualAuth. 4 handler variant(s). */
export const GET_ORG_CONTACT_TAGS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  TAGS_GET_CLERK_AUTH_REQUIRED_403,
  TAGS_GET_INTERNAL_ERROR_500,
  TAGS_GET_OK_200,
  TAGS_GET_ORG_NOT_FOUND_404,
} as const;

/** `DELETE /org-custom-fields` — CUSTOM authorizer 0jd4m5 DualAuth. 9 handler variant(s). */
export const DELETE_ORG_CUSTOM_FIELDS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CF_CLERK_AUTH_REQUIRED_403,
  CF_DELETE_INTERNAL_ERROR_500,
  CF_DELETE_KEY_PARAM_REQUIRED_400,
  CF_DELETE_NOT_FOUND_404,
  CF_DELETE_OK_200,
  CF_DELETE_WRITE_CONFLICT_409,
  CF_MUTATE_PERMISSION_DENIED_403,
  CF_ORG_NOT_FOUND_404,
  CF_PERMISSION_RESOLUTION_UNAVAILABLE_503,
} as const;

/** `POST /org-custom-fields` — CUSTOM authorizer 0jd4m5 DualAuth. 19 handler variant(s). */
export const POST_ORG_CUSTOM_FIELDS_VARIANTS = {
  ...GATEWAY_VARIANTS,
  CF_CLERK_AUTH_REQUIRED_403,
  CF_CREATE_BAD_TYPE_400,
  CF_CREATE_BODY_NOT_OBJECT_400,
  CF_CREATE_CREATED_201,
  CF_CREATE_INTERNAL_ERROR_500,
  CF_CREATE_INVALID_JSON_400,
  CF_CREATE_LABEL_CONFLICT_409,
  CF_CREATE_LABEL_INVALID_CHARS_400,
  CF_CREATE_LABEL_NOT_STRING_400,
  CF_CREATE_LABEL_NO_VALID_KEY_400,
  CF_CREATE_LABEL_REQUIRED_400,
  CF_CREATE_LABEL_TOO_LONG_400,
  CF_CREATE_PERMISSION_DENIED_403,
  CF_CREATE_QUOTA_EXCEEDED_409,
  CF_CREATE_RESERVED_KEY_400,
  CF_CREATE_REUSED_200,
  CF_CREATE_WRITE_CONFLICT_409,
  CF_ORG_NOT_FOUND_404,
  CF_PERMISSION_RESOLUTION_UNAVAILABLE_503,
} as const;

/**
 * Reachable in HANDLER code but NOT via any declared route on this api: OPTIONS branches
 * on lambdas whose ApiStack registers only the single verb, and method fall-throughs under
 * a gateway that routes only the declared methods. They cannot carry a route-keyed
 * positive fixture, because parse dispatches by route and would have to return a variant
 * that route cannot produce. `schemas.test.ts` proves them absent from every route
 * registry instead.
 */
export const UNROUTABLE_VARIANTS = {
  CF_OPTIONS_PREFLIGHT_204,
  OS_NON_PUT_404,
  PP_DISPATCH_NOT_FOUND_404,
  RP_METHOD_NOT_ALLOWED_405,
  TAGS_METHOD_NOT_ALLOWED_405,
  TAGS_OPTIONS_PREFLIGHT_204,
  UNROUTABLE_METHOD_NOT_ALLOWED_405_GET_POST_PATCH_DELETE,
  UNROUTABLE_NON_PATCH_500,
  USERS_METHOD_NOT_ALLOWED,
} as const;

// --------------------------------------------------------------------------
// Effect schemas (bucket D half of the two Stripe webhooks) — the message,
// table write, or outbound call a variant produces, not an HTTP envelope.
// --------------------------------------------------------------------------

/**
 * stripe_subscription_webhook.py:241 — the SINGLE DynamoDB write site shared by all 5
 * event handlers (called from :496, :573, :614, :684, :720). :bd is z.record(z.unknown()),
 * NOT a strict object and NOT .passthrough(): _write_sub rebuilds the map by re-reading
 * the stored item (:226), popping the 11 _SUBSCRIPTION_FIELDS keys (:230-231) plus
 * 'marketing'/'subscriptions'/'pendingCustom' (:233-235), then re-setting only the
 * non-None keys from `details` (:237-239) — everything else on the stored billingDetails
 * (notably processingFees) is PRESERVED VERBATIM. The key set is therefore genuinely open
 * and bounded only by what prior writers left, so per the hard rule it is modeled as an
 * open record rather than a passthrough object. See SswBillingDetailsSubscriptionFields
 * for the subset this handler controls. DEFECTS PINNED: no ConditionExpression and no
 * version attribute, with a non-ConsistentRead get_item at :226 — a lost-update race
 * between concurrent Stripe deliveries for one org; and no Stripe-event-id idempotency
 * anywhere, so a retry appends a duplicate history entry.
 */
export const SswBillingDetailsWholeMapWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET billingDetails = :bd'), conditionExpression: z.null(), values: z.object({ ':bd': z.record(z.unknown()) }).strict() }).strict();

/**
 * stripe_subscription_webhook.py:261, called only from handle_checkout_completed (:512)
 * after _write_sub and after the EventBridge publish. `values: z.undefined()` is
 * load-bearing — the call passes NO ExpressionAttributeValues at all, which is distinct
 * from passing an empty map. Path-scoped so it never touches billingDetails. REMOVE on a
 * missing attribute is a DynamoDB no-op, so it is idempotent. Wrapped in try/except
 * (:260/:266): any failure including ConditionalCheckFailedException is logged and
 * swallowed, and the webhook still 200s.
 */
export const SswClearSelectedPlanIntent = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('REMOVE onboardingDetails.selectedPlan'), conditionExpression: z.literal('attribute_exists(organizationId)'), values: z.undefined() }).strict();

/**
 * _append_history at stripe_subscription_webhook.py:275-291. The 4 base keys are always
 * present (:278-282); the 4 optional keys are appended only when truthy / non-None
 * (:283-290). action enum CLOSED by reading every call site: `grep -n '_append_history('`
 * = 1 definition (:275) + 7 calls — :479 'subscribe', :564 'renew', :608 'past_due', :638
 * 'cancel_scheduled', :647 'reactivate', :677 'subscription_updated', :713 'cancel'. 7
 * sites, 7 distinct literals, no dynamic action value anywhere. startDate/endDate are
 * str(...) of a Stripe UNIX timestamp (:286, :288) — STRING-TYPED NUMBERS, a bug-for-bug
 * quirk: they are epoch integers stringified, not ISO dates, unlike changedAt.
 * paymentAmountCents is int(...) (:290) so it stays numeric. billingPeriod uses the
 * 2-value output enum of _normalize_billing_period (:294-300).
 */
export const SswHistoryEntry = z.object({ plan: z.string(), action: z.enum(['subscribe', 'renew', 'past_due', 'cancel_scheduled', 'reactivate', 'subscription_updated', 'cancel']), changedAt: z.string(), changedBy: z.literal('stripe-direct'), billingPeriod: z.enum(['month', 'year']).optional(), startDate: z.string().optional(), endDate: z.string().optional(), paymentAmountCents: z.number().int().optional() }).strict();

/**
 * stripe_subscription_webhook.py:386 — OUTBOUND HMAC-signed POST to MARKETING_WEBHOOK_URL.
 * Body source at :362-366, serialized with separators=(',',':') at :367. Signature
 * f-string source: f't={unix_ts},v1={signature}' (:381) where signature = hmac.new(secret,
 * f'{unix_ts}.{raw_body}', hashlib.sha256).hexdigest() (:369-373) — hexdigest of SHA-256
 * is exactly 64 lowercase hex chars, which closes the regex. plan is z.literal('PLUS')
 * because the ONLY call site (:523-524) is gated on plan_id == 'PLUS'. timeout is the
 * module constant MARKETING_WEBHOOK_TIMEOUT_SECONDS = 4 (:73). Four except clauses (:400,
 * :411, :416) guarantee it never raises into the handler; it also no-ops with a log when
 * the URL (:346-351) or the HMAC secret (:353-360) is absent.
 */
export const SswMarketingActivationNotification = z.object({ method: z.literal('POST'), headers: z.object({ 'Content-Type': z.literal('application/json'), 'X-CS-Signature': z.string().regex(/^t=\d+,v1=[0-9a-f]{64}$/) }).strict(), body: z.object({ organizationId: z.string(), plan: z.literal('PLUS'), activatedAt: z.string() }).strict(), timeoutSeconds: z.literal(4) }).strict();

/**
 * product_analytics_events.py:141 via publish_subscription_canceled, called from
 * stripe_subscription_webhook.py:721. canceledAt =
 * _iso_from_timestamp(sub.get('canceled_at') or sub.get('ended_at')) (:729), which returns
 * None on a missing or unparseable timestamp (:319-325) and is then stripped by _compact —
 * hence .optional() rather than .nullable().
 */
export const SswSubscriptionCanceledEvent = z.object({ Source: z.literal('charitystack.accounts'), DetailType: z.literal('subscription.canceled.v1'), EventBusName: z.string(), Detail: z.object({ eventId: z.string().uuid(), originIdempotencyKey: z.string(), organizationId: z.string(), occurredAt: z.string(), previousPlan: z.string().optional(), stripeSubscriptionId: z.string().optional(), canceledAt: z.string().optional() }).strict() }).strict();

/**
 * product_analytics_events.py:141 via publish_subscription_created, called from
 * stripe_subscription_webhook.py:497. Detail is JSON-serialized with separators=(',',':')
 * (:147). The 4 envelope keys are always present (:133-137); the payload keys are
 * .optional() because _compact (:172-177) STRIPS any value that is None or '' before
 * serialization. originIdempotencyKey is the Stripe event id, falling back to the f-string
 * f"checkout.session.completed:{session.get('id') or session.get('subscription') or
 * organization_id}" (:501). Best-effort: put_events is wrapped at :140/:151 and a
 * FailedEntryCount>0 only warns (:155-157) — it can never raise into the webhook.
 */
export const SswSubscriptionCreatedEvent = z.object({ Source: z.literal('charitystack.accounts'), DetailType: z.literal('subscription.created.v1'), EventBusName: z.string(), Detail: z.object({ eventId: z.string().uuid(), originIdempotencyKey: z.string(), organizationId: z.string(), occurredAt: z.string(), plan: z.string().optional(), billingPeriod: z.enum(['month', 'year']).optional(), stripeSubscriptionId: z.string().optional() }).strict() }).strict();

/**
 * product_analytics_events.py:141 via publish_subscription_plan_changed, called from
 * stripe_subscription_webhook.py:686. Emitted ONLY when plan_changed is True (:685) — a
 * billing-period-only change still WRITES DynamoDB at :684 but emits NO event, so the
 * event stream under-reports period changes. previousPlan is z.string().optional() because
 * a first-ever org has no stored plan and _compact strips the resulting None.
 * originIdempotencyKey falls back to f"customer.subscription.updated:{sub.get('id') or
 * organization_id}" (:690).
 */
export const SswSubscriptionPlanChangedEvent = z.object({ Source: z.literal('charitystack.accounts'), DetailType: z.literal('subscription.plan-changed.v1'), EventBusName: z.string(), Detail: z.object({ eventId: z.string().uuid(), originIdempotencyKey: z.string(), organizationId: z.string(), occurredAt: z.string(), previousPlan: z.string().optional(), newPlan: z.string().optional(), previousBillingPeriod: z.enum(['month', 'year']).optional(), newBillingPeriod: z.enum(['month', 'year']).optional(), stripeSubscriptionId: z.string().optional() }).strict() }).strict();

/**
 * apple_pay_utils.py:190, invoked TWICE per activation (from :265 with
 * CHARITYSTACK_HOSTED_PAGE_DOMAIN, from :282 with the redirect-resolved merchant domain).
 * URL source:
 * f'{STRIPE_DOMAIN_API_URL}?domain={quote_plus(domain)}&merchant={quote_plus(merchant_name)}'
 * (:185). The callee (transactions-backend /stripe/domain) performs a Stripe WRITE
 * server-side — stripe.PaymentMethodDomain.create() — so this GET is an EFFECT despite its
 * verb. timeout is _STRIPE_DOMAIN_TIMEOUT_SECONDS (env
 * APPLE_PAY_STRIPE_DOMAIN_TIMEOUT_SECONDS, default 10, :49). All three except clauses
 * (:197, :212, :215) return False; this never raises.
 */
export const SwApplePayDomainRegisterCall = z.object({ method: z.literal('GET'), urlTemplate: z.string().regex(/^\{STRIPE_DOMAIN_API_URL\}\?domain=[^&]*&merchant=.*$/), headers: z.object({ 'User-Agent': z.literal('CharityStack-Webhook/1.0') }).strict(), timeoutSeconds: z.number().int() }).strict();

/**
 * stripe_webhooks.py:276, reached only from the charges_enabled=TRUE branch via :422.
 * Fixed 2-clause UpdateExpression. The :s map is register_apple_pay_domain's result
 * (apple_pay_utils.py:249-254) after `clean = {k: v for k, v in status_result.items() if v
 * is not None}` at :274 — so `domain` and `errorCategory` are OPTIONAL-BY-OMISSION, not
 * nullable: a None value means the KEY IS ABSENT from the stored map, never stored as
 * NULL. status enum closed by the 2 reassignment sites (:283 'active', :285 'inactive')
 * over the 'skipped' initializer (:250). errorCategory enum closed by `grep -n
 * 'errorCategory'` = 6 hits, 4 of which are assignments (:258, :272, :278, :286).
 * lastCheckedAt is datetime.now(timezone.utc).isoformat() (apple_pay_utils.py:53) — note
 * this is isoformat(), a DIFFERENT format from the '%Y-%m-%dT%H:%M:%S.%fZ' used everywhere
 * else in stripe_webhooks (:158). Whole write is swallowed by try/except at :275/:285.
 */
export const SwApplePayStatusWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET applePayStatus = :s, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':s': z.object({ status: z.enum(['active', 'inactive', 'skipped']), domain: z.string().optional(), errorCategory: z.enum(['missing_merchant', 'missing_website', 'unparseable_url', 'registration_failed']).optional(), lastCheckedAt: z.string() }).strict(), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:428, the charges_enabled=FALSE branch at :424. The source splits the
 * expression across two adjacent string literals at :430-433 which concatenate to exactly
 * the pinned value. defaultPaymentProcessor is deliberately NOT cleared here.
 */
export const SwChargesDisabledWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET stripeDetails.chargesEnabled = :ce, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':ce': z.literal(false), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:352. Built by `"SET " + ", ".join(stripe_parts)` where stripe_parts
 * is a FIXED 5-element list literal at :345-351 with no conditional appends — so the
 * joined string is CONSTANT and pinned as a z.literal, not a regex. Fires when
 * charges_enabled transitions to TRUE. :dpp is the only value 'STRIPE' ever written (also
 * at :489); it is never cleared on the disable path (comment at :440-441).
 */
export const SwChargesEnabledWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET stripeDetails.accountId = :said, stripeDetails.chargesEnabled = :ce, stripeDetails.activatedAt = :at, defaultPaymentProcessor = :dpp, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':said': z.string(), ':ce': z.literal(true), ':at': z.string(), ':dpp': z.literal('STRIPE'), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:764. One-way latch — the guard `if details_submitted and not
 * stored_details` (:762) means false is never written back.
 */
export const SwDetailsSubmittedWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET stripeDetails.detailsSubmitted = :ds, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':ds': z.literal(true), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:392, the else-branch at :388. WHOLE-MAP REPLACE. The :od map is
 * _merged_onboarding_details(org_record, {'currentStep': 'checklist'}) (:292-314): it
 * starts as a copy of the existing nested map, backfills currentStep / completedSteps /
 * activationPath / driverFlowsCompleted from flat V1 fields when absent (:302-309), then
 * the extra_updates override forces currentStep='checklist' (:311-312).
 * completedSteps/activationPath/driverFlowsCompleted are z.unknown() because they are
 * copied verbatim from the stored row with NO validation or shape check — their type is
 * whatever a prior writer left. .strict() is used per the hard rule; the four listed keys
 * are what _merged_onboarding_details can produce from a row that itself only carried
 * those, but note the seed `dict(org_record.get('onboardingDetails'))` at :294-296 copies
 * EVERY pre-existing key, so in practice this map is an open passthrough of stored data —
 * a consumer validating it strictly will reject legitimate rows. TWO DEFECTS PINNED: (a)
 * no ConditionExpression, so a duplicate delivery re-replaces the whole map — lost-update
 * race against any concurrent writer; (b) `has_nested_step` computed at :369 is DEAD,
 * never read — the guard it was written for does not exist.
 */
export const SwOnboardingDetailsWholeMapReplace = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET onboardingDetails = :od, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':od': z.object({ currentStep: z.literal('checklist'), completedSteps: z.unknown().optional(), activationPath: z.unknown().optional(), driverFlowsCompleted: z.unknown().optional() }).strict(), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:509, the payouts_enabled=FALSE branch at :505. ACTIVE (:490) and
 * PAUSED (:518) are the only two payoutStatus values this handler ever writes — closed by
 * `grep -n '":ps"'` = 2 hits.
 */
export const SwPayoutsDisabledWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET stripeDetails.payoutsEnabled = :pe, payoutStatus = :ps, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':pe': z.literal(false), ':ps': z.literal('PAUSED'), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:466. 14-clause expression assembled from adjacent literals at
 * :468-483; constant, so pinned as a literal. :dnp is
 * Decimal(str(int(target_utc.timestamp()))) from _calculate_next_payout_unix_hour
 * (:161-177) — RAW EPOCH SECONDS hour-aligned to tomorrow 09:00 America/New_York, NOT
 * epoch//3600 despite the field name (a //3600 value is silently unmatchable by the
 * payouts cron; the :162-171 docstring records the 2026-07-11 incident that stranded 8
 * orgs). Modeled as z.number().int() because this is a DynamoDB attribute value, not an
 * HTTP body — no DecimalEncoder is involved on this path. :sd/:sd2 are the same
 * statement_desc, capped at 22 chars only on the company.name fallback branch (:462) — a
 * Stripe-supplied settings.payments.statement_descriptor is passed through UNCAPPED, so
 * .max(22) documents intent but the code does not enforce it on that branch. :cc is
 * stripe_account_data.get('country', 'US') — unvalidated passthrough of Stripe's value.
 * TWO DEFECTS PINNED: (a) no ConditionExpression, so payoutCounter and
 * payoutCreationRetryAttempts are RESET TO 0 and dateNextPayoutUnixHour recomputed on
 * every False->True transition; (b) the nested-path clauses payoutDetails.* and
 * address.countryCode raise ValidationException if the parent map is absent on the row,
 * which surfaces as SW_PROCESSING_FAILED_500.
 */
export const SwPayoutsEnabledWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET stripeDetails.accountId = :said, stripeDetails.payoutsEnabled = :pe, stripeDetails.statementDescriptor = :sd, stripeConnectComplete = :scc, defaultPaymentProcessor = :dpp, payoutStatus = :ps, dateNextPayoutUnixHour = :dnp, payoutDetails.payoutFrequency = :pf, payoutDetails.payoutArrivalDay = :pad, payoutDetails.payoutCounter = :pc, payoutDetails.payoutCreationRetryAttempts = :pcra, address.countryCode = :cc, statementDescriptor = :sd2, updatedAt = :ua'), conditionExpression: z.null(), values: z.object({ ':said': z.string(), ':pe': z.literal(true), ':sd': z.string().max(22), ':scc': z.literal(true), ':dpp': z.literal('STRIPE'), ':ps': z.literal('ACTIVE'), ':dnp': z.number().int(), ':pf': z.literal('DAILY'), ':pad': z.literal(''), ':pc': z.number().int(), ':pcra': z.number().int(), ':cc': z.string(), ':sd2': z.string().max(22), ':ua': z.string() }).strict() }).strict();

/**
 * stripe_webhooks.py:545 — OUTBOUND third-party email send via resend.Emails.send. subject
 * f-string source: f'Action required: Stripe verification for {org_name}'. html f-string
 * source spans :549-553 and interpolates `due_list_html` = ''.join(f'<li>{item}</li>' for
 * item in currently_due + future_currently_due) (:544). SECURITY NOTE: those requirement
 * strings are interpolated into HTML UNESCAPED; Stripe controls them so practical risk is
 * low, but the code performs no escaping. `to` is [email] where email =
 * org_record.get('ownerEmail') or org_record.get('email') or '' (:539), skipped with a
 * print when empty (:540-542) — so the array is always length 1 when the call happens.
 * Gated on should_email (:594) = a requirement item is NEW versus the stored snapshot;
 * fired AFTER the :618 snapshot write commits (:627-628) so a Stripe retry recomputes the
 * diff and does not double-send. Whole helper wrapped in try/except (:534/:555).
 */
export const SwResendVerificationEmail = z.object({ from: z.literal('CharityStack <hello@charitystack.com>'), to: z.array(z.string()).length(1), subject: z.string().regex(/^Action required: Stripe verification for /), html: z.string() }).strict();

/**
 * stripe_webhooks.py:374. The ONLY ConditionExpression-guarded write in either handler —
 * the sole idempotency mechanism against duplicate webhook delivery on the step-advance
 * path. The condition string is assembled from two adjacent source literals at :383-386
 * that concatenate to exactly the pinned value (note the single space before 'OR').
 * ConditionalCheckFailedException is caught at :400 and swallowed with a print. Fires when
 * advance_step (:340) AND nested_current_step in ('share','awaiting_stripe_charges')
 * (:371).
 */
export const SwStepAdvanceConditionalWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET onboardingDetails.currentStep = :ns, updatedAt = :ua'), conditionExpression: z.literal('onboardingDetails.currentStep = :expected_share OR onboardingDetails.currentStep = :expected_awaiting'), values: z.object({ ':ns': z.literal('checklist'), ':ua': z.string(), ':expected_share': z.literal('share'), ':expected_awaiting': z.literal('awaiting_stripe_charges') }).strict() }).strict();

/**
 * stripe_webhooks.py:654 — OUTBOUND Stripe API WRITE,
 * stripe.Account.modify(stripe_account_id, capabilities={...}). Gated at :647-651: fires
 * only on the taxIdProvided False->True edge AND country == 'US' AND a truthy account id
 * AND _init_stripe() succeeding. Idempotent per the :645 docstring (Stripe accepts
 * re-requests for active capabilities). Wrapped in try/except (:653/:662) so it cannot
 * produce the 500.
 */
export const SwStripeAccountModifyTaxReporting = z.object({ accountId: z.string(), capabilities: z.object({ tax_reporting_us_1099_k: z.object({ requested: z.literal(true) }).strict(), tax_reporting_us_1099_misc: z.object({ requested: z.literal(true) }).strict() }).strict() }).strict();

/**
 * stripe_webhooks.py:618 — THE ONLY VARIABLE-LENGTH UpdateExpression in this group. Built
 * as `"SET " + ", ".join(update_parts)` where update_parts starts as the FIXED 2-element
 * list ['stripeDetails.taxIdProvided = :tip', 'updatedAt = :ua'] (:599) and takes exactly
 * TWO conditional appends, in a fixed source order: 'stripeDetails.verificationEmail =
 * :ve' at :604 (when requirements_changed or should_email) then 'ein = :ein' at :615 (when
 * raw_tax_id present and the stored ein is blank). The regex therefore CLOSES the tail
 * vocabulary to those two optional clauses in that order — 4 possible strings total, fully
 * enumerable — rather than widening to z.string(). Anchored both ends so no other clause
 * can match. :ve.lastEmailSentAt is z.string().nullable() NOT optional: at :608 it
 * evaluates to `_now_iso() if should_email else stored_snap.get('lastEmailSentAt')`, and
 * that .get() returns None on a first-ever write, which DynamoDB stores as NULL inside the
 * map rather than omitting the key. Early-returns WITHOUT writing at :597 when nothing
 * changed.
 */
export const SwTaxIdSnapshotWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.string().regex(/^SET stripeDetails\.taxIdProvided = :tip, updatedAt = :ua(, stripeDetails\.verificationEmail = :ve)?(, ein = :ein)?$/), conditionExpression: z.null(), values: z.object({ ':tip': z.boolean(), ':ua': z.string(), ':ve': z.object({ lastCurrentlyDue: z.array(z.string()), lastFutureCurrentlyDue: z.array(z.string()), lastEmailSentAt: z.string().nullable(), emailCount: z.number().int() }).strict().optional(), ':ein': z.string().optional() }).strict() }).strict();

/**
 * stripe_webhooks.py:797. Assembled from adjacent literals at :799-802; note it does NOT
 * touch updatedAt, unlike every other write here. :et is the raw event_type string —
 * unvalidated, and NOT restricted to SW_HANDLED_STRIPE_EVENT_TYPES, because this line runs
 * for any event that matched an org. FIRES ON EVERY org-matched event including the
 * changes_made=False path, so a SwOkNoChanges response does NOT imply zero writes. Wrapped
 * in its own try/except (:795/:808) so it can never produce the 500.
 */
export const SwTelemetryWrite = z.object({ table: z.literal('ORGANIZATIONS_TABLE'), key: z.object({ organizationId: z.string() }).strict(), updateExpression: z.literal('SET stripeDetails.lastWebhookEventType = :et, stripeDetails.lastWebhookEventAt = :ea'), conditionExpression: z.null(), values: z.object({ ':et': z.string(), ':ea': z.string() }).strict() }).strict();

/**
 * apple_pay_utils.py:145 — OUTBOUND HTTP to a MERCHANT-SUPPLIED URL, redirect-followed.
 * url is the org's websiteUrl with 'https://' prepended when it lacks a scheme (:131-132);
 * typed z.string() and NOT narrowed because it is unvalidated stored input. SSRF-guarded
 * by hostname_is_public() before the call (:135) and again on the resolved redirect target
 * (:148); known TOCTOU/DNS-rebinding gap documented at :75-83. timeout is
 * _RESOLVE_TIMEOUT_SECONDS (env APPLE_PAY_RESOLVE_TIMEOUT_SECONDS, default 5, :48).
 */
export const SwWebsiteHeadProbe = z.object({ method: z.literal('HEAD'), url: z.string(), headers: z.object({ 'User-Agent': z.literal('CharityStack-DomainResolver/1.0') }).strict(), timeoutSeconds: z.number().int() }).strict();

/**
 * The subset of billingDetails that _write_sub actually controls — the 11
 * _SUBSCRIPTION_FIELDS at stripe_subscription_webhook.py:126-138 MINUS the 3
 * _RETIRED_LEGACY_FIELDS ('smsEnabled','smsAmount','limits', :143) which are
 * popped-but-never-written (the PE-1636 garbage-collect described at :113-125). Every
 * field is .optional() because omission from `details` is DESTRUCTIVE, not neutral: the
 * pop-then-reset mechanism at :230-239 means a key absent from `details` is DELETED from
 * the stored map. That is how customer.subscription.deleted (:714-719) removes
 * billingPeriod / stripeSubscriptionId / stripeCustomerId / cancelAt, and how
 * checkout.session.completed clears a prior cancelAt by passing None (:493, filtered out
 * by the `is not None` check at :238). `plan` is z.string() NOT an enum: it is validated
 * against the VENDORED entitlements engine's PLAN_TO_TIER via is_known_plan (:449,
 * plan_catalog_engine.py:156-171), whose value set lives outside this repo — the docstring
 * at plan_catalog_engine.py:15 names {STARTER, BASE, PLUS, PRO, ENTERPRISE} but that is
 * documentation, not a source-closable literal here. Two hardcoded rewrites: 'CUSTOM_ORG'
 * -> 'PLUS' at :450-451 and :663-664. history is capped at HISTORY_MAX = 20 by the slice
 * at :291.
 */
export const SswBillingDetailsSubscriptionFields = z.object({ plan: z.string(), billingPeriod: z.enum(['month', 'year']).optional(), configuredAt: z.string().optional(), configuredBy: z.literal('stripe-direct').optional(), stripeSubscriptionId: z.string().optional(), stripeCustomerId: z.string().optional(), cancelAt: z.string().optional(), history: z.array(SswHistoryEntry).max(20).optional() }).strict();

export const EFFECT_SCHEMAS = {
  SswBillingDetailsWholeMapWrite,
  SswClearSelectedPlanIntent,
  SswHistoryEntry,
  SswMarketingActivationNotification,
  SswSubscriptionCanceledEvent,
  SswSubscriptionCreatedEvent,
  SswSubscriptionPlanChangedEvent,
  SwApplePayDomainRegisterCall,
  SwApplePayStatusWrite,
  SwChargesDisabledWrite,
  SwChargesEnabledWrite,
  SwDetailsSubmittedWrite,
  SwOnboardingDetailsWholeMapReplace,
  SwPayoutsDisabledWrite,
  SwPayoutsEnabledWrite,
  SwResendVerificationEmail,
  SwStepAdvanceConditionalWrite,
  SwStripeAccountModifyTaxReporting,
  SwTaxIdSnapshotWrite,
  SwTelemetryWrite,
  SwWebsiteHeadProbe,
  SswBillingDetailsSubscriptionFields,
} as const;

export const ROUTE_VARIANTS = {
  "DELETE /org-contact-tags": DELETE_ORG_CONTACT_TAGS_VARIANTS,
  "PATCH /org-custom-fields": PATCH_ORG_CUSTOM_FIELDS_VARIANTS,
  "POST /privacy-policy/generate": POST_PRIVACY_POLICY_GENERATE_VARIANTS,
  "POST /privacy-policy/accept": POST_PRIVACY_POLICY_ACCEPT_VARIANTS,
  "POST /users": POST_USERS_VARIANTS,
  "PATCH /custom-roles": PATCH_CUSTOM_ROLES_VARIANTS,
  "POST /onboarding/create-org": POST_ONBOARDING_CREATE_ORG_VARIANTS,
  "POST /subscription-cancel": POST_SUBSCRIPTION_CANCEL_VARIANTS,
  "GET /users/me": GET_USERS_ME_VARIANTS,
  "POST /webhooks/stripe-subscription": POST_WEBHOOKS_STRIPE_SUBSCRIPTION_VARIANTS,
  "GET /stripe/account-status": GET_STRIPE_ACCOUNT_STATUS_VARIANTS,
  "POST /privacy-policy/overlay": POST_PRIVACY_POLICY_OVERLAY_VARIANTS,
  "PATCH /nonprofit-settings": PATCH_NONPROFIT_SETTINGS_VARIANTS,
  "GET /clerk-users": GET_CLERK_USERS_VARIANTS,
  "DELETE /clerk-users": DELETE_CLERK_USERS_VARIANTS,
  "PATCH /clerk-users": PATCH_CLERK_USERS_VARIANTS,
  "POST /website-enrich": POST_WEBSITE_ENRICH_VARIANTS,
  "DELETE /org-role-permissions": DELETE_ORG_ROLE_PERMISSIONS_VARIANTS,
  "PUT /org-contact-tags": PUT_ORG_CONTACT_TAGS_VARIANTS,
  "GET /org-custom-fields": GET_ORG_CUSTOM_FIELDS_VARIANTS,
  "PUT /org-slug": PUT_ORG_SLUG_VARIANTS,
  "POST /verify-script": POST_VERIFY_SCRIPT_VARIANTS,
  "POST /custom-roles": POST_CUSTOM_ROLES_VARIANTS,
  "GET /merchant": GET_MERCHANT_VARIANTS,
  "GET /stripe/account-email": GET_STRIPE_ACCOUNT_EMAIL_VARIANTS,
  "POST /stripe/management-session": POST_STRIPE_MANAGEMENT_SESSION_VARIANTS,
  "DELETE /custom-roles": DELETE_CUSTOM_ROLES_VARIANTS,
  "POST /stripe/account-session": POST_STRIPE_ACCOUNT_SESSION_VARIANTS,
  "GET /users": GET_USERS_VARIANTS,
  "POST /clerk-users": POST_CLERK_USERS_VARIANTS,
  "GET /public/privacy-policy/{slug}": GET_PUBLIC_PRIVACY_POLICY_SLUG_VARIANTS,
  "POST /ein-lookup": POST_EIN_LOOKUP_VARIANTS,
  "PATCH /users/me": PATCH_USERS_ME_VARIANTS,
  "GET /org-role-permissions": GET_ORG_ROLE_PERMISSIONS_VARIANTS,
  "GET /get-location": GET_GET_LOCATION_VARIANTS,
  "POST /webhooks/clerk": POST_WEBHOOKS_CLERK_VARIANTS,
  "PATCH /onboarding/state": PATCH_ONBOARDING_STATE_VARIANTS,
  "POST /users/me/active-org/sync": POST_USERS_ME_ACTIVE_ORG_SYNC_VARIANTS,
  "PATCH /global-custom-inputs": PATCH_GLOBAL_CUSTOM_INPUTS_VARIANTS,
  "PATCH /billing-plan": PATCH_BILLING_PLAN_VARIANTS,
  "PATCH /user-profile": PATCH_USER_PROFILE_VARIANTS,
  "PUT /org-role-permissions": PUT_ORG_ROLE_PERMISSIONS_VARIANTS,
  "POST /webhooks/stripe": POST_WEBHOOKS_STRIPE_VARIANTS,
  "GET /org-contact-tags": GET_ORG_CONTACT_TAGS_VARIANTS,
  "DELETE /org-custom-fields": DELETE_ORG_CUSTOM_FIELDS_VARIANTS,
  "POST /org-custom-fields": POST_ORG_CUSTOM_FIELDS_VARIANTS,
} as const;

export type RouteKey = keyof typeof ROUTE_VARIANTS;

export type ParseSuccess<N extends string> = { ok: true; variant: N; value: unknown };
export type ParseFailure = {
  ok: false;
  reason: 'ENVELOPE' | 'BODY_NOT_JSON' | 'NO_VARIANT' | 'AMBIGUOUS';
  matches?: string[];
  issues?: Record<string, z.ZodIssue[]>;
};
export type ParseResult<N extends string> = ParseSuccess<N> | ParseFailure;

/**
 * Envelope in, named variant out. Exactly one variant must match: zero is a contract
 * failure, more than one is a contract DEFECT (the variants are meant to be structurally
 * disjoint), so it is reported rather than hidden.
 *
 * An empty-string body is NOT decoded — it is matched directly, because `JSON.parse('')`
 * throws and a 204 preflight would otherwise be reported as BODY_NOT_JSON.
 */
export function parseAgainst<V extends Record<string, z.ZodTypeAny>>(
  variants: V,
  envelope: unknown,
): ParseResult<Extract<keyof V, string>> {
  const env = LambdaProxyResponse.safeParse(envelope);
  if (!env.success) return { ok: false, reason: 'ENVELOPE', issues: { envelope: env.error.issues } };

  let decoded: unknown;
  if (env.data.body === '') {
    decoded = { statusCode: env.data.statusCode, headers: env.data.headers, body: '' };
  } else {
    let body: unknown;
    try {
      body = JSON.parse(env.data.body);
    } catch {
      return { ok: false, reason: 'BODY_NOT_JSON' };
    }
    decoded = { statusCode: env.data.statusCode, headers: env.data.headers, body };
  }

  const matches: string[] = [];
  const issues: Record<string, z.ZodIssue[]> = {};
  let value: unknown;
  for (const [name, schema] of Object.entries(variants)) {
    const r = schema.safeParse(decoded);
    if (r.success) {
      matches.push(name);
      value = r.data;
    } else {
      issues[name] = r.error.issues;
    }
  }
  if (matches.length === 1) {
    return { ok: true, variant: matches[0] as Extract<keyof V, string>, value };
  }
  if (matches.length === 0) return { ok: false, reason: 'NO_VARIANT', issues };
  return { ok: false, reason: 'AMBIGUOUS', matches };
}

/** Parse a response captured for a known route key. */
export function parseRouteResponse<R extends RouteKey>(route: R, envelope: unknown) {
  return parseAgainst(ROUTE_VARIANTS[route], envelope);
}
